  import React, { useState, useEffect } from "react"
  import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TextField,
    Card,
    Typography,
    IconButton,
    styled,
    Tooltip,
    CircularProgress,
    useTheme,
    Collapse,
    Grid,
    Chip,
    Button,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Divider,
    alpha,
    Checkbox,
    Alert,
    AlertTitle,
    Menu,
    ListItemIcon,
    ListItemText,
    ListSubheader,
    FormControlLabel,
    Stack,
    Radio,
    RadioGroup,
  } from "@mui/material"
  import { Pagination } from "@mui/material"
  import {
    Add as AddIcon,
    KeyboardArrowDown as KeyboardArrowDownIcon,
    KeyboardArrowRight as KeyboardArrowRightIcon,
    Edit,
    Visibility,
    LocalShipping,
    Payment,
    Person,
    LocationOn,
    ShoppingCart,
    Update as UpdateIcon,
    CheckCircle,
    Schedule,
    LocalShippingOutlined,
    Cancel,
    Refresh,
    FileDownload,
    SelectAll,
    Receipt as ReceiptIcon,
    Download as DownloadIcon,
    Close as CloseIcon,
    PictureAsPdf as PdfIcon,
    LocalShipping as DeliveryIcon,
    Assignment as ChallanIcon,
    Email as EmailIcon,
    Send as SendIcon
  } from "@mui/icons-material"
  import { useNavigate } from "react-router-dom"

  import apiService from "Network/apiService"
  import { API_ENDPOINTS } from "Network/apiEndpoints"
  import {
    submitNewPaidPaymentStatus,
    buildNewPaidPaymentFormData,
    extractPaymentApiError,
    getOrderPaymentDetailsFromOrder,
    validateNewPaidPaymentRequest,
    validatePaidPaymentFormDetails,
    isZeroRemainingPayment,
    PAYMENT_UPDATE_LOCKED_ORDER_STATUSES,
    getOrderPaymentStatusValue,
    normalizeStatusKey,
    getOrderStatusKey,
    isPaymentUpdateDisabledForOrder,
    getPaymentUpdateDisabledReason,
    DEFAULT_PAYMENT_UPDATE_MODAL,
    getPaidPaymentStepState,
    getAdvancePaymentEntries,
  } from "Network/paymentStatusService"
  import {
    fetchAdvancePaymentMethods,
    buildPaymentMethodsById,
  } from "Network/advancePaymentMethodService"
  import { useToast } from "OtherReusableFunctionality/ToastContext"

  const getOrderChallan = (order) =>
    order?.delivery_challan || order?.deliveryChallan || order?.challan;

  const isOrderByTruck = (order) => {
    if (!order) return false;
    const challan = getOrderChallan(order);
    const orderBy = String(order?.order_by || challan?.order_by || '')
      .trim()
      .toLowerCase();
    return orderBy === 'truck' || orderBy === 'by truck';
  };

  const ORDER_STATUS_TO_TAB = {
    placed: 'Placed',
    confirmed: 'Confirmed',
    processing: 'Processing',
    dispatch: 'Dispatch',
    dispatched: 'Dispatch',
    shipped: 'Dispatch',
    delivered: 'Delivered',
    cancelled: 'Cancelled',
    returned: 'Returned',
  };

  const APP_ORDER_STATUS_TAB_STORAGE_KEY = 'app_order_status_tab';

  const consumeSavedAppOrderStatusTab = () => {
    const savedTab = sessionStorage.getItem(APP_ORDER_STATUS_TAB_STORAGE_KEY);
    if (!savedTab) return null;
    sessionStorage.removeItem(APP_ORDER_STATUS_TAB_STORAGE_KEY);
    return savedTab;
  };

  const PAYMENT_ADVANCE_TAB_MAP = {
    placed: 'Confirmed',
    confirmed: 'Processing',
    processing: 'Dispatch',
    dispatch: 'Delivered',
    delivered: 'Returned',
  };

  const isPaymentSatisfiedForOrderAdvance = (paymentStatus = '') => {
    const status = normalizeStatusKey(paymentStatus);
    return status === 'paid' || status === 'outstanding' || status === 'credit';
  };

  const shouldAdvanceTabAfterPaymentUpdate = (paymentStatus = '') =>
    isPaymentSatisfiedForOrderAdvance(paymentStatus);

  const getStatusOrderFromOptions = (statusOptions = []) => {
    const orderMap = {};
    statusOptions.forEach((option) => {
      const category = normalizeStatusKey(option.category);
      const sequence = option.sequence || 0;
      if (category) orderMap[category] = sequence;
    });
    return orderMap;
  };

  const getNextSequentialStatusFromOptions = (statusOptions, currentStatusName, currentStatusCategory) => {
    if (!statusOptions?.length) return null;
    const statusOrder = getStatusOrderFromOptions(statusOptions);
    let currentStatusLower = normalizeStatusKey(currentStatusCategory);
    let currentOrder = statusOrder[currentStatusLower] || 0;

    if (currentOrder === 0 && currentStatusName) {
      const currentStatusOption = statusOptions.find((opt) => opt.name === currentStatusName);
      if (currentStatusOption) {
        currentStatusLower = normalizeStatusKey(currentStatusOption.category);
        currentOrder = statusOrder[currentStatusLower] || 0;
      }
    }
    if (currentOrder === 0) return null;

    const isDelivered =
      normalizeStatusKey(currentStatusName) === 'delivered' || currentStatusLower === 'delivered';
    if (isDelivered) {
      return statusOptions.find((option) => {
        const optionCategory = normalizeStatusKey(option.category);
        const optionName = normalizeStatusKey(option.name);
        return optionCategory === 'returned' || optionName === 'returned';
      }) || null;
    }

    const nextOrder = currentOrder + 1;
    return statusOptions.find((option) => {
      const optionCategory = normalizeStatusKey(option.category);
      return statusOrder[optionCategory] === nextOrder;
    }) || null;
  };

  const isSequentialNextStatusTransition = (statusOptions, fromName, fromCategory, toName) => {
    const nextStatus = getNextSequentialStatusFromOptions(statusOptions, fromName, fromCategory);
    if (!nextStatus || !toName) return false;
    return normalizeStatusKey(nextStatus.name) === normalizeStatusKey(toName);
  };

  const statusUpdateRequiresDeliveryPayload = (statusName = '') => {
    const key = normalizeStatusKey(statusName);
    return key === 'dispatch' || key === 'shipped' || key === 'delivered';
  };

  const resolveTabFromOrderStatus = (status) => {
    if (!status) return null;
    const normalized = normalizeStatusKey(status);
    return ORDER_STATUS_TO_TAB[normalized] || status;
  };

  const getAdvancedStatusFromResponse = (response) => {
    const status =
      response?.data?.status?.name ||
      response?.data?.order_status ||
      response?.data?.status;
    return status ? String(status) : null;
  };

  const submitOrderStatusAdvanceRequest = async (order, statusOptions, targetStatusName = null) => {
    const userId = localStorage.getItem('user_id');
    const orderId = order?.order_id;
    if (!orderId) {
      return { success: false, message: 'Order ID is required' };
    }

    let statusName = targetStatusName;
    if (!statusName) {
      const nextStatus = getNextSequentialStatusFromOptions(
        statusOptions,
        order?.status?.name || order?.status_name,
        order?.status?.category
      );
      statusName = nextStatus?.name;
    }

    if (!statusName) {
      return { success: false, message: 'Next order status not found' };
    }

    return apiService.put(
      API_ENDPOINTS.UPDATE_product_status,
      {
        user_id: userId,
        status: statusName,
        order_id: orderId,
      },
      'application/json'
    );
  };

  const resolveNextTabAfterPaymentUpdate = (order, currentTabFilter, response = null) => {
    const responseStatus = getAdvancedStatusFromResponse(response);
    if (responseStatus) {
      return resolveTabFromOrderStatus(responseStatus);
    }

    const currentKey =
      normalizeStatusKey(currentTabFilter) || getOrderStatusKey(order);
    return PAYMENT_ADVANCE_TAB_MAP[currentKey] || null;
  };

  // *Enhanced styled components with better dark theme support
  const StyledTableCell = styled(TableCell)(({ theme }) => ({
    padding: "16px 20px",
    color: theme.palette.text.primary,
    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
    whiteSpace: "nowrap",
    "&.header": {
        fontWeight: 600,
        fontSize: "0.875rem",
        backgroundColor: alpha(theme.palette.primary.main, 0.04),
        color: theme.palette.text.primary,
        position: 'sticky',
        top: 0,
        zIndex: 1,
    },
  }));

  const StyledTableRow = styled(TableRow)(({ theme }) => ({
    cursor: 'pointer',
    transition: 'all 0.2s ease-in-out',
    "&:hover": {
        backgroundColor: alpha(theme.palette.primary.main, 0.04),
        transform: 'translateY(-1px)',
        boxShadow: `0 4px 8px ${alpha(theme.palette.common.black, 0.1)}`,
    },
    "& > .MuiTableCell-root": {
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
    },
  }));

  const ExpandableRow = styled(TableRow)(({ theme }) => ({
    backgroundColor: alpha(theme.palette.background.paper, 0.8),
    '& > .MuiTableCell-root': {
      borderBottom: 'none',
    }
  }));

  const DetailCard = styled(Card)(({ theme }) => ({
    margin: '8px 0',
    padding: '20px',
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
    borderRadius: theme.shape.borderRadius * 2,
    transition: 'all 0.3s ease-in-out',
  }));

  const StatusChip = styled(Chip)(({ theme, status }) => {
    const getStatusConfig = (status) => {
      const configs = {
        pending: { 
          bg: '#FFB74D',
          text: '#FFFFFF',
          icon: <Schedule sx={{ fontSize: '16px !important' }} />
        },
        confirmed: { 
          bg: '#BA68C8',
          text: '#FFFFFF',
          icon: <CheckCircle sx={{ fontSize: '16px !important' }} />
        },
        processing: { 
          bg: '#BA68C8',
          text: '#FFFFFF',
          icon: <Schedule sx={{ fontSize: '16px !important' }} />
        },
        shipped: { 
          bg: '#66BB6A',
          text: '#FFFFFF',
          icon: <LocalShippingOutlined sx={{ fontSize: '16px !important' }} />
        },
        dispatch: {
          bg: '#66BB6A',
          text: '#FFFFFF',
          icon: <LocalShippingOutlined sx={{ fontSize: '16px !important' }} />
        },
        delivered: { 
          bg: '#66BB6A',
          text: '#FFFFFF',
          icon: <CheckCircle sx={{ fontSize: '16px !important' }} />
        },
        cancelled: { 
          bg: '#EF5350',
          text: '#FFFFFF',
          icon: <Cancel sx={{ fontSize: '16px !important' }} />
        },
        returned: { 
          bg: '#BA68C8',
          text: '#FFFFFF',
          icon: <Refresh sx={{ fontSize: '16px !important' }} />
        },
        orders: {
          bg: '#FFB74D',
          text: '#FFFFFF',
          icon: <Schedule sx={{ fontSize: '16px !important' }} />
        }
      }; 
      return configs[status?.toLowerCase()] || { 
        bg: theme.palette.grey[400], 
        text: theme.palette.common.white,
        icon: null
      };
    };

    const config = getStatusConfig(status);
    return {
      backgroundColor: config.bg,
      color: config.text,
      fontWeight: 600,
      textTransform: 'capitalize',
      borderRadius: theme.shape.borderRadius,
      height: 'auto',
      padding: '4px 10px',
      minWidth: 'auto',
      '& .MuiChip-icon': {
        color: config.text,
        marginLeft: '4px',
        marginRight: '4px'
      },
      '& .MuiChip-label': {
        padding: '0 4px',
        fontSize: '0.875rem',
        color: config.text,
        fontWeight: 600
      }
    };
  });

  const PaymentStatusChip = styled(Chip)(({ theme, status }) => {
    const getPaymentStatusConfig = (status) => {
      const configs = {
        pending: { 
          bg: '#fcc874',
          text: '#FFFFFF',
          icon: <Schedule sx={{ fontSize: '16px !important' }} />
        },
        outstanding: { 
          bg: '#888cff',
          text: '#FFFFFF',
          icon: <Schedule sx={{ fontSize: '16px !important' }} />
        },
        paid: { 
          bg: '#22d28a',
          text: '#FFFFFF',
          icon: <CheckCircle sx={{ fontSize: '16px !important' }} />
        },
        failed: { 
          bg: '#f4346e',
          text: '#FFFFFF',
          icon: <Cancel sx={{ fontSize: '16px !important' }} />
        },
        refunded: { 
          bg: '#888cff',
          text: '#FFFFFF',
          icon: <Refresh sx={{ fontSize: '16px !important' }} />
        },
      }; 
      return configs[status?.toLowerCase()] || { 
        bg: theme.palette.grey[400], 
        text: '#FFFFFF',
        icon: null
      };
    };

    const config = getPaymentStatusConfig(status);
    return {
      backgroundColor: config.bg,
      color: config.text,
      fontWeight: 600,
      textTransform: 'capitalize',
      borderRadius: theme.shape.borderRadius,
      height: 'auto',
      padding: '4px 10px',
      minWidth: 'auto',
      '& .MuiChip-icon': {
        color: config.text,
        marginLeft: '4px',
        marginRight: '4px'
      },
      '& .MuiChip-label': {
        padding: '0 4px',
        fontSize: '0.875rem',
        color: config.text,
        fontWeight: 600
      }
    };
  });

  const EmptyStateBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 300,
    padding: theme.spacing(4),
    textAlign: 'center',
    '& .empty-icon': {
      fontSize: 64,
      color: theme.palette.text.disabled,
      marginBottom: theme.spacing(2),
    }
  }));

  const InvoiceModal = styled(Dialog)(({ theme }) => ({
    '& .MuiDialog-paper': {
      maxWidth: '90vw',
      maxHeight: '90vh',
      width: '1200px',
      height: '800px',
      margin: theme.spacing(2),
    },
  }));

  const AppOrderPopup = styled(Dialog)(({ theme }) => ({
    '& .MuiDialog-paper': {
      borderRadius: 8,
      overflow: 'hidden',
      boxShadow: `0 12px 32px ${alpha(theme.palette.common.black, 0.18)}`,
    },
    '& .MuiDialogTitle-root': {
      padding: theme.spacing(2.5, 3, 1.5),
    },
    '& .MuiDialogContent-root': {
      padding: theme.spacing(0, 3, 2.25),
    },
    '& .MuiDialogActions-root': {
      padding: theme.spacing(0, 3, 2.5),
    },
  }));

  const InvoiceFrame = styled('iframe')(({ theme }) => ({
    width: '100%',
    height: '100%',
    border: 'none',
    borderRadius: theme.shape.borderRadius,
  }));

  const VEHICLE_REFERENCE_NO_REGEX = /^[A-Z]{2}\s\d{2}\s[A-Z]{2}\s\d{4}$/;
  const VEHICLE_REFERENCE_NO_ERROR = 'Enter valid format: MH 12 AB 1234';
  const formatVehicleReferenceNo = (value = '') => {
    const cleanedValue = String(value || '').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 10);
    const part1 = cleanedValue.slice(0, 2).replace(/[^A-Z]/g, '');
    const part2 = cleanedValue.slice(2, 4).replace(/[^0-9]/g, '');
    const part3 = cleanedValue.slice(4, 6).replace(/[^A-Z]/g, '');
    const part4 = cleanedValue.slice(6, 10).replace(/[^0-9]/g, '');
    return [part1, part2, part3, part4].filter(Boolean).join(' ');
  };
  const isVehicleReferenceNoInvalid = (value = '') => {
    const normalizedValue = String(value || '').trim();
    return normalizedValue !== '' && !VEHICLE_REFERENCE_NO_REGEX.test(normalizedValue);
  };

  const PAYMENT_ACCOUNT_DETAILS = {
    accountHolder: 'NEXGENAI AGRITECH',
    accountNumber: '039905009909',
    ifscCode: 'ICIC0000399',
    email: 'CEO@NEXGENAGRITECH.AI',
  };

  const formatPaymentCurrency = (amount) => {
    const value = parseFloat(amount);
    return `₹${(Number.isNaN(value) ? 0 : value).toLocaleString('en-IN')}`;
  };

  const formatOrderDateTime = (value) => {
    if (!value) return '—';
    try {
      return new Date(value).toLocaleString('en-IN', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return String(value);
    }
  };

  const ADVANCE_PAYMENT_FIELD_LABELS = {
    transaction_id: 'Transaction ID',
    transaction_reference_no: 'Transaction Reference No.',
    upi_id_used: 'UPI ID',
    payment_screenshot: 'Payment Screenshot',
    sender_bank_name: 'Bank Name',
    payment_confirmation_file: 'Payment Confirmation File',
    cheque_number: 'Cheque Number',
    bank_name_branch: 'Bank Name',
    cheque_date: 'Cheque Date',
    cheque_amount: 'Cheque Amount',
    cheque_image: 'Cheque Image',
    cash_receipt_number: 'Cash Receipt No.',
    receiver_name: 'Receiver Name (Staff)',
  };

  const getEntryBankName = (entry = {}) =>
    entry?.sender_bank_name ||
    entry?.fields?.sender_bank_name ||
    entry?.fields?.bank_name_branch ||
    '';

  const getEntryNote = (entry = {}, order = null) => {
    if (entry?.comment != null && String(entry.comment).trim() !== '') {
      return String(entry.comment).trim();
    }
    const notes = entry?.order?.notes ?? order?.notes;
    if (notes != null && String(notes).trim() !== '') {
      return String(notes).trim();
    }
    return '';
  };

  const getAdvancePaymentFieldRows = (entry = {}) => {
    const fields = entry?.fields;
    if (!fields || typeof fields !== 'object') return [];
    const skipKeys = new Set([
      'id',
      'created_at',
      'updated_at',
      'is_deleted',
      'sender_bank_name',
      'bank_name_branch',
    ]);
    return Object.entries(fields)
      .filter(([key, val]) => !skipKeys.has(key) && val != null && String(val).trim() !== '')
      .map(([key, val]) => ({
        key,
        label:
          ADVANCE_PAYMENT_FIELD_LABELS[key] ||
          key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
        value: String(val),
      }));
  };

  function AdvancePaymentEntryCard({ entry, index, theme, order = null }) {
    const fieldRows = getAdvancePaymentFieldRows(entry);
    const recordedBy =
      entry.user?.full_name ||
      [entry.user?.first_name, entry.user?.last_name].filter(Boolean).join(' ') ||
      '—';
    const [previewUrl, setPreviewUrl] = useState(null);
    const entries = getAdvancePaymentEntries(order) || [];

    return (
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          mb: index > 0 ? 0 : 0,
          borderRadius: 2,
          bgcolor: alpha(theme.palette.warning.main, 0.04),
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5, gap: 1 }}>
          <Typography variant="subtitle1" fontWeight={700}>
            Entry #{entries.length - index}
            {entry.is_full_payment ? ' · Pending Payment' : ' · Advanced Payment'}
          </Typography>
          <Chip
            size="small"
            label={entry.method?.name || '—'}
            color="warning"
            variant="outlined"
          />
        </Box>

        <Grid container spacing={1.5}>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2">
              <strong>{entry?.remaining_amount === 0 || entry?.remaining_amount === 0.0 ? "Pending Paid Amount" : "Advance Paid Amount"}:</strong> {formatPaymentCurrency(entry.advance_amount)}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2">
              <strong>Pending Amount:</strong> {formatPaymentCurrency(entry.remaining_amount)}
            </Typography>
          </Grid>
          {entry.transaction_id ? (
            <Grid item xs={12} sm={6}>
              <Typography variant="body2">
                <strong>Transaction ID:</strong> {entry.transaction_id}
              </Typography>
            </Grid>
          ) : null}
          
            <Grid item xs={12} sm={6}>
              <Typography variant="body2">
                <strong>Bank Name:</strong> {getEntryBankName(entry) || 'N/A'}
              </Typography>
            </Grid>
          
          <Grid item xs={12} sm={6}>
            <Typography variant="body2">
              <strong>Recorded By:</strong> {recordedBy}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2">
              <strong>Payment Date:</strong> {formatOrderDateTime(entry.created_at)}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2">
              <strong>Updated At:</strong> {formatOrderDateTime(entry.updated_at)}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2">
              <strong>Note:</strong> {getEntryNote(entry, order) || 'N/A'}
            </Typography>
          </Grid>
        </Grid>

        {fieldRows.length > 0 && (
          <>
            <Divider sx={{ my: 1.5 }} />
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              Payment method details
            </Typography>
            <Grid container spacing={1}>
              {fieldRows.map((row) => (
                <Grid item xs={12} sm={6} key={`${entry.id}-${row.key}`}>
                  {row.key === 'payment_screenshot' ||
                  row.key === 'payment_confirmation_file' ||
                  row.key === 'cheque_image' ? (
                    <Typography
                      variant="body2"
                      component="div"
                      sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}
                    >
                      <strong>{row.label}:</strong>
                      {row.value ? (
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => setPreviewUrl(row.value)}
                          sx={{
                            textTransform: 'none',
                            border: '1px solid #1e293b',
                            borderRadius: '8px',
                            px: '18px',
                            py: '6px',
                            fontSize: '13px',
                            fontWeight: 500,
                            color: '#1e293b',
                            bgcolor: '#fff',
                            '&:hover': {
                              border: '1px solid #1e293b',
                              bgcolor: '#f1f5f9',
                            },
                          }}
                        >
                          View
                        </Button>
                      ) : null}
                    </Typography>
                  ) : (
                    <Typography variant="body2">
                      <strong>{row.label}:</strong> {row.value}
                    </Typography>
                  )}
                </Grid>
              ))}
            </Grid>
          </>
        )}

        {entry.image && (
          <Typography variant="body2" sx={{ mt: 1.5 }}>
            <strong>Attachment:</strong>{' '}
            <Box
              component="a"
              href={entry.image}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ wordBreak: 'break-all' }}
            >
              View file
            </Box>
          </Typography>
        )}

        <Dialog
          open={Boolean(previewUrl)}
          onClose={() => setPreviewUrl(null)}
          maxWidth={false}
          PaperProps={{
            sx: {
              borderRadius: '12px',
              p: 2,
              maxWidth: '90vw',
              maxHeight: '90vh',
            },
          }}
          BackdropProps={{
            sx: {
              backgroundColor: 'rgba(0,0,0,0.6)',
            },
          }}
        >
          <Box sx={{ position: 'relative' }}>
            <IconButton
              onClick={() => setPreviewUrl(null)}
              size="small"
              sx={{
                position: 'absolute',
                top: 6,
                right: 6,
                zIndex: 1,
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
            <Box
              component="img"
              src={previewUrl || ''}
              alt="Payment Screenshot"
              sx={{
                maxWidth: '100%',
                maxHeight: '80vh',
                objectFit: 'contain',
                borderRadius: '10px',
              }}
            />
          </Box>
        </Dialog>
      </Paper>
    );
  }

  function AdvancePaymentDetailsSection({ order, theme }) {
    const entries = getAdvancePaymentEntries(order);
    if (entries.length === 0) return null;

    return (
      <Grid item xs={12}>
        <DetailCard
          elevation={2}
          sx={{
            backgroundColor: 'background.paper',
            border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
            borderRadius: 3,
            p: 2.5,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Payment sx={{ mr: 1, color: theme.palette.warning.main }} />
            <Typography variant="h6" fontWeight={600}>
              Payment Details ({entries.length})
            </Typography>
          </Box>
          {entries.map((entry, index) => (
            <Box key={entry.id ?? `advance-${index}`} sx={{ mb: index < entries.length - 1 ? 2 : 0 }}>
              <AdvancePaymentEntryCard entry={entry} index={index} theme={theme} order={order} />
            </Box>
          ))}
        </DetailCard>
      </Grid>
    );
  }

  const formatOrderDetailCurrency = (amount) =>
    `₹${parseFloat(amount).toLocaleString('en-IN')}`;

  const getOrderDetailStatusIcon = (status) => {
    const category = typeof status === 'object' && status !== null && 'category' in status
      ? status.category
      : status;
    const key = (typeof category === 'string' ? category : (category?.name ?? '')).toLowerCase();
    switch (key) {
      case 'pending': return <Schedule />;
      case 'confirmed': return <CheckCircle />;
      case 'processing': return <Schedule />;
      case 'shipped': return <LocalShippingOutlined />;
      case 'delivered': return <CheckCircle />;
      case 'cancelled': return <Cancel />;
      case 'returned': return <Refresh />;
      default: return null;
    }
  };

  function AppOrderDetailsView({ order, onCheckPaymentStatus, checkingPaymentOrderId = null }) {
    const theme = useTheme();
    return (
<Box sx={{ width: '100%', padding: 2 }}>
              <Grid container spacing={2.5} alignItems="stretch">
                <Grid item xs={12} md={4} sx={{ minWidth: 0 }}>
                  <DetailCard elevation={2} sx={{ height: '100%', display: 'flex', flexDirection: 'column', backgroundColor: 'background.paper', border: `1px solid ${alpha(theme.palette.divider, 0.5)}`, borderRadius: 3, p: 2.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Person sx={{ mr: 1, color: theme.palette.primary.main }} />
                      <Typography variant="h6" fontWeight={600}>Customer Information</Typography>
                    </Box>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Name:</strong> {order.user?.full_name || order.user_name}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Order ID:</strong> {order.order_id || "N/A"}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Email:</strong> {order.user?.email || order.user_email}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Order Date:</strong> {order.order_date_formatted || "N/A"}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Mobile:</strong> {order.user?.mobile_number || order.mobile_number}
                    </Typography>
                    
                    {(order.farmer_name || getAdvancePaymentEntries(order)[0]?.order?.farmer_name) && (
                      <Box sx={{ mt: 1, mb: 1, p: 1.5, backgroundColor: alpha(theme.palette.info.main, 0.1), borderRadius: 1, border: `1px solid ${alpha(theme.palette.info.main, 0.3)}` }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: "grey.600", mb: 0.5 }}>
                          Order on Behalf of : {order.farmer_name || getAdvancePaymentEntries(order)[0]?.order?.farmer_name}
                        </Typography>
                      </Box>
                    )}
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 2, mb: 1 }}>
                      <LocationOn sx={{ mr: 1, color: theme.palette.secondary.main }} />
                      <Typography variant="subtitle2" fontWeight={600}>Delivery Address</Typography>
                    </Box>
                    <Tooltip title={order.delivery_address || (order.address_details ? `${order.address_details.full_address}, ${order.address_details.village}, ${order.address_details.tehsil}, ${order.address_details.pincode}` : 'Address not available')} arrow placement="top">
                      <Typography variant="body2" color="text.secondary" sx={{ backgroundColor: alpha(theme.palette.background.default, 0.5), padding: 1.5, borderRadius: 1, border: `1px solid ${alpha(theme.palette.divider, 0.1)}`, maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'help' }}>
                        {order.delivery_address || (order.address_details ? `${order.address_details.full_address}, ${order.address_details.village}, ${order.address_details.tehsil}, ${order.address_details.pincode}` : 'Address not available')}
                      </Typography>
                    </Tooltip>
                    
                    {order.notes && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }} fontWeight={600}>Special Instructions:</Typography>
                        <Tooltip title={order.notes} arrow placement="top">
                          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', p: 1.5, backgroundColor: alpha(theme.palette.info.main, 0.1), borderRadius: 1, border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`, maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'help' }}>
                            {order.notes}
                          </Typography>
                        </Tooltip>
                      </Box>
                    )}
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 2, mb: 1 }}>
                      <LocationOn sx={{ mr: 1, color: theme.palette.warning.main }} />
                      <Typography variant="subtitle2" fontWeight={600}>Billing Address</Typography>
                    </Box>
                    <Tooltip title={order.billing_address || (order.bill_address_details ? `${order.bill_address_details.full_address}, ${order.bill_address_details.village}, ${order.bill_address_details.tehsil}, ${order.bill_address_details.pincode}` : 'Billing address not available')} arrow placement="top">
                      <Typography variant="body2" color="text.secondary" sx={{ backgroundColor: alpha(theme.palette.background.default, 0.5), padding: 1.5, borderRadius: 1, border: `1px solid ${alpha(theme.palette.divider, 0.1)}`, maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'help' }}>
                        {order.billing_address || (order.bill_address_details ? `${order.bill_address_details.full_address}, ${order.bill_address_details.village}, ${order.bill_address_details.tehsil}, ${order.bill_address_details.pincode}` : 'Billing address not available')}
                      </Typography>
                    </Tooltip>
                  </DetailCard>
                </Grid>

                <Grid item xs={12} md={4} sx={{ minWidth: 0 }}>
                  <DetailCard elevation={2} sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: 2.5, backgroundColor: 'background.paper', border: `1px solid ${alpha(theme.palette.divider, 0.5)}`, borderRadius: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2.5 }}>
                      <LocalShipping sx={{ mr: 1.25, fontSize: '1.25rem', color: theme.palette.success.main }} />
                      <Typography variant="h6" fontWeight={600}>Order Status</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5 }}>
                      <Box>
                        <Typography variant="body2" fontWeight={500}>
                          <strong>Order Status:</strong> <StatusChip label={order.status?.name || order.order_status_display} status={order.status?.category?.toLowerCase() || order.status?.name?.toLowerCase()} size="small" icon={getOrderDetailStatusIcon(order.status)} />
                        </Typography>
                      </Box>
                    </Box>
                    
                    {order.expected_delivery_date && (
                      <Box sx={{ mb: 1.5 }}>
                        <Typography variant="body2"><strong>Expected Delivery Date (Farmer):</strong></Typography>
                        <Typography variant="body2" color="primary" fontWeight={600} sx={{ mt: 0.5 }}>
                          {new Date(order.expected_delivery_date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                        </Typography>
                      </Box>
                    )}
                    
                    {order.delivery_date && ['delivered', 'returned'].includes(
                      (order.status?.category || order.status?.name || '').toString().toLowerCase()
                    ) && (
                      <Box sx={{ mt: 2.5, mb: 1.5 }}>
                        <Typography variant="body2" sx={{ mb: 1.25 }}>
                          <strong>Actual Delivery Date:</strong>{' '}
                          <Typography component="span" variant="body2" color="success.main" fontWeight={600}>
                            {(() => {
                              try {
                                const date = new Date(order.delivery_date);
                                return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
                              } catch (e) {
                                return order.delivery_date_formatted || order.delivery_date;
                              }
                            })()}
                          </Typography>
                        </Typography>
                      </Box>
                    )}

                    {(order.status?.category || order.status?.name || '').toString().toLowerCase() === 'returned' && (
                      <Box sx={{ mt: 2.5, mb: 1.5 }}>
                        <Typography variant="body2" sx={{ mb: 1.25 }}>
                          <strong>Returned At:</strong>{' '}
                          {(() => {
                            const dt = order.returned_at ?? order.status?.returned_at;
                            if (!dt) return 'N/A';
                            try {
                              const date = new Date(dt);
                              return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
                            } catch (e) {
                              return String(dt);
                            }
                          })()}
                        </Typography>
                      </Box>
                    )}

                    {(order.status?.category || order.status?.name || '').toString().toLowerCase() === 'cancelled' && (
                      <Box sx={{ mt: 2.5, mb: 1.5 }}>
                        <Typography variant="body2" sx={{ mb: 1.25 }}>
                          <strong>Cancelled At:</strong>{' '}
                          {(() => {
                            const dt = order.cancelled_at ?? order.status?.cancelled_at;
                            if (!dt) return 'N/A';
                            try {
                              const date = new Date(dt); 
                              return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
                            } catch (e) {
                              return String(dt);
                            }
                          })()}
                        </Typography>
                      </Box>
                    )}
                    
                    {order.reason && (
                      <Box sx={{ mb: 2.5 }}>
                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                          <strong>Reason:</strong> {order.reason}
                        </Typography>
                      </Box>
                    )}

                    <Divider sx={{ my: 2 }} />
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Payment sx={{ mr: 1.25, fontSize: '1.25rem', color: theme.palette.info.main }} />
                      <Typography variant="subtitle1" fontWeight={600}>Payment Information</Typography>
                    </Box>
                    <Typography variant="body2" sx={{ mb: 1.25 }}>
                      <strong>Payment Status:</strong> 
                      <PaymentStatusChip label={order.payment_status?.name || order.payment_status_display || 'Pending'} status={(order.payment_status?.name || order.payment_status || 'pending')?.toLowerCase()} size="small" sx={{ ml: 1.25 }} />
                    </Typography>
                    
                    {/* {((order.reason ?? order.status?.reason) != null && String(order.reason ?? order.status?.reason ?? '').trim() !== '') && (
                      <Typography variant="body2" sx={{ mb: 1.25, mt: 1 }}>
                        <strong>Reason:</strong> {order.reason ?? order.status?.reason ?? ''}
                      </Typography>
                    )} */}
                    
                    {order?.latest_refund && (
                      <>
                        <Typography variant="body2" sx={{ mb: 1.25 }}>
                          <strong>Refunded Amount:</strong> ₹{order?.latest_refund?.amount ?? "N/A"}
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1.25 }}>
                          <strong>Refunded At:</strong> {order?.latest_refund?.refunded_at ? new Date(order.latest_refund.refunded_at).toLocaleString() : "N/A"}
                        </Typography>
                      </>
                    )}
                    
                    {(order.payment_status?.name || order.payment_status || '').toString().toLowerCase() === 'refunded' && (
                      <>
                        <Typography variant="body2" sx={{ mb: 1.25 }}>
                          <strong>Refunded Amount:</strong> {order.refunded_amount ?? order.payment_status?.refunded_amount ?? 'N/A'}
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1.25 }}>
                          <strong>Refunded At:</strong> {(() => {
                            const dt = order.refunded_at ?? order.payment_status?.refunded_at;
                            if (!dt) return 'N/A';
                            try {
                              const date = new Date(dt);
                              return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
                            } catch (e) {
                              return String(dt);
                            }
                          })()}
                        </Typography>
                      </>
                    )}

                    {order?.latest_refund && !order?.latest_refund?.is_success && (
                      <Button size="small" variant="outlined" color="primary" onClick={(e) => { e.stopPropagation(); onCheckPaymentStatus?.(order); }} disabled={checkingPaymentOrderId === (order?.order_id ?? order?.id)} sx={{ mt: 1.5, textTransform: 'none', fontWeight: 600 }}>
                        {checkingPaymentOrderId === (order?.order_id ?? order?.id) ? (<CircularProgress size={18} sx={{ mr: 0.5 }} />) : null}
                        Check refund status
                      </Button>
                    )}
                  </DetailCard>
                </Grid>

                {order.delivery_challan && (
                  <Grid item xs={12} md={4} sx={{ minWidth: 0 }}>
                    <DetailCard elevation={2} sx={{ height: '100%', display: 'flex', flexDirection: 'column', backgroundColor: 'background.paper', border: `1px solid ${alpha(theme.palette.divider, 0.5)}`, borderRadius: 3, p: 2.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <ChallanIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
                        <Typography variant="h6" fontWeight={600}>Delivery Challan Details</Typography>
                      </Box>
                      {order.delivery_challan.vehicle_number && <Typography variant="body2" sx={{ mb: 1 }}><strong>Vehicle Number:</strong> {order.delivery_challan.vehicle_number}</Typography>}
                      {order.delivery_challan.vehicle_name && <Typography variant="body2" sx={{ mb: 1 }}><strong>Vehicle Name:</strong> {order.delivery_challan.vehicle_name}</Typography>}
                      {order.delivery_challan.driver_name && <Typography variant="body2" sx={{ mb: 1 }}><strong>Driver Name:</strong> {order.delivery_challan.driver_name}</Typography>}
                      {order.delivery_challan.dispatch_person_name && <Typography variant="body2" sx={{ mb: 1 }}><strong>Dispatch Person:</strong> {order.delivery_challan.dispatch_person_name}</Typography>}
                      {order.order_by && <Typography variant="body2" sx={{ mb: 1 }}><strong>Order By:</strong> {order.order_by}</Typography>}
                      {order.order_by_entity && <Typography variant="body2" sx={{ mb: 1 }}><strong>Entity Name:</strong> {order.order_by_entity}</Typography>}
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="subtitle2" fontWeight={600} gutterBottom>Kilometer Information</Typography>
                      {order.delivery_challan.start_km !== null && order.delivery_challan.start_km !== undefined && <Typography variant="body2" sx={{ mb: 1 }}><strong>Start KM:</strong> {order.delivery_challan.start_km}</Typography>}
                      {order.delivery_challan.end_km !== null && order.delivery_challan.end_km !== undefined && <Typography variant="body2" sx={{ mb: 1 }}><strong>End KM:</strong> {order.delivery_challan.end_km}</Typography>}
                      {order.delivery_challan.total_km !== null && order.delivery_challan.total_km !== undefined && <Typography variant="body2" sx={{ mb: 1 }}><strong>Total KM:</strong> {order.delivery_challan.total_km}</Typography>}
                    </DetailCard>
                  </Grid>
                )}

                <AdvancePaymentDetailsSection order={order} theme={theme} />

                <Grid item xs={12}>
                  <DetailCard elevation={2} sx={{ backgroundColor: 'background.paper', border: `1px solid ${alpha(theme.palette.divider, 0.5)}`, borderRadius: 3, p: 2.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <ShoppingCart sx={{ mr: 1, color: theme.palette.primary.main }} />
                      <Typography variant="h5" fontWeight={600}>Order Items ({order.total_items})</Typography>
                    </Box>
                    
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 600, width: '80px' }}>Sr. No.</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Product</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>SKU</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>Unit Price</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>Quantity</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>GST Rate</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>Total</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {order.items.map((item, index) => (
                            <TableRow key={index} sx={{ '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.04) } }}>
                              <TableCell>{index + 1}</TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ maxWidth: 220, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", fontWeight: 500 }} title={item.product_name}>
                                  {item.product_name}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }} title={item.category_name}>
                                  {item.category_name || 'N/A'}
                                </Typography>
                              </TableCell>
                              <TableCell><Typography variant="body2" color="text.secondary">{item.product_sku}</Typography></TableCell>
                              <TableCell align="right">{formatOrderDetailCurrency(item.unit_price)}</TableCell>
                              <TableCell align="right">{parseFloat(item.quantity)}</TableCell>
                              <TableCell align="right">{parseFloat(item.gst_rate)}%</TableCell>
                              <TableCell align="right"><Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.primary.main }}>{formatOrderDetailCurrency(item.item_total_with_gst)}</Typography></TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    
                    <Divider sx={{ my: 2 }} />
                    
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" sx={{ mb: 0.5 }}><strong>Total Items:</strong> {order.total_items}</Typography>
                        <Typography variant="body2"><strong>Total Quantity:</strong> {parseFloat(order.total_quantity)}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography variant="body2" sx={{ mb: 0.5 }}><strong>Subtotal:</strong> {formatOrderDetailCurrency(order.subtotal)}</Typography>
                          <Typography variant="body2" sx={{ mb: 0.5 }}><strong>GST Amount:</strong> {formatOrderDetailCurrency(order.gst_amount)}</Typography>
                          <Typography variant="body2" sx={{ mb: 0.5 }}><strong>Delivery Charge:</strong> {formatOrderDetailCurrency(order.delivery_charge)}</Typography>
                          {parseFloat(order.discount_amount) > 0 && <Typography variant="body2" color="success.main" sx={{ mb: 0.5 }}><strong>Discount:</strong> -{formatOrderDetailCurrency(order.discount_amount)}</Typography>}
                          <Divider sx={{ my: 1 }} />
                          <Typography variant="h6" color="primary.main" fontWeight={700}>Total: {formatOrderDetailCurrency(order.total_amount)}</Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </DetailCard>
                </Grid>
              </Grid>
            </Box>
    
    );
  }


  const sanitizePaymentFieldValue = (value = '', maxLength) => {
    const cleaned = String(value).replace(/[^a-zA-Z0-9\s.\-/@]/g, '');
    return maxLength ? cleaned.slice(0, maxLength) : cleaned;
  };

  const PAYMENT_FIELD_SANITIZERS = {
    transaction_reference_no: (value, maxLength = 22) =>
      String(value).toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, maxLength),
    sender_bank_name: (value, maxLength = 50) =>
      String(value).replace(/[^a-zA-Z\s]/g, '').slice(0, maxLength),
    cheque_number: (value, maxLength = 6) =>
      String(value).replace(/\D/g, '').slice(0, maxLength),
    bank_name_branch: (value, maxLength = 50) =>
      String(value).replace(/[^a-zA-Z\s]/g, '').slice(0, maxLength),
    cheque_amount: (value) => {
      const cleaned = String(value).replace(/[^\d.]/g, '');
      const [whole, fraction = ''] = cleaned.split('.');
      if (!cleaned.includes('.')) return whole.slice(0, 12);
      return `${whole.slice(0, 12)}.${fraction.slice(0, 2)}`;
    },
    receiver_name: (value, maxLength = 50) =>
      String(value).replace(/[^a-zA-Z\s]/g, '').slice(0, maxLength),
  };

  const sanitizePaymentFieldByKey = (key, value, maxLength) => {
    const sanitizer = PAYMENT_FIELD_SANITIZERS[key];
    if (sanitizer) return sanitizer(value, maxLength);
    return sanitizePaymentFieldValue(value, maxLength);
  };

  const getChequeDateBounds = () => {
    const today = new Date();
    const min = new Date(today);
    min.setDate(min.getDate() - 90);
    const max = new Date(today);
    max.setDate(max.getDate() + 365);
    return {
      min: min.toISOString().slice(0, 10),
      max: max.toISOString().slice(0, 10),
    };
  };

  const PAYMENT_DETAILS_DIALOG_CONTENT_SX = {
    flex: '1 1 auto',
    overflowY: 'auto',
    py: 1,
    px: 2,
  };

  const PAYMENT_DETAILS_DIALOG_ACTIONS_SX = {
    flexShrink: 0,
    px: 2,
    py: 1,
    borderTop: 1,
    borderColor: 'divider',
    bgcolor: 'background.paper',
  };

  const PAYMENT_DETAILS_DIALOG_PAPER_PROPS = {
    sx: {
      display: 'flex',
      flexDirection: 'column',
      maxHeight: '90vh',
      maxWidth: { xs: 'calc(100% - 16px)', sm: 520 },
      width: '100%',
      m: { xs: 1, sm: 2 },
    },
  };

  const PAYMENT_BANK_NAME_FIELD_KEYS = new Set([
    'sender_bank_name',
    'bank_name_branch',
    'bank_name',
  ]);

  const isPaymentBankNameField = (field = {}) => {
    const key = String(field.key || '').toLowerCase();
    const label = String(field.label || '').toLowerCase();
    if (PAYMENT_BANK_NAME_FIELD_KEYS.has(key)) return true;
    return label.includes('sender bank') || label.includes('bank name');
  };

  const enforcePaymentMethodRequiredFields = (formConfig) => {
    if (!formConfig?.fields) return formConfig;
    const enforcedConfig = {
      ...formConfig,
      fields: formConfig.fields.map((field) =>
        isPaymentBankNameField(field)
          ? { ...field, required: false }
          : { ...field, required: true }
      ),
    };
    if (enforcedConfig.fileField) {
      enforcedConfig.fileField = { ...enforcedConfig.fileField, required: true };
    }
    return enforcedConfig;
  };

  const getCreditChequePaymentFields = (paymentFields, installmentAmount, paymentMethodConfig) => {
    if (paymentMethodConfig?.type !== 'cheque') return paymentFields;
    return {
      ...paymentFields,
      cheque_amount: installmentAmount ?? '',
    };
  };

  const validatePaymentMethodMandatoryFields = (modalState) => {
    if (isZeroRemainingPayment(modalState)) return '';
    if (!modalState?.paymentMethod || !modalState?.paymentMethodConfig) return '';

    const fields = modalState.paymentFields || {};
    const paymentFiles = modalState.paymentFiles || {};
    const formConfig = enforcePaymentMethodRequiredFields(modalState.paymentMethodConfig);
    const type = formConfig.type;

    const missingField = formConfig.fields?.find((field) => {
      if (!field.required) return false;
      return !String(fields[field.key] || '').trim();
    });
    if (missingField) {
      return `Please enter ${missingField.label}`;
    }

    if (type === 'bank_transfer') {
      const ref = String(fields.transaction_reference_no || fields.transaction_id || '').trim();
      if (!/^[A-Z0-9]+$/.test(ref)) {
        return 'Transaction Reference No. must contain only English letters and numbers (A–Z, 0–9)';
      }
    }
    if (type === 'cash') {
      const receiver = String(fields.receiver_name || '').trim();
      if (!/^[a-zA-Z\s]+$/.test(receiver)) {
        return 'Receiver Name must contain only English letters';
      }
    }
    if (type === 'cheque') {
      const chequeNo = String(fields.cheque_number || '').trim();
      if (!/^\d{1,6}$/.test(chequeNo)) {
        return 'Cheque Number must be 1 to 6 digits';
      }
    }
    if (
      formConfig.fileField?.required &&
      !(paymentFiles[formConfig.fileField.key] instanceof File)
    ) {
      return `Please upload ${formConfig.fileField.label}`;
    }
    return '';
  };

  const buildNewCreditPaymentFormData = (modalState, orderId, installmentAmount, creditNote = '') => {
    const paidFormState = {
      ...modalState,
      advanceAmount: String(installmentAmount),
      orderPaymentDetails: modalState?.orderPaymentDetails
        ? { ...modalState.orderPaymentDetails, remainingAmount: installmentAmount }
        : { remainingAmount: installmentAmount, totalAmount: installmentAmount, paidAmount: 0 },
    };
    const paidFormData = buildNewPaidPaymentFormData(paidFormState, orderId);
    const formData = new FormData();
    for (const [key, value] of paidFormData.entries()) {
      if (key === 'payment_status') {
        formData.append(key, 'Credit');
      } else if (key === 'paid_amount') {
        formData.append(key, String(installmentAmount));
      } else {
        formData.append(key, value);
      }
    }
    if (creditNote.trim()) {
      formData.append('note', creditNote.trim());
    }
    return formData;
  };

  const getInlinePaidPaymentStepState = (orderPaymentDetails) => ({
    ...getPaidPaymentStepState(orderPaymentDetails),
    step: 'status',
  });

  const getCreditPaymentStepState = (orderPaymentDetails = null) => {
    const remainingAmount = Number(orderPaymentDetails?.remainingAmount ?? 0);
    return {
    step: 'status',
    paymentOption: 'credit',
    installmentAmount: remainingAmount === 0 ? '0' : '',
    creditNote: '',
    paymentMethod: '',
    paymentMethodConfig: null,
    paymentFields: {},
    paymentFiles: {},
    paymentError: '',
  };
  };

  function PaymentAmountSummary({ orderPaymentDetails }) {
    if (!orderPaymentDetails) return null;
    const rows = [
      ['Total Amount', Number(orderPaymentDetails.totalAmount ?? 0)],
      ['Paid Amount', Number(orderPaymentDetails.paidAmount ?? 0)],
      ['Remaining Amount', Number(orderPaymentDetails.remainingAmount ?? 0)],
    ];

    return (
      <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5, borderRadius: 2 }}>
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
          Payment Summary
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
            gap: 1.25,
          }}
        >
          {rows.map(([label, value]) => (
            <Box key={label}>
              <Typography variant="caption" color="text.secondary" display="block">
                {label}
              </Typography>
              <Typography variant="body1" fontWeight={700}>
                {formatPaymentCurrency(value)}
              </Typography>
            </Box>
          ))}
        </Box>
      </Paper>
    );
  }

  function PaymentDetailsStepContent({
    idPrefix,
    order,
    orderPaymentDetails = null,
    paymentOption,
    advanceAmount,
    onAdvanceAmountChange,
    installmentAmount = '',
    onInstallmentAmountChange,
    creditNote = '',
    onCreditNoteChange,
    paymentMethod,
    paymentMethodConfig = null,
    onPaymentMethodChange,
    advancePaymentMethods = [],
    loadingAdvancePaymentMethods = false,
    paymentFields,
    paymentFiles,
    onFieldChange,
    onFileChange,
    summaryBanner = null,
    itemsLimit = null,
    totalLabel = 'Total',
  }) {
    const isCreditFlow = paymentOption === 'credit';
    const theme = useTheme();
    const items = Array.isArray(order?.items) ? order.items : [];
    const displayItems = itemsLimit != null ? items.slice(0, itemsLimit) : items;
    const totalAmount = Number(
      orderPaymentDetails?.totalAmount ?? order?.total_amount ?? order?.total ?? 0
    );
    const remainingAmount = Number(orderPaymentDetails?.remainingAmount ?? 0);
    const useApiPaymentSummary = !!orderPaymentDetails;
    const zeroRemaining = remainingAmount === 0;

    return (
      <Box>
        {summaryBanner}

        {useApiPaymentSummary ? (
          <PaymentAmountSummary orderPaymentDetails={orderPaymentDetails} />
        ) : (
          <>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.75 }}>
              Order Summary
            </Typography>
            <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5, borderRadius: 2 }}>
              {displayItems.length > 0 ? (
                displayItems.map((item, index) => (
                  <Box
                    key={`${item.product_sku || item.product_name}-${index}`}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      gap: 1.5,
                      py: 0.5,
                      borderBottom:
                        index < displayItems.length - 1
                          ? `1px solid ${alpha(theme.palette.divider, 0.6)}`
                          : 'none',
                    }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={600} noWrap>
                        {item.product_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {item.product_sku || '—'}
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ flexShrink: 0 }}>
                      {parseFloat(item.quantity || 0)} x {formatPaymentCurrency(item.unit_price || 0)}
                    </Typography>
                  </Box>
                ))
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {order?.order_id ? `Reference order: ${order.order_id}` : 'No line items available'}
                </Typography>
              )}
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  Total Items: {order?.total_items ?? items.length} items
                </Typography>
                <Typography variant="body2" fontWeight={700}>
                  {totalLabel}: {formatPaymentCurrency(totalAmount)}
                </Typography>
              </Box>
            </Paper>
          </>
        )}

        {useApiPaymentSummary && isCreditFlow && (() => {
          const installmentValue = Number(installmentAmount);
          const exceedsRemaining =
            installmentAmount !== '' &&
            !Number.isNaN(installmentValue) &&
            installmentValue > remainingAmount;

          return (
          <>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Installment Amount"
              value={zeroRemaining ? '0' : installmentAmount}
              onChange={onInstallmentAmountChange}
              placeholder="Enter installment amount"
              disabled={zeroRemaining}
              sx={{ mb: 1.5 }}
              error={!zeroRemaining && exceedsRemaining}
              helperText={
                !zeroRemaining && exceedsRemaining
                  ? 'Installment amount cannot exceed the remaining amount'
                  : ''
              }
              inputProps={{ min: 0, max: remainingAmount, step: 0.01 }}
            />
            {!zeroRemaining && (
              <>
                <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                  <InputLabel id={`${idPrefix}-payment-method-select-label`}>Select Payment Method</InputLabel>
                  <Select
                    labelId={`${idPrefix}-payment-method-select-label`}
                    label="Select Payment Method"
                    value={paymentMethod}
                    onChange={onPaymentMethodChange}
                    disabled={loadingAdvancePaymentMethods}
                  >
                    <MenuItem value="">
                      <em>Select payment method</em>
                    </MenuItem>
                    {loadingAdvancePaymentMethods && (
                      <MenuItem disabled>
                        <CircularProgress size={16} sx={{ mr: 1 }} />
                        Loading payment methods...
                      </MenuItem>
                    )}
                    {!loadingAdvancePaymentMethods && advancePaymentMethods.length === 0 && (
                      <MenuItem disabled>No payment methods available</MenuItem>
                    )}
                    {advancePaymentMethods.map((method) => (
                      <MenuItem key={`${idPrefix}-credit-${method.value}`} value={method.value}>
                        {method.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <AdminPaymentMethodDetailsSection
                  idPrefix={idPrefix}
                  formConfig={paymentMethodConfig}
                  orderPaymentDetails={orderPaymentDetails}
                  paymentFields={paymentFields}
                  paymentFiles={paymentFiles}
                  onFieldChange={onFieldChange}
                  onFileChange={onFileChange}
                />
              </>
            )}
            <Box
              sx={{
                ...(paymentMethodConfig?.fileField?.key &&
                paymentFiles[paymentMethodConfig.fileField.key]
                  ? { mt: 2 }
                  : {}),
              }}
            >
              <TextField
                fullWidth
                size="small"
                label="Note"
                value={creditNote}
                onChange={onCreditNoteChange}
                placeholder="Enter note"
                multiline
                minRows={3}
                required
                sx={{ mb: 1.5 }}
              />
            </Box>
          </>
          );
        })()}

        {useApiPaymentSummary && !isCreditFlow && (
          <TextField
            fullWidth
            size="small"
            label="Payment Amount"
            value={formatPaymentCurrency(remainingAmount)}
            disabled
            sx={{ mb: 1.5 }}
            helperText={
              zeroRemaining
                ? 'No payment required — paid amount will be 0'
                : 'Payment amount must equal the remaining amount'
            }
          />
        )}

        {!isCreditFlow && zeroRemaining && (
          <Alert severity="info" sx={{ mb: 1.5 }}>
            Remaining amount is 0. Submit to mark the order as Paid without payment details.
          </Alert>
        )}

        {!isCreditFlow && !zeroRemaining && (
          <>
        {/* <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.75 }}>
          Account Details
        </Typography>
        <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5, borderRadius: 2 }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 1.25,
            }}
          >
            {[
              ['Account Holder', PAYMENT_ACCOUNT_DETAILS.accountHolder],
              ['Account Number', PAYMENT_ACCOUNT_DETAILS.accountNumber],
              ['IFSC Code', PAYMENT_ACCOUNT_DETAILS.ifscCode],
              ['Email', PAYMENT_ACCOUNT_DETAILS.email],
            ].map(([label, value]) => (
              <Box key={`${idPrefix}-${label}`}>
                <Typography variant="caption" color="text.secondary" display="block">
                  {label}
                </Typography>
                <Typography variant="body2" fontWeight={600} sx={{ wordBreak: 'break-word' }}>
                  {value}
                </Typography>
              </Box>
            ))}
          </Box>
        </Paper> */}
          </>
        )}

        {!useApiPaymentSummary && paymentOption === 'advance' && (
          <TextField
            fullWidth
            size="small"
            type="number"
            label="Advance Amount"
            value={advanceAmount}
            onChange={onAdvanceAmountChange}
            sx={{ mb: 1.5 }}
            inputProps={{ min: 0, max: totalAmount, step: 0.01 }}
          />
        )}

        {!isCreditFlow && !zeroRemaining && (
          <FormControl fullWidth size="small" sx={{ mb: 1 }}>
            <InputLabel id={`${idPrefix}-payment-method-select-label`}>Select Payment Method</InputLabel>
            <Select
              labelId={`${idPrefix}-payment-method-select-label`}
              label="Select Payment Method"
              value={paymentMethod}
              onChange={onPaymentMethodChange}
              disabled={loadingAdvancePaymentMethods}
            >
              <MenuItem value="">
                <em>Select payment method</em>
              </MenuItem>
              {loadingAdvancePaymentMethods && (
                <MenuItem disabled>
                  <CircularProgress size={16} sx={{ mr: 1 }} />
                  Loading payment methods...
                </MenuItem>
              )}
              {!loadingAdvancePaymentMethods && advancePaymentMethods.length === 0 && (
                <MenuItem disabled>No payment methods available</MenuItem>
              )}
              {advancePaymentMethods.map((method) => (
                <MenuItem key={`${idPrefix}-${method.value}`} value={method.value}>
                  {method.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {!isCreditFlow && !zeroRemaining && (
          <AdminPaymentMethodDetailsSection
            idPrefix={idPrefix}
            formConfig={enforcePaymentMethodRequiredFields(paymentMethodConfig)}
            orderPaymentDetails={orderPaymentDetails}
            paymentFields={paymentFields}
            paymentFiles={paymentFiles}
            onFieldChange={onFieldChange}
            onFileChange={onFileChange}
          />
        )}

        {useApiPaymentSummary && !isCreditFlow && (
          <TextField
            fullWidth
            size="small"
            label="Note"
            value={creditNote}
            onChange={onCreditNoteChange}
            placeholder="Enter note"
            multiline
            minRows={3}
            required
            sx={{
              mb: 1.5,
              ...(paymentMethodConfig?.fileField?.key &&
              paymentFiles[paymentMethodConfig.fileField.key]
                ? { mt: 1.5 }
                : {}),
            }}
          />
        )}
      </Box>
    );
  }

  const MAX_PAYMENT_CONFIRMATION_FILE_SIZE_BYTES = 2 * 1024 * 1024;

  function AdminPaymentMethodDetailsSection({
    idPrefix,
    formConfig = null,
    orderPaymentDetails = null,
    paymentFields = {},
    paymentFiles = {},
    onFieldChange,
    onFileChange,
  }) {
    const [paymentFileError, setPaymentFileError] = useState('');

    if (!formConfig) return null;

    const fileInputId = `${idPrefix}-${formConfig.fileField?.key || 'file'}-upload`;
    const chequeDateBounds = getChequeDateBounds();
    const remainingAmount = Number(orderPaymentDetails?.remainingAmount ?? 0);

    const renderPaymentField = (field) => {
      if (field.key === 'cheque_amount' && field.readOnlyWhenRemaining && orderPaymentDetails) {
        return (
          <TextField
            key={`${idPrefix}-${field.key}`}
            fullWidth
            size="small"
            sx={{ mb: 1 }}
            label={`${field.label} *`}
            value={
              field.key === 'cheque_amount' && Object.prototype.hasOwnProperty.call(paymentFields, 'cheque_amount')
                ? paymentFields.cheque_amount
                : paymentFields[field.key] ||
                  (remainingAmount > 0 ? String(remainingAmount) : '')
            }
            disabled
            helperText="Must equal the remaining order amount"
          />
        );
      }

      const isDateField = field.type === 'date';
      const isDecimalField = field.type === 'decimal';

      return (
        <TextField
          key={`${idPrefix}-${field.key}`}
          fullWidth
          size="small"
          sx={{ mb: 1 }}
          label={`${field.label}${field.required ? ' ' : ''}`}
          type={isDateField ? 'date' : isDecimalField ? 'text' : 'text'}
          value={paymentFields[field.key] || ''}
          onChange={(e) =>
            onFieldChange(
              field.key,
              sanitizePaymentFieldByKey(field.key, e.target.value, field.maxLength)
            )
          }
          inputProps={{
            ...(field.maxLength ? { maxLength: field.maxLength } : {}),
            ...(field.key === 'transaction_reference_no'
              ? { style: { textTransform: 'uppercase' } }
              : {}),
            ...(isDateField
              ? { min: chequeDateBounds.min, max: chequeDateBounds.max }
              : {}),
            ...(isDecimalField ? { inputMode: 'decimal' } : {}),
            ...(field.key === 'cheque_number' ? { inputMode: 'numeric' } : {}),
          }}
          helperText={
            field.maxLength
              ? `${String(paymentFields[field.key] || '').length}/${field.maxLength}`
              : field.key === 'transaction_reference_no'
                ? 'Alphanumeric (A–Z, 0–9) only'
                : undefined
          }
          FormHelperTextProps={{ sx: { textAlign: 'right', mr: 0 } }}
          InputLabelProps={isDateField ? { shrink: true } : undefined}
          required={field.required}
        />
      );
    };

    return (
      <Paper variant="outlined" sx={{ p: 1.5, mt: 1, borderRadius: 2 }}>
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>
          {formConfig.title}
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.4 }}>
          (*) marked fields are mandatory
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          sx={{ mb: 1.25, lineHeight: 1.4, fontStyle: 'italic' }}
        >
          (*) Only English numbers and alphabets are allowed, please fill the fields accordingly
        </Typography>

        {formConfig.fields.map((field) => renderPaymentField(field))}

        {formConfig.fileField && (
          <Box sx={{ mt: 0.5 }}>
            <Typography variant="body2" fontWeight={500} sx={{ mb: 0.75 }}>
              {formConfig.fileField.label}
              {formConfig.fileField.required ? ' *' : ''}
            </Typography>
            <input
              type="file"
              accept={formConfig.fileField.accept || 'image/*,.pdf,application/pdf'}
              style={{ display: 'none' }}
              id={fileInputId}
              onChange={(e) => {
                const file = e.target.files?.[0] || null;
                e.target.value = '';

                if (!file) {
                  setPaymentFileError('');
                  onFileChange(formConfig.fileField.key, null);
                  return;
                }

                const isImageOrPdf =
                  file.type.startsWith('image/') ||
                  file.type === 'application/pdf' ||
                  /\.(pdf|jpe?g|png|gif|webp|bmp)$/i.test(file.name || '');

                if (!isImageOrPdf) {
                  setPaymentFileError('Only image or PDF files are allowed.');
                  onFileChange(formConfig.fileField.key, null);
                  return;
                }

                if (file.size > MAX_PAYMENT_CONFIRMATION_FILE_SIZE_BYTES) {
                  setPaymentFileError('File size must not exceed 2 MB. Please choose a smaller image or PDF.');
                  onFileChange(formConfig.fileField.key, null);
                  return;
                }

                setPaymentFileError('');
                onFileChange(formConfig.fileField.key, file);
              }}
            />
            <Button
              variant="contained"
              component="label"
              htmlFor={fileInputId}
              sx={{ textTransform: 'none' }}
            >
              Choose file from gallery / device
            </Button>
            {paymentFileError && (
              <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                {paymentFileError}
              </Typography>
            )}
            {paymentFiles[formConfig.fileField.key] && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Selected: {paymentFiles[formConfig.fileField.key].name}
              </Typography>
            )}
          </Box>
        )}
      </Paper>
    );
  }

  const closePaymentUpdateModalState = () => ({ ...DEFAULT_PAYMENT_UPDATE_MODAL });

  const openPaymentUpdateModalState = (order) => {
    const paymentStatus = getOrderPaymentStatusValue(order);
    const orderDetails = getOrderPaymentDetailsFromOrder(order);
    const base = {
      ...DEFAULT_PAYMENT_UPDATE_MODAL,
      open: true,
      order,
      initialPaymentStatus: paymentStatus,
      refundAmount: order?.refunded_amount ?? order?.refund_amount ?? order?.total_amount ?? '',
    };

    if (paymentStatus === 'credit') {
      return {
        ...base,
        orderPaymentDetails: orderDetails,
        ...getCreditPaymentStepState(orderDetails),
      };
    }

    return base;
  };

  const DEFAULT_BULK_PAYMENT_STATUS_MODAL = {
    open: false,
    selectedPaymentStatus: '',
    orderPaymentDetails: null,
    error: '',
    isUpdating: false,
    step: 'status',
    paymentOption: '',
    advanceAmount: '',
    paymentMethod: '',
    paymentMethodConfig: null,
    paymentFields: {},
    paymentFiles: {},
    paymentError: '',
    summaryOrder: null,
    installmentAmount: '',
    creditNote: '',
  };

  const ProductList = ({ 
    data: products = [], 
    searchQuery = '', 
    statusFilter = 'all', 
    isLoading = false, 
    onRefresh,
    selectedProducts = [],
    onProductSelect,
    onSelectAll,
    isAllSelected = false,
    onExport,
    onZipDownload,
    showCancelRequestActions = false,
    onApproveCancelRequest,
    onRejectCancelRequest,
    updatingCancelRequestOrderId = null,
    showReturnRequestActions = false,
    onApproveReturnRequest,
    onRejectReturnRequest,
    updatingReturnRequestOrderId = null,
    pagination = null,
    currentPage = 1,
    pageSize = 10,
    onPageChange = null,
    onPageSizeChange = null,
    shouldEnableButton,
  }) => {
    const toast = useToast()

    const refreshOrdersAfterPaymentUpdate = async () => {
      await onRefresh?.();
    }

    const [statusUpdateModal, setStatusUpdateModal] = useState({ 
      open: false, 
      order: null,
      entityName: '',
      orderBy: '',
      driverName: '',
      vehicleNo: '',
      delivery_date: new Date().toISOString().slice(0,10),
      shippedDate: new Date().toISOString().slice(0, 10),
      generateInvoice: false,
      isCreatingChallan: false,
      challanError: '',
      dispatchPersonName: '',
      customDispatchPerson: '',
      useCustomDispatchPerson: false,
      start_km: '',
      end_km: '',
      cancellationReason: '',
      returnReason: '',
      /** When true (Placed tab only), unpaid orders may submit Cancelled without paying first. */
      allowUnpaidCancelFromPlaced: false,
    })
    const [entityOptions, setEntityOptions] = useState([])
    const [loadingEntities, setLoadingEntities] = useState(false)
    const [orderByChoices, setOrderByChoices] = useState([])
    const [paymentUpdateModal, setPaymentUpdateModal] = useState(() => ({ ...DEFAULT_PAYMENT_UPDATE_MODAL }))
    const [invoiceModal, setInvoiceModal] = useState({ open: false, invoice: null, order: null })
    const [deliveryChallanModal, setDeliveryChallanModal] = useState({ open: false, challan: null, order: null })
    const [isDownloading, setIsDownloading] = useState(false);
    const [bulkChallanModal, setBulkChallanModal] = useState({
      open: false,
      orderBy: '',
      entityName: '',
      driverName: '',
      vehicleNo: '',
      deliveryDate: new Date().toISOString().slice(0,10),
      generateInvoice: false,
      isCreating: false,
      error: '',
      selectedStatus: '',
      selectedStatusCategory: '',
      emails: [],
      emailInput: '',
      dispatchPersonName: '',
      customDispatchPerson: '',
      useCustomDispatchPerson: false,
      start_km: '',
      end_km: '',
      bulkPaymentStatus: '',
      cancellationReason: '',
      returnReason: '',
      /** Placed tab only: unpaid selected orders may bulk-update to Cancelled without paying first. */
      allowUnpaidBulkCancelFromPlaced: false,
    })
    const [bulkPaymentStatusModal, setBulkPaymentStatusModal] = useState(() => ({
      ...DEFAULT_BULK_PAYMENT_STATUS_MODAL,
    }))
    const [bulkEntityOptions, setBulkEntityOptions] = useState([])
    const [loadingBulkEntities, setLoadingBulkEntities] = useState(false)
    const [updatingStatus, setUpdatingStatus] = useState(false)
    const [updatingPayment, setUpdatingPayment] = useState(false)
    const [isUpdatingBulkPayment, setIsUpdatingBulkPayment] = useState(false)
    const [downloadingInvoice, setDownloadingInvoice] = useState(false)
    const [downloadingChallan, setDownloadingChallan] = useState(false)
    const [checkingPaymentOrderId, setCheckingPaymentOrderId] = useState(null)
    const navigate = useNavigate()
    const theme = useTheme()

    const [statusOptions, setStatusOptions] = useState([])
    const [paymentStatusChoices, setPaymentStatusChoices] = useState([])
    const [loadingPaymentStatusChoices, setLoadingPaymentStatusChoices] = useState(false)
    const [advancePaymentMethods, setAdvancePaymentMethods] = useState([])
    const [loadingAdvancePaymentMethods, setLoadingAdvancePaymentMethods] = useState(false)
    const paymentMethodsById = React.useMemo(
      () => buildPaymentMethodsById(advancePaymentMethods),
      [advancePaymentMethods]
    )
    const [dispatchPersons, setDispatchPersons] = useState([])
    const [actionsAnchorEl, setActionsAnchorEl] = useState(null)
    const isActionsMenuOpen = Boolean(actionsAnchorEl)
    const openActionsMenu = (e) => setActionsAnchorEl(e.currentTarget)
    const closeActionsMenu = () => setActionsAnchorEl(null)
    const [emailDialog, setEmailDialog] = useState({
      open: false,
      emails: '',
      sendInvoice: true,
      sendDelivery: true,
      isSending: false,
      error: '',
      success: ''
    })
    const [singleEmailDialog, setSingleEmailDialog] = useState({
      open: false,
      order: null,
      emailInput: '',
      emails: [],
      sendInvoice: true,
      sendDelivery: true,
      isSending: false,
      error: '',
      success: ''
    })

    const extractInvoice = (order) => {
      if (!order) return null;
      const inv = order.invoice;
      if (inv && typeof inv === 'object') {
        const path = inv.path || inv.url || inv.file || inv.link;
        if (path) return { ...inv, path };
      }
      const path = order.invoice_path || order.invoice_url || (typeof inv === 'string' ? inv : null);
      if (path) return { id: inv?.id || order.id, path };
      return null;
    };

    const extractDeliveryChallan = (order) => {
      if (!order) return null;
      const dc = order.delivery_challan || order.deliveryChallan || order.challan;
      if (dc && typeof dc === 'object') {
        const path = dc.path || dc.url || dc.file || dc.link;
        if (path) return { ...dc, path };
      }
      const path = order.delivery_challan_path || order.delivery_challan_url || order.challan_url || (typeof dc === 'string' ? dc : null);
      if (path) return { id: dc?.id || order.id, path };
      return null;
    };

    const getIconForCategory = (category) => {
      const key = (typeof category === 'string' ? category : (category?.name ?? '')).toLowerCase()
      switch (key) {
        case 'pending': return <Schedule />
        case 'confirmed': return <CheckCircle />
        case 'processing': return <Schedule />
        case 'shipped': return <LocalShippingOutlined />
        case 'delivered': return <CheckCircle />
        case 'cancelled': return <Cancel />
        case 'returned': return <Refresh />
        default: return null
      }
    }

    useEffect(() => {
      const fetchOrderStatuses = async () => {
        try {
          const res = await apiService.get(API_ENDPOINTS.GET_order_status_choices)
          const list = Array.isArray(res?.data) ? res.data : []
          const options = list
            .sort((a, b) => (a.sequence || 0) - (b.sequence || 0))
            .map(s => ({
              id: s.id,
              value: s.name || '',
              name: s.name || '',
              label: s.name || '',
              category: s.category || s.name || '',
              sequence: s.sequence || 0
            }))
          setStatusOptions(options)
        } catch (e) {
          console.error('Error fetching order status choices:', e)
        }
      }
      fetchOrderStatuses()
    }, [])

    useEffect(() => {
      const fetchDispatchPersons = async () => {
        try {
          const res = await apiService.get(API_ENDPOINTS.GET_dispatch_persons)
          const list = Array.isArray(res?.data?.DispatchPerson) ? res.data.DispatchPerson : []
          const filteredList = list.filter(person => !person.is_deleted)
          setDispatchPersons(filteredList)
        } catch (e) {
          console.error('Error fetching dispatch persons:', e)
        }
      }
      fetchDispatchPersons()
    }, [])

    useEffect(() => {
      const fetchOrderByChoices = async () => {
        try {
          const res = await apiService.get(API_ENDPOINTS.GET_order_by_choices)
          if (res?.success && Array.isArray(res?.data)) {
            setOrderByChoices(res.data)
          } else {
            console.error('Invalid response format from order by choices API')
            setOrderByChoices([])
          }
        } catch (e) {
          console.error('Error fetching order by choices:', e)
          setOrderByChoices([])
        }
      }
      fetchOrderByChoices()
    }, [])

    const applyOrderPaymentDetails = (orderId, orderDetails) => {
      const currentStatus = orderDetails?.current_payment_status
        ? String(orderDetails.current_payment_status).toLowerCase()
        : null
      const remaining = Number(orderDetails?.remainingAmount ?? 0)
      const paidStepPatch = getPaidPaymentStepState(orderDetails)

      setPaymentUpdateModal((prev) => {
        if (!prev.open || prev.order?.order_id !== orderId) return prev
        return {
          ...prev,
          orderPaymentDetails: orderDetails,
          ...(currentStatus
            ? {
                order: {
                  ...prev.order,
                  payment_status: {
                    ...(typeof prev.order?.payment_status === 'object'
                      ? prev.order.payment_status
                      : {}),
                    name: currentStatus,
                  },
                },
              }
            : {}),
          ...(currentStatus === 'credit' || prev.paymentOption === 'credit'
            ? { ...getCreditPaymentStepState(orderDetails), step: 'status' }
            : prev.paymentOption === 'remaining'
              ? { ...paidStepPatch, step: 'status' }
              : {}),
        }
      })

      setBulkPaymentStatusModal((prev) => {
        if (!prev.open || prev.summaryOrder?.order_id !== orderId) return prev
        return {
          ...prev,
          orderPaymentDetails: orderDetails,
          ...(prev.step === 'payment_details'
            ? {
                ...paidStepPatch,
                selectedPaymentStatus: prev.selectedPaymentStatus || 'paid',
              }
            : {}),
        }
      })
    }

    const fetchPaymentStatusChoicesForOrder = async (orderId) => {
      if (!orderId) {
        setPaymentStatusChoices([])
        return
      }
      setLoadingPaymentStatusChoices(true)
      try {
        const res = await apiService.get(
          `${API_ENDPOINTS.GET_payment_status_choices}?order_id=${encodeURIComponent(orderId)}`
        )
        if (res?.success && Array.isArray(res?.data)) {
          setPaymentStatusChoices(
            [...res.data].sort((a, b) => (a.sequence || 0) - (b.sequence || 0))
          )
          applyOrderPaymentDetails(orderId, res?.order_details || null)
        } else {
          console.error('Invalid response format from payment status choices API')
          setPaymentStatusChoices([])
          applyOrderPaymentDetails(orderId, null)
        }
      } catch (e) {
        console.error('Error fetching payment status choices:', e)
        setPaymentStatusChoices([])
        applyOrderPaymentDetails(orderId, null)
      } finally {
        setLoadingPaymentStatusChoices(false)
      }
    }

    const isPaymentUpdateDisabled = (order) => {
      const paymentStatus = getOrderPaymentStatusValue(order);
      if (paymentStatus === 'paid') return true;
      if (paymentStatus === 'credit') return false;
      return isPaymentUpdateDisabledForOrder(order, statusFilter);
    };

    const getPaymentUpdateTooltip = (order) => {
      if (!shouldEnableButton?.('update_payment')) {
        return "You don't have permission to update payment";
      }
      const paymentStatus = getOrderPaymentStatusValue(order);
      if (paymentStatus === 'paid') {
        return 'Payment status cannot be updated when the order is already paid';
      }
      if (paymentStatus === 'credit') {
        return 'Update Payment';
      }
      const reason = getPaymentUpdateDisabledReason(order, statusFilter);
      if (reason) return reason;
      return 'Update Payment';
    };

    const isBulkPaymentUpdateDisabled = () => {
      if (PAYMENT_UPDATE_LOCKED_ORDER_STATUSES.has(normalizeStatusKey(statusFilter))) {
        return true;
      }
      const selected = filteredData.filter((order) => selectedProducts.includes(order.id));
      if (selected.length === 0) return true;
      return selected.every((order) => isPaymentUpdateDisabled(order));
    };

    const loadAdvancePaymentMethods = async () => {
      setLoadingAdvancePaymentMethods(true);
      try {
        const methods = await fetchAdvancePaymentMethods();
        setAdvancePaymentMethods(methods);
      } catch (error) {
        console.error('Error fetching advance payment methods:', error);
        setAdvancePaymentMethods([]);
      } finally {
        setLoadingAdvancePaymentMethods(false);
      }
    };

    const buildPaymentMethodChangePayload = (methodId, orderPaymentDetails) => {
      const config = paymentMethodsById[methodId] || null;
      const remaining = Number(orderPaymentDetails?.remainingAmount ?? 0);
      return {
        paymentMethod: methodId,
        paymentMethodConfig: config,
        paymentFields:
          config?.type === 'cheque' && remaining > 0
            ? { cheque_amount: String(remaining) }
            : {},
        paymentFiles: {},
        paymentError: '',
      };
    };

    const openPaymentModalForOrder = (order) => {
      if (!order || isPaymentUpdateDisabled(order)) return;
      setPaymentUpdateModal(openPaymentUpdateModalState(order));
      fetchPaymentStatusChoicesForOrder(order.order_id);
      loadAdvancePaymentMethods();
    };

    const getPaymentChoiceValue = (choice) =>
      String(choice.value || choice.key || choice.name || '').toLowerCase()

    const getPaymentChoiceLabel = (choice) =>
      choice.label || choice.name || choice.value || choice.key || ''

    const renderPaymentStatusMenuItems = ({ hidePaid = false } = {}) => {
      const shouldHidePaid = (value) =>
        hidePaid && String(value || '').toLowerCase() === 'paid'

      if (loadingPaymentStatusChoices) {
        return (
          <MenuItem disabled>
            <CircularProgress size={18} sx={{ mr: 1 }} />
            Loading payment statuses...
          </MenuItem>
        )
      }
      if (paymentStatusChoices.length > 0) {
        return [...paymentStatusChoices]
          .sort((a, b) => (a.sequence || 0) - (b.sequence || 0))
          .filter((choice) => !shouldHidePaid(getPaymentChoiceValue(choice)))
          .map((choice) => {
          const value = getPaymentChoiceValue(choice)
          const label = getPaymentChoiceLabel(choice)
          return (
            <MenuItem key={value || label} value={value}>
              <PaymentStatusChip
                label={label}
                status={value}
                size="small"
                sx={{ mr: 1 }}
              />
              {label}
            </MenuItem>
          )
        })
      }
      return paymentStatusOptions
        .filter((option) => !shouldHidePaid(option.value))
        .map((option) => (
        <MenuItem key={option.value} value={option.value}>
          <PaymentStatusChip
            label={option.label}
            status={option.value}
            size="small"
            sx={{ mr: 1 }}
          />
          {option.label}
        </MenuItem>
      ))
    }

    const paymentStatusOptions = [
      { value: 'pending', label: 'Pending', icon: <Schedule /> },
      { value: 'outstanding', label: 'Outstanding', icon: <Schedule /> },
      { value: 'paid', label: 'Paid', icon: <CheckCircle /> },
      { value: 'failed', label: 'Failed', icon: <Cancel /> },
      { value: 'refunded', label: 'Refunded', icon: <Refresh /> },
    ]

    const getAvailableCounts = () => {
      const selected = filteredData.filter(order => selectedProducts.includes(order.id))
      const invoices = selected.filter(order => !!extractInvoice(order)?.path).length
      const challans = selected.filter(order => !!extractDeliveryChallan(order)?.path).length
      return { invoices, challans }
    }

    const shouldDisableDocumentsAndEmail = () => {
      const selected = filteredData.filter(order => selectedProducts.includes(order.id))
      const restrictedStatuses = ['Placed', 'Confirmed', 'Processing']
      return selected.some(order => {
        const statusName = order.status?.name || order.status_name || ''
        return restrictedStatuses.includes(statusName)
      })
    }

    const handleOpenEmailDialog = () => {
      setEmailDialog(prev => ({ ...prev, open: true, error: '', success: '' }))
      closeActionsMenu()
    }

    const handleCloseEmailDialog = () => {
      setEmailDialog({ open: false, emails: '', sendInvoice: true, sendDelivery: true, isSending: false, error: '', success: '' })
    }

    const handleEmailDialogChange = (field, value) => {
      setEmailDialog(prev => ({ ...prev, [field]: value, error: '', success: '' }))
    }

    const handleEmailSendSubmit = async () => {
      const { emails, sendInvoice, sendDelivery } = emailDialog
      const userId = localStorage.getItem('user_id')
      const selectedOrderIds = filteredData
        .filter(order => selectedProducts.includes(order.id))
        .map(order => order.order_id)
        .filter(Boolean)

      const trimmed = (emails || '').split(/[,\s]+/).map(e => e.trim()).filter(Boolean)
      if (trimmed.length === 0) {
        setEmailDialog(prev => ({ ...prev, error: 'Please enter at least one email address' }))
        return
      }
      if (!sendInvoice && !sendDelivery) {
        setEmailDialog(prev => ({ ...prev, error: 'Please select at least one document type to send' }))
        return
      }
      const { invoices, challans } = getAvailableCounts()
      if ((sendInvoice && invoices === 0) && (sendDelivery && challans === 0)) {
        setEmailDialog(prev => ({ ...prev, error: 'No available documents to send for selected orders' }))
        return
      }

      setEmailDialog(prev => ({ ...prev, isSending: true, error: '', success: '' }))
      try {
        const payload = {
          emails: trimmed.join(','),
          order_id: selectedOrderIds.join(','),
          user_id: userId,
          invoice: !!sendInvoice,
          delivery: !!sendDelivery
        }

        const res = await apiService.post(
          API_ENDPOINTS.POST_send_challan_on_email,
          JSON.stringify(payload),
          'application/json'
        )

        if (res?.success) {
          setEmailDialog(prev => ({ ...prev, success: 'Email sent successfully', isSending: false }))
          setTimeout(() => handleCloseEmailDialog(), 1200)
        } else {
          throw new Error(res?.message || 'Failed to send email')
        }
      } catch (err) {
        setEmailDialog(prev => ({ ...prev, error: err?.message || 'Failed to send email', isSending: false }))
      }
    }

    const openSingleOrderEmail = (order) => {
      const hasInvoice = !!extractInvoice(order)?.path
      const hasChallan = !!extractDeliveryChallan(order)?.path
      setSingleEmailDialog({
        open: true,
        order: order,
        emailInput: '',
        emails: [],
        sendInvoice: hasInvoice,
        sendDelivery: hasChallan,
        isSending: false,
        error: '',
        success: ''
      })
    }

    const closeSingleOrderEmail = () => {
      setSingleEmailDialog({
        open: false,
        order: null,
        emailInput: '',
        emails: [],
        sendInvoice: true,
        sendDelivery: true,
        isSending: false,
        error: '',
        success: ''
      })
    }

    const addEmailToSingle = () => {
      const input = (singleEmailDialog.emailInput || '').trim()
      if (!input) return
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(input)) {
        setSingleEmailDialog(prev => ({ ...prev, error: 'Enter a valid email address' }))
        return
      }
      if (singleEmailDialog.emails.includes(input)) {
        setSingleEmailDialog(prev => ({ ...prev, error: 'Email already added' }))
        return
      }
      setSingleEmailDialog(prev => ({ ...prev, emails: [...prev.emails, input], emailInput: '', error: '' }))
    }

    const removeEmailFromSingle = (email) => {
      setSingleEmailDialog(prev => ({ ...prev, emails: prev.emails.filter(e => e !== email) }))
    }

    const submitSingleOrderEmail = async () => {
      const { order, emails, sendInvoice, sendDelivery } = singleEmailDialog
      if (!order) return
      if (emails.length === 0) {
        setSingleEmailDialog(prev => ({ ...prev, error: 'Please add at least one email' }))
        return
      }
      if (!sendInvoice && !sendDelivery) {
        setSingleEmailDialog(prev => ({ ...prev, error: 'Please select at least one document type' }))
        return
      }
      const hasInvoice = !!extractInvoice(order)?.path
      const hasChallan = !!extractDeliveryChallan(order)?.path
      if ((sendInvoice && !hasInvoice) && (sendDelivery && !hasChallan)) {
        setSingleEmailDialog(prev => ({ ...prev, error: 'Selected documents are not available for this order' }))
        return
      }

      setSingleEmailDialog(prev => ({ ...prev, isSending: true, error: '', success: '' }))
      try {
        const userId = localStorage.getItem('user_id')
        const payload = {
          emails: emails.join(','),
          order_id: order.order_id,
          user_id: userId,
          invoice: !!sendInvoice,
          delivery: !!sendDelivery
        }
        const res = await apiService.post(
          API_ENDPOINTS.POST_send_challan_on_email,
          JSON.stringify(payload),
          'application/json'
        )
        if (res?.success) {
          setSingleEmailDialog(prev => ({ ...prev, success: 'Email sent successfully', isSending: false }))
          setTimeout(() => closeSingleOrderEmail(), 1200)
        } else {
          throw new Error(res?.message || 'Failed to send email')
        }
      } catch (err) {
        setSingleEmailDialog(prev => ({ ...prev, error: err?.message || 'Failed to send email', isSending: false }))
      }
    }

    const handleViewInvoice = (order) => {
      const inv = extractInvoice(order)
      if (inv?.path) {
        window.open(inv.path, '_blank')
      }
    }

    const handleDownloadInvoice = async (invoice, orderName) => {
      if (!invoice || !invoice.path) {
        alert('Invoice not available for download')
        return
      }

      setDownloadingInvoice(true)
      try {
        const link = document.createElement('a')
        link.href = invoice.path
        link.download = `Invoice_${invoice.id}_${orderName.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`
        link.target = '_blank'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      } catch (error) {
        console.error('Error downloading invoice:', error)
        alert('Error downloading invoice. Please try again.')
      } finally {
        setDownloadingInvoice(false)
      }
    }

    const handleCloseInvoiceModal = () => {
      setInvoiceModal({ open: false, invoice: null, order: null })
    }

    const handleViewDeliveryChallan = (order) => {
      const dc = extractDeliveryChallan(order)
      if (dc?.path) {
        window.open(dc.path, '_blank')
      }
    }

    const handleCheckPaymentStatus = async (order) => {
      const orderId = order?.order_id ?? order?.id
      if (!orderId) return
      const userId = localStorage.getItem('user_id') || ''
      setCheckingPaymentOrderId(orderId)
      try {
        const url = `${API_ENDPOINTS.Refund_button_click}?user_id=${encodeURIComponent(userId)}&order_id=${encodeURIComponent(orderId)}`
        const response = await apiService.get(url)
        if (response?.success && onRefresh) {
          await onRefresh()
        } else if (!response?.success) {
          alert(response?.message || 'Check payment status failed.')
        }
      } catch (error) {
        console.error('Check payment status error:', error)
        alert(error?.message || 'Check payment status failed.')
      } finally {
        setCheckingPaymentOrderId(null)
      }
    }

    const handleDownloadDeliveryChallan = async (challan, orderName) => {
      if (!challan || !challan.path) {
        alert('Delivery challan not available for download')
        return
      }

      setDownloadingChallan(true)
      try {
        const link = document.createElement('a')
        link.href = challan.path
        link.download = `Delivery_Challan_${challan.id}_${orderName.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`
        link.target = '_blank'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      } catch (error) {
        console.error('Error downloading delivery challan:', error)
        alert('Error downloading delivery challan. Please try again.')
      } finally {
        setDownloadingChallan(false)
      }
    }

    const handleCloseDeliveryChallanModal = () => {
      setDeliveryChallanModal({ open: false, challan: null, order: null })
    }

    const handleBulkDeliveryChallan = () => {
      const selectedOrders = filteredData.filter(order => selectedProducts.includes(order.id));
      const statusOrder = getStatusOrder();
      
      let minStatusOrder = 999;
      let minStatusCategory = '';
      
      selectedOrders.forEach(order => {
        const orderStatusCategory = (order.status?.category || '').toLowerCase();
        const orderStatusName = order.status?.name || '';
        
        let orderCategory = orderStatusCategory;
        if (!orderCategory && orderStatusName) {
          const orderStatusOption = statusOptions.find(opt => opt.name === orderStatusName);
          orderCategory = orderStatusOption?.category?.toLowerCase() || '';
        }
        
        const orderSequence = statusOrder[orderCategory] || 0;
        if (orderSequence > 0 && orderSequence < minStatusOrder) {
          minStatusOrder = orderSequence;
          minStatusCategory = orderCategory;
        }
      });
      
      let defaultStatus = null;
      
      if (statusFilter === 'Dispatch') {
        defaultStatus = statusOptions.find(status => status.name === 'Delivered') || 
                        statusOptions.find(status => status.name === 'Returned') ||
                        statusOptions.find(status => status.name === 'Cancelled') ||
                        (statusOptions.length > 0 ? statusOptions[0] : null);
      } else if (statusFilter === 'Delivered') {
        // Keep bulk flow aligned with single update: Delivered -> Returned only.
        defaultStatus = statusOptions.find(status => status.name === 'Returned') || null;
      } else if (minStatusOrder !== 999 && minStatusOrder > 0) {
        const nextSequence = minStatusOrder + 1;
        defaultStatus = statusOptions.find(status => {
          const statusCategory = (status.category || '').toLowerCase();
          return statusOrder[statusCategory] === nextSequence;
        });
        
        if (!defaultStatus) {
          defaultStatus = statusOptions.find(status => {
            const statusCategory = (status.category || '').toLowerCase();
            const statusSeq = statusOrder[statusCategory] || 0;
            return statusSeq > minStatusOrder && statusSeq <= minStatusOrder + 1;
          });
        }
        
        if (!defaultStatus) {
          defaultStatus = statusOptions.length > 0 ? statusOptions[0] : null;
        }
      } else {
        defaultStatus = statusOptions.length > 0 ? statusOptions[0] : null;
      }

      const isPlacedTab = (statusFilter || '').toLowerCase() === 'placed';
      const cancelledOption = statusOptions.find(
        (s) =>
          (s.name || '').toLowerCase() === 'cancelled' ||
          (s.category || '').toLowerCase() === 'cancelled'
      );
      let allowUnpaidBulkCancelFromPlaced = false;
      let bulkCancellationReason = '';
      if (isPlacedTab && cancelledOption) {
        defaultStatus = cancelledOption;
        allowUnpaidBulkCancelFromPlaced = true;
        bulkCancellationReason = '';
      }

      const selectedOrderWithStartKm = selectedOrders.find((order) => {
        const startKm = order.delivery_challan?.start_km;
        return startKm !== null && startKm !== undefined && String(startKm).trim() !== '';
      });
      const selectedDeliveryChallan = selectedOrderWithStartKm?.delivery_challan;
      
      setBulkChallanModal({
        open: true,
        driverName: selectedDeliveryChallan?.driver_name || '',
        vehicleNo:
          selectedDeliveryChallan?.vehicle_number ||
          selectedDeliveryChallan?.vehicle_no ||
          selectedDeliveryChallan?.vehicle_name ||
          '',
        deliveryDate: new Date().toISOString().slice(0,10),
        generateInvoice: false,
        isCreating: false,
        error: '',
        emails: [],
        emailInput: '',
        selectedStatus: defaultStatus?.name || '',
        selectedStatusCategory: defaultStatus?.category || '',
        dispatchPersonName: '',
        customDispatchPerson: '',
        useCustomDispatchPerson: false,
        start_km:
          selectedDeliveryChallan?.start_km !== null && selectedDeliveryChallan?.start_km !== undefined
            ? String(selectedDeliveryChallan.start_km)
            : '',
        end_km: '',
        cancellationReason: bulkCancellationReason,
        returnReason: '',
        allowUnpaidBulkCancelFromPlaced,
      })
    }

    const createBulkDeliveryChallan = async (orderIds, driverName, vehicleNo) => {
      try {
        const formData = new FormData()
        formData.append('order_id', orderIds.join(','))
        formData.append('driver_name', driverName)
        formData.append('vehicle_no', vehicleNo)
        if (bulkChallanModal.deliveryDate) {
          formData.append('delivery_date', bulkChallanModal.deliveryDate)
        }
        formData.append('invoice', bulkChallanModal.generateInvoice ? 'true' : 'false')
        const dispatchPersonName = bulkChallanModal.useCustomDispatchPerson 
          ? bulkChallanModal.customDispatchPerson 
          : bulkChallanModal.dispatchPersonName;
        if (dispatchPersonName) {
          formData.append('dispatch_person_name', dispatchPersonName);
        }

        const response = await apiService.post(
          API_ENDPOINTS.POST_create_delivery_challan,
          formData,
          "multipart/form-data"
        )

        return response
      } catch (error) {
        console.error('Error creating bulk delivery challan:', error)
        throw error
      }
    }

    const handleCloseBulkChallanModal = () => {
      setBulkChallanModal({
        open: false,
        orderBy: '',
        entityName: '',
        driverName: '',
        vehicleNo: '',
        deliveryDate: new Date().toISOString().slice(0,10),
        generateInvoice: false,
        isCreating: false,
        error: '',
        emails: [],
        emailInput: '',
        selectedStatus: '',
        selectedStatusCategory: '',
        dispatchPersonName: '',
        customDispatchPerson: '',
        useCustomDispatchPerson: false,
        start_km: '',
        end_km: '',
        cancellationReason: '',
        returnReason: '',
        bulkPaymentStatus: '',
        allowUnpaidBulkCancelFromPlaced: false,
      })
      setBulkEntityOptions([])
    }

    const handleBulkChallanSubmit = async () => {
      const { driverName, vehicleNo, selectedStatus } = bulkChallanModal

      if (!selectedStatus.trim()) {
        setBulkChallanModal(prev => ({
          ...prev,
          error: 'Please select a status to update orders to'
        }))
        return
      }

      if (selectedProducts.length === 0) {
        setBulkChallanModal(prev => ({
          ...prev,
          error: 'No products selected'
        }))
        return
      }

      if (statusFilter === 'Dispatch' && selectedStatus === 'Dispatch') {
        setBulkChallanModal(prev => ({
          ...prev,
          error: 'Cannot change status to Dispatch when viewing Dispatch tab. You can only change to Delivered, Returned, or Cancelled.'
        }))
        return
      }

      const selectedOrders = filteredData.filter(order => selectedProducts.includes(order.id));
      const statusOrder = getStatusOrder();
      const selectedOption = statusOptions.find(opt => opt.name === selectedStatus);
      const newStatusCategory = (selectedOption?.name || '').toLowerCase();

      const hasUnpaidOrders = selectedOrders.some(order => {
        const paymentStatus = (order.payment_status?.name || order.payment_status || '').toLowerCase();
        return !isPaymentSatisfiedForOrderAdvance(paymentStatus);
      });

      const allowUnpaidPlacedBulkCancel =
        bulkChallanModal.allowUnpaidBulkCancelFromPlaced &&
        (selectedStatus || '').toLowerCase() === 'cancelled';

      if (hasUnpaidOrders && selectedStatus !== 'Orders' && !allowUnpaidPlacedBulkCancel) {
        setBulkChallanModal(prev => ({
          ...prev,
          error: 'Some selected orders must be paid before they can be moved to any status other than "Orders". Please update the payment status first.'
        }));
        return;
      }

      const isReturnedSelected = selectedStatus.toLowerCase() === 'returned';
      const isCancelledSelected = selectedStatus.toLowerCase() === 'cancelled';
      const hasDeliveredOrders = selectedOrders.some(order => {
        const statusName = (order.status?.name || order.status_name || '').toLowerCase();
        const statusCategory = (order.status?.category || '').toLowerCase();
        return statusName === 'delivered' || statusCategory === 'delivered';
      });
      const hasCancelledOrders = selectedOrders.some(order => {
        const statusName = (order.status?.name || order.status_name || '').toLowerCase();
        const statusCategory = (order.status?.category || '').toLowerCase();
        return statusName === 'cancelled' || statusCategory === 'cancelled';
      });

      if (hasCancelledOrders) {
        setBulkChallanModal(prev => ({
          ...prev,
          error: 'Cancelled is a final status. No further status changes are allowed for cancelled orders.'
        }));
        return;
      }

      if (hasDeliveredOrders && !isReturnedSelected) {
        setBulkChallanModal(prev => ({
          ...prev,
          error: 'When an order is Delivered, only Returned status is allowed.'
        }));
        return;
      }

      if (isReturnedSelected && !hasDeliveredOrders) {
        setBulkChallanModal(prev => ({
          ...prev,
          error: 'Returned status is only available when orders are Delivered.'
        }));
        return;
      }

      const alwaysAvailableCategories = ['cancelled', 'returned'];

      if (!alwaysAvailableCategories.includes(newStatusCategory)) {
        let minStatusOrder = 999;
        selectedOrders.forEach(order => {
          const orderStatusCategory = (order.status?.category || '').toLowerCase();
          const orderStatusName = order.status?.name || '';

          let orderCategory = orderStatusCategory;
          if (!orderCategory && orderStatusName) {
            const orderStatusOption = statusOptions.find(opt => opt.name === orderStatusName);
            orderCategory = orderStatusOption?.category?.toLowerCase() || '';
          }

          const orderSequence = statusOrder[orderCategory] || 0;
          if (orderSequence > 0 && orderSequence < minStatusOrder) {
            minStatusOrder = orderSequence;
          }
        });

        if (minStatusOrder === 999) {
          minStatusOrder = 0;
        }
      }

      for (const order of selectedOrders) {
        const originalStatusCategory = (order.status?.category || '').toLowerCase();
        const originalStatusName = (order.status?.name || order.status_name || '').toLowerCase();
        const disabledForOrder = getStatusesToDisable(originalStatusCategory, originalStatusName) || [];
        const newCat = (selectedOption?.category || '').toLowerCase();

        if (!allowUnpaidPlacedBulkCancel && disabledForOrder.includes(newCat)) {
          setBulkChallanModal(prev => ({
            ...prev,
            error: 'Selected status is not allowed for one or more of the selected orders due to sequential/final status rules.'
          }));
          return;
        }

        const isDispatch = newCat === 'shipped' || newCat === 'dispatch' || selectedStatus === 'Dispatch';
        if (isDispatch) {
          const processingOrder = statusOrder['processing'] || 0;
          const currentStatusName = (order.status?.name || order.status_name || '').toLowerCase();
          const currentStatusCat = (order.status?.category || '').toLowerCase();
          const currentOrder = statusOrder[currentStatusCat] || 0;

          const isActuallyProcessing = (currentStatusName === 'processing' || currentStatusCat === 'processing') && currentOrder === processingOrder && processingOrder > 0;

          if (!isActuallyProcessing) {
            setBulkChallanModal(prev => ({
              ...prev,
              error: `Dispatch status is only available when orders are in Processing status. One or more selected orders are not in Processing.`
            }));
            return;
          }
        }
      }

      if (selectedStatus === 'Dispatch') {
        const isTruckOrderBy = String(bulkChallanModal.orderBy || '').trim().toLowerCase() === 'truck';
        if (!driverName.trim() || (isTruckOrderBy && !vehicleNo.trim())) {
          setBulkChallanModal(prev => ({
            ...prev,
            error: 'Driver name and vehicle number are required for Dispatch status'
          }))
          return
        }

        if (isTruckOrderBy && isVehicleReferenceNoInvalid(vehicleNo)) {
          setBulkChallanModal(prev => ({
            ...prev,
            error: 'Vehicle No / Reference No must be in format MH 12 AB 1234'
          }))
          return
        }
      }

      setBulkChallanModal(prev => ({
        ...prev,
        isCreating: true,
        error: ''
      }))

      try {
        const selectedOrders = filteredData.filter(product => selectedProducts.includes(product.id));
        const selectedOrderIds = selectedOrders
          .map(product => product.order_id)
          .filter(Boolean)
        const isDispatchTabForBulk = ['dispatch', 'shipped'].includes((statusFilter || '').toLowerCase());
        const anySelectedOrderHasStartKm = selectedOrders.some((order) => {
          const startKm = order.delivery_challan?.start_km;
          const hasStartKm = startKm !== null && startKm !== undefined && String(startKm).trim() !== '';
          if (isDispatchTabForBulk && !isOrderByTruck(order)) return false;
          return hasStartKm;
        });

        if (selectedOrderIds.length === 0) {
          throw new Error('No valid orders found for selected products')
        }

        let response = { success: true };
        
        if (selectedStatus === 'Dispatch') {
          console.log('Creating bulk delivery challan for orders:', selectedOrderIds)
          response = await createBulkDeliveryChallan(selectedOrderIds, driverName, vehicleNo)
        }

        if (response?.success) {
          console.log('Updating order statuses to:', selectedStatus);
          console.log('Order IDs to update:', selectedOrderIds);
          
          try {
            if (selectedStatus === 'Delivered') {
              const truckOrderWithStartKm = isDispatchTabForBulk
                ? selectedOrders.find((order) => {
                    const startKm = order.delivery_challan?.start_km;
                    return (
                      isOrderByTruck(order) &&
                      startKm !== null &&
                      startKm !== undefined &&
                      String(startKm).trim() !== ''
                    );
                  })
                : null;
              const startKm = parseFloat(
                bulkChallanModal.start_km || truckOrderWithStartKm?.delivery_challan?.start_km
              );
              const endKm = parseFloat(bulkChallanModal.end_km);
              const hasStartKm = !isNaN(startKm);
              
              if (hasStartKm && !String(bulkChallanModal.end_km || '').trim()) {
                setBulkChallanModal(prev => ({
                  ...prev,
                  error: 'End KM is required',
                  isCreating: false
                }));
                return;
              }

              if (hasStartKm && !isNaN(endKm) && endKm <= startKm) {
                alert('End KM must be greater than Start KM. Please enter a valid End KM value.');
                setBulkChallanModal(prev => ({
                  ...prev,
                  error: 'End KM must be greater than Start KM',
                  isCreating: false
                }));
                return;
              }
            }

            const userId = localStorage.getItem("user_id");
            const dispatchPersonName = bulkChallanModal.useCustomDispatchPerson 
              ? bulkChallanModal.customDispatchPerson 
              : bulkChallanModal.dispatchPersonName;
            const payload = {
              user_id: userId,
              status: selectedStatus,
              order_id: selectedOrderIds.join(','),
              emails: bulkChallanModal.emails?.length > 0 ? bulkChallanModal.emails.join(',') : '',
              generateInvoice: bulkChallanModal.generateInvoice,
              driverName: selectedStatus === 'Dispatch' ? bulkChallanModal.driverName : '',
              vehicleNo: selectedStatus === 'Dispatch' ? bulkChallanModal.vehicleNo : '',
              delivery_date: bulkChallanModal.deliveryDate || new Date().toISOString().slice(0,10),
              dispatch_person_name: dispatchPersonName,
              start_km: bulkChallanModal.start_km || '',
              ...(selectedStatus === 'Dispatch' && bulkChallanModal.orderBy && { order_by: bulkChallanModal.orderBy }),
              ...(selectedStatus === 'Dispatch' && bulkChallanModal.entityName && { order_by_entity: bulkChallanModal.entityName }),
              ...(selectedStatus === 'Delivered' && anySelectedOrderHasStartKm && { end_km: bulkChallanModal.end_km || '' }),
              ...(selectedStatus === 'Cancelled' && { reason: bulkChallanModal.cancellationReason || '' }),
              ...(selectedStatus === 'Returned' && { reason: bulkChallanModal.returnReason || '' })
            };

            console.log('Bulk status update payload:', payload);

            const statusResponse = await apiService.put(
              API_ENDPOINTS.UPDATE_product_status,
              payload,
              "application/json"
            );
            
            console.log('Bulk status update response:', statusResponse);

            if (statusResponse?.success) {
              setBulkChallanModal({
                open: false,
                orderBy: '',
                entityName: '',
                driverName: '',
                vehicleNo: '',
                deliveryDate: new Date().toISOString().slice(0,10),
                generateInvoice: false,
                isCreating: false,
                error: '',
                emails: [],
                emailInput: '',
                selectedStatus: '',
                selectedStatusCategory: '',
                dispatchPersonName: '',
                customDispatchPerson: '',
                useCustomDispatchPerson: false,
                start_km: '',
                end_km: '',
                cancellationReason: '',
                returnReason: '',
                bulkPaymentStatus: '',
                allowUnpaidBulkCancelFromPlaced: false,
              })
              
              if (onSelectAll) {
                onSelectAll(false)
              }
              setTimeout(() => {
                onRefresh?.()
              }, 100)
            } else {
              throw new Error(statusResponse?.message || 'Failed to update order statuses')
            }
          } catch (statusError) {
            console.error('Error updating order statuses:', statusError);
            throw new Error(statusError?.message || 'Failed to update order statuses');
          }
        } else {
          throw new Error(response?.message || 'Failed to create bulk delivery challan')
        }
      } catch (error) {
        console.error('Error creating bulk delivery challan:', error)
        setBulkChallanModal(prev => ({
          ...prev,
          error: error.message || 'Failed to create bulk delivery challan',
          isCreating: false
        }))
      }
    }

    const fetchBulkEntitiesByOrderBy = async (orderBy) => {
      if (!orderBy) {
        setBulkEntityOptions([]);
        return;
      }
      
      setLoadingBulkEntities(true);
      try {
        const entityMapping = {
          'indian post': ['Mumbai Branch', 'Delhi Branch', 'Bangalore Branch', 'Chennai Branch'],
          'courier services': ['BlueDart', 'FedEx', 'DTDC', 'First Flight', 'Professional Courier'],
          'name': ['Mumbai Branch', 'Delhi Branch', 'Bangalore Branch', 'Chennai Branch']
        };
        
        const entities = entityMapping[orderBy.toLowerCase()] || [];
        setBulkEntityOptions(entities);
      } catch (error) {
        console.error('Error fetching bulk entities:', error);
        setBulkEntityOptions([]);
      } finally {
        setLoadingBulkEntities(false);
      }
    };

    const handleBulkChallanInputChange = (field, value) => {
      setBulkChallanModal(prev => ({
        ...prev,
        [field]: value,
        error: ''
      }))
      
      if (field === 'orderBy') {
        setBulkChallanModal(prev => ({
          ...prev,
          orderBy: value,
          entityName: '',
          error: ''
        }));
        fetchBulkEntitiesByOrderBy(value);
      }
    }

    const isBulkSubmitDisabled = () => {
      if (bulkChallanModal.isCreating) return true;
      const selectedStatus = bulkChallanModal.selectedStatus;
      if (!selectedStatus || !selectedStatus.trim()) return true;

      const selectedOrders = filteredData.filter(order => selectedProducts.includes(order.id));
      const hasUnpaidOrders = selectedOrders.some(order => {
        const paymentStatus = (order.payment_status?.name || order.payment_status || '').toLowerCase();
        return !isPaymentSatisfiedForOrderAdvance(paymentStatus);
      });
      const allowUnpaidPlacedBulkCancel =
        bulkChallanModal.allowUnpaidBulkCancelFromPlaced &&
        (selectedStatus || '').toLowerCase() === 'cancelled';

      if (hasUnpaidOrders && selectedStatus !== 'Orders' && !allowUnpaidPlacedBulkCancel) return true;

      const selCat = (bulkChallanModal.selectedStatusCategory || '').toLowerCase();
      const isDispatchTabForBulk = ['dispatch', 'shipped'].includes((statusFilter || '').toLowerCase());
      const anySelectedOrderHasStartKm = selectedOrders.some(order => {
        const startKm = order.delivery_challan?.start_km;
        const hasStartKm = startKm !== null && startKm !== undefined && String(startKm).trim() !== '';
        if (isDispatchTabForBulk && !isOrderByTruck(order)) return false;
        return hasStartKm;
      });

      if (selCat === 'shipped' || selectedStatus === 'Dispatch') {
        const dispatchPersonName = bulkChallanModal.useCustomDispatchPerson ? bulkChallanModal.customDispatchPerson : bulkChallanModal.dispatchPersonName;
        const isTruckOrderBy = String(bulkChallanModal.orderBy || '').trim().toLowerCase() === 'truck';
        const isProcessingTab = (statusFilter || '').toLowerCase() === 'processing';
        if (
          !bulkChallanModal.orderBy?.trim() ||
          !bulkChallanModal.entityName?.trim() ||
          !bulkChallanModal.driverName?.trim() ||
          (isTruckOrderBy && !bulkChallanModal.vehicleNo?.trim()) ||
          !bulkChallanModal.deliveryDate?.trim() ||
          !dispatchPersonName?.trim() ||
          (isTruckOrderBy && isVehicleReferenceNoInvalid(bulkChallanModal.vehicleNo))
        ) return true;
        if (isTruckOrderBy && isProcessingTab && !String(bulkChallanModal.start_km || '').trim()) return true;
        if (
          isTruckOrderBy &&
          String(bulkChallanModal.start_km || '').trim() !== '' &&
          Number(bulkChallanModal.start_km) < 1
        ) return true;
      }

      if (selectedStatus === 'Delivered' && anySelectedOrderHasStartKm) {
        const truckOrderWithStartKm = isDispatchTabForBulk
          ? selectedOrders.find((order) => {
              const startKm = order.delivery_challan?.start_km;
              return (
                isOrderByTruck(order) &&
                startKm !== null &&
                startKm !== undefined &&
                String(startKm).trim() !== ''
              );
            })
          : null;
        const startKm = parseFloat(
          bulkChallanModal.start_km || truckOrderWithStartKm?.delivery_challan?.start_km
        );
        const endKm = parseFloat(bulkChallanModal.end_km);
        if (!String(bulkChallanModal.end_km || '').trim()) return true;
        if (!isNaN(startKm) && !isNaN(endKm) && endKm <= startKm) return true;
      }

      if (selectedStatus === 'Cancelled' && !bulkChallanModal.cancellationReason?.trim()) return true;
      if (selectedStatus === 'Returned' && !bulkChallanModal.returnReason?.trim()) return true;

      return false;
    }

    const addEmailToBulkModal = () => {
      const email = bulkChallanModal.emailInput?.trim()
      if (email && !bulkChallanModal.emails?.includes(email)) {
        setBulkChallanModal(prev => ({
          ...prev,
          emails: [...(prev.emails || []), email],
          emailInput: ''
        }))
      }
    }

    const removeEmailFromBulkModal = (emailToRemove) => {
      setBulkChallanModal(prev => ({
        ...prev,
        emails: prev.emails?.filter(email => email !== emailToRemove) || []
      }))
    }

    const getEligibleProductsCount = () => {
      return filteredData
        .filter(order => selectedProducts.includes(order.id))
        .filter(order => !extractDeliveryChallan(order)?.path)
        .length
    }

    const getSelectedProductsCount = () => {
      return filteredData
        .filter(order => selectedProducts.includes(order.id))
        .length
    }

    const handleProductSelection = (orderId, isSelected) => {
      if (onProductSelect) {
        onProductSelect(orderId, isSelected)
      }
    }

    const handleSelectAllToggle = () => {
      if (onSelectAll) {
        onSelectAll(!isAllSelected)
      }
    }

    const isProductSelected = (orderId) => {
      return selectedProducts.includes(orderId)
    }

    const openOrderDetails = (order) => {
      const orderRouteId = order?.order_id || order?.id
      if (!orderRouteId) return
      if (statusFilter) {
        sessionStorage.setItem(APP_ORDER_STATUS_TAB_STORAGE_KEY, statusFilter)
      }
      navigate(`/dashboard/orders-management/${encodeURIComponent(orderRouteId)}`, {
        state: { order, statusFilter },
      })
    }

    const createDeliveryChallan = async (orderId, driverName, vehicleNo) => {
      try {
        const formData = new FormData();
        formData.append('order_id', orderId);
        formData.append('driver_name', driverName);
        formData.append('vehicle_no', vehicleNo);
        if (statusUpdateModal.shippedDate) {
          formData.append('delivery_date', statusUpdateModal.shippedDate);
        }
        formData.append('invoice', statusUpdateModal.generateInvoice ? 'true' : 'false');
        const dispatchPersonName = statusUpdateModal.useCustomDispatchPerson 
          ? statusUpdateModal.customDispatchPerson 
          : statusUpdateModal.dispatchPersonName;
        if (dispatchPersonName) {
          formData.append('dispatch_person_name', dispatchPersonName);
        }
        if (statusUpdateModal.orderBy) {
          formData.append('order_by', statusUpdateModal.orderBy);
        }
        if (statusUpdateModal.entityName) {
          formData.append('order_by_entity', statusUpdateModal.entityName);
        }

        const response = await apiService.post(
          API_ENDPOINTS.POST_create_delivery_challan,
          formData,
          "multipart/form-data"
        );

        return response;
      } catch (error) {
        console.error('Error creating delivery challan:', error);
        throw error;
      }
    };

    const getStatusOrder = () => {
      if (!statusOptions || statusOptions.length === 0) {
        return {};
      }
      
      const orderMap = {};
      statusOptions.forEach(option => {
        const category = (option.category || '').toLowerCase();
        const sequence = option.sequence || 0;
        if (category) {
          orderMap[category] = sequence;
        }
      });
      
      return orderMap;
    };

    const getNextSequentialStatus = (currentStatusName, currentStatusCategory) => {
      if (!statusOptions || statusOptions.length === 0) {
        return null;
      }

      const statusOrder = getStatusOrder();
      let currentStatusLower = (currentStatusCategory || '').toLowerCase();
      let currentOrder = statusOrder[currentStatusLower] || 0;

      if (currentOrder === 0 && currentStatusName) {
        const currentStatusOption = statusOptions.find(opt => opt.name === currentStatusName);
        if (currentStatusOption) {
          currentStatusLower = (currentStatusOption.category || '').toLowerCase();
          currentOrder = statusOrder[currentStatusLower] || 0;
        }
      }

      if (currentOrder === 0) {
        return null;
      }
      
      const isDelivered = (currentStatusName || '').toLowerCase() === 'delivered' ||
                        (currentStatusCategory || '').toLowerCase() === 'delivered';
      
      if (isDelivered) {
        const returnedStatus = statusOptions.find(option => {
          const optionCategory = (option.category || '').toLowerCase();
          const optionName = (option.name || '').toLowerCase();
          return optionCategory === 'returned' || optionName === 'returned';
        });
        return returnedStatus || null;
      }
      
      const nextOrder = currentOrder + 1;
      const nextStatus = statusOptions.find(option => {
        const optionCategory = (option.category || '').toLowerCase();
        return statusOrder[optionCategory] === nextOrder;
      });

      return nextStatus || null;
    };

    const getStatusesToDisable = (currentStatusCategory, currentStatusName) => {
      if (!statusOptions || statusOptions.length === 0) {
        return [];
      }
      
      const statusOrder = getStatusOrder();
      let currentStatusLower = (currentStatusCategory || '').toLowerCase();
      let currentOrder = statusOrder[currentStatusLower] || 0;
      
      if (currentOrder === 0 && currentStatusName) {
        const currentStatusOption = statusOptions.find(opt => opt.name === currentStatusName);
        if (currentStatusOption) {
          currentStatusLower = (currentStatusOption.category || '').toLowerCase();
          currentOrder = statusOrder[currentStatusLower] || 0;
        }
      }
      
      const dispatchOrder = statusOrder['shipped'] || statusOrder['dispatch'] || 999;
      const processingOrder = statusOrder['processing'] || 0;
      const deliveredOrder = statusOrder['delivered'] || 999;
      
      const isProcessingStatus = currentStatusName?.toLowerCase() === 'processing' || 
                                  currentStatusLower === 'processing' ||
                                  currentOrder === processingOrder;
      
      const isDeliveredStatus = currentStatusName?.toLowerCase() === 'delivered' || 
                                currentStatusLower === 'delivered' ||
                                currentOrder === deliveredOrder;
      
      const isCancelledStatus = currentStatusName?.toLowerCase() === 'cancelled' || 
                                currentStatusLower === 'cancelled' ||
                                currentStatusLower === 'cancelled';
      
      if (currentOrder === 0) {
        return statusOptions
          .filter(option => {
            const optionCategory = (option.category || '').toLowerCase();
            const optionName = (option.name || '').toLowerCase();
            if (optionCategory === 'shipped' || optionCategory === 'dispatch' || optionName === 'dispatch') {
              return true;
            }
            return true;
          })
          .map(option => option.category?.toLowerCase() || '');
      }
      
      if (isCancelledStatus) {
        return statusOptions.filter(option => {
          const optionCategory = (option.category || '').toLowerCase();
          const optionName = (option.name || '').toLowerCase();
          const isReturned = optionCategory === 'returned' || optionName === 'returned';
          return !isReturned;
        }).map(option => option.category?.toLowerCase() || '');
      }
      
      if (isDeliveredStatus) {
        return statusOptions
          .filter(option => {
            const optionCategory = (option.category || '').toLowerCase();
            const optionName = (option.name || '').toLowerCase();
            const isReturned = optionCategory === 'returned' || optionName === 'returned';
            return !isReturned;
          })
          .map(option => option.category?.toLowerCase() || '');
      }
      
      return statusOptions.filter(option => {
        const optionCategory = (option.category || '').toLowerCase();
        const optionName = (option.name || '').toLowerCase();
        const isReturned = optionCategory === 'returned' || optionName === 'returned';
        const isCancelled = optionCategory === 'cancelled' || optionName === 'cancelled';
        const isDispatch = optionCategory === 'shipped' || optionCategory === 'dispatch' || optionName === 'dispatch';
        
        if (isDispatch) {
          return !isProcessingStatus;
        }
        
        if (isReturned) {
          return !isDeliveredStatus;
        }
        
        const statusOrderValue = statusOrder[optionCategory] || 0;
        const dispatchOrder = statusOrder['shipped'] || statusOrder['dispatch'] || 999;
        
        if (statusOrderValue < currentOrder) {
          return true;
        }
        
        if (statusOrderValue === currentOrder) {
          return true;
        }
        
        if (isCancelled) {
          if (currentOrder <= dispatchOrder) {
            return false;
          } else {
            return true;
          }
        }
        
        if (statusOrderValue > currentOrder + 1) {
          return true;
        }
        
        return false;
      }).map(option => option.category?.toLowerCase() || '');
    };

    const isStatusUpdateDispatchFormIncomplete = (modalState) => {
      const dispatchPersonName = modalState.useCustomDispatchPerson
        ? modalState.customDispatchPerson
        : modalState.dispatchPersonName;
      const isTruckOrderBy = String(modalState.orderBy || '').trim().toLowerCase() === 'truck';
      const isProcessingTab = (statusFilter || '').toLowerCase() === 'processing';

      return (
        !modalState.orderBy?.trim() ||
        !modalState.entityName?.trim() ||
        !modalState.driverName?.trim() ||
        (isTruckOrderBy && !modalState.vehicleNo?.trim()) ||
        !modalState.shippedDate?.trim() ||
        !dispatchPersonName?.trim() ||
        (isTruckOrderBy && isVehicleReferenceNoInvalid(modalState.vehicleNo)) ||
        (isTruckOrderBy && isProcessingTab && !String(modalState.start_km || '').trim()) ||
        (isTruckOrderBy && String(modalState.start_km || '').trim() !== '' && Number(modalState.start_km) < 1)
      );
    };

    const isSingleStatusSubmitDisabled = () => {
      if (updatingStatus || statusUpdateModal.isCreatingChallan) return true;

      const selectedStatus = statusUpdateModal.order?.status?.name || statusUpdateModal.order?.status_name;
      if (!selectedStatus) return true;

      const paymentStatus = (statusUpdateModal.order?.payment_status?.name || statusUpdateModal.order?.payment_status || '').toLowerCase();
      const isPaid = isPaymentSatisfiedForOrderAdvance(paymentStatus);
      const isOrdersStatus = selectedStatus === 'Orders';
      const isDispatchTab = ['dispatch', 'shipped'].includes((statusFilter || '').toLowerCase());
      const isTruckOrder = isDispatchTab
        ? isOrderByTruck(statusUpdateModal.order)
        : String(statusUpdateModal.orderBy || '').trim().toLowerCase() === 'truck';

      const originalStatusName = (statusUpdateModal.originalStatus?.name || '').toLowerCase();
      const originalStatusCategory = (statusUpdateModal.originalStatus?.category || '').toLowerCase();
      const isOriginallyDelivered = originalStatusName === 'delivered' || originalStatusCategory === 'delivered';
      const isReturnedSelected = selectedStatus === 'Returned';

      const isShippedCategory = (statusUpdateModal.order?.status?.category || '').toLowerCase() === 'shipped';
      if (
        isShippedCategory &&
        !(isDispatchTab && selectedStatus === 'Delivered') &&
        isStatusUpdateDispatchFormIncomplete(statusUpdateModal)
      ) {
        return true;
      }

      if (selectedStatus === 'Delivered') {
        if (isDispatchTab && !isTruckOrder) return false;
        const startKmFromModal = statusUpdateModal.start_km;
        const startKmFromOrder = statusUpdateModal.order?.delivery_challan?.start_km;
        const startKm = parseFloat(startKmFromModal || startKmFromOrder);
        const endKm = parseFloat(statusUpdateModal.end_km);
        const hasStartKm = !isNaN(startKm);
        if (hasStartKm && !String(statusUpdateModal.end_km || '').trim()) return true;
        if (hasStartKm && !isNaN(endKm) && endKm <= startKm) return true;
      }

      if (selectedStatus === 'Cancelled' && !statusUpdateModal.cancellationReason?.trim()) return true;
      if (selectedStatus === 'Returned' && !statusUpdateModal.returnReason?.trim()) return true;

      if (
        selectedStatus !== 'Returned' &&
        selectedStatus !== 'Cancelled' &&
        !isPaid &&
        !isOrdersStatus &&
        !(isOriginallyDelivered && isReturnedSelected) &&
        !(isDispatchTab && selectedStatus === 'Delivered')
      ) {
        return true;
      }

      return false;
    };

    const handleStatusUpdate = async (id, statusName, orderId) => {
      try {
        setUpdatingStatus(true);
        
        const originalStatusCategory = (statusUpdateModal.originalStatus?.category || '').toLowerCase();
        const originalStatusName = (statusUpdateModal.originalStatus?.name || '').toLowerCase();
        const currentStatusCategory = originalStatusCategory || (statusUpdateModal.order?.status?.category || statusUpdateModal.order?.status_name || '').toLowerCase();
        const currentStatusName = originalStatusName || (statusUpdateModal.order?.status?.name || statusUpdateModal.order?.status_name || '').toLowerCase();
        const selectedOption = statusOptions.find(opt => opt.name === statusName);
        const newStatusCategory = (selectedOption?.category || '').toLowerCase();
        const newStatusName = (statusName || '').toLowerCase();
        
        const statusOrder = getStatusOrder();
        const currentOrder = statusOrder[currentStatusCategory] || 0;
        const newOrder = statusOrder[newStatusCategory] || 0;
        const processingOrder = statusOrder['processing'] || 0;
        const deliveredOrder = statusOrder['delivered'] || 999;
        
        const isProcessingStatus = currentStatusName === 'processing' || 
                                    currentStatusCategory === 'processing' ||
                                    currentOrder === processingOrder;
        
        const isDeliveredStatus = currentStatusName === 'delivered' || 
                                  currentStatusCategory === 'delivered' ||
                                  currentOrder === deliveredOrder;
        
        const isCancelledStatus = currentStatusName === 'cancelled' || 
                                  currentStatusCategory === 'cancelled';
        
        const isCancelledOrReturned = newStatusCategory === 'cancelled' || newStatusCategory === 'returned' ||
                                      newStatusName === 'cancelled' || newStatusName === 'returned';
        const isReturned = newStatusCategory === 'returned' || newStatusName === 'returned';
        const isDispatch = newStatusCategory === 'shipped' || newStatusCategory === 'dispatch' || newStatusName === 'dispatch';
        
        const paymentStatus = (statusUpdateModal.order?.payment_status?.name || statusUpdateModal.order?.payment_status || '').toLowerCase();
        const isPaid = isPaymentSatisfiedForOrderAdvance(paymentStatus);
        const isOrdersStatus = statusName === 'Orders';
        const isOriginallyDelivered = originalStatusName === 'delivered' || originalStatusCategory === 'delivered';
        
        if (!isPaid && !isOrdersStatus) {
          const allowUnpaidPlacedCancel =
            statusUpdateModal.allowUnpaidCancelFromPlaced &&
            (statusName || '').toLowerCase() === 'cancelled';

          if (!allowUnpaidPlacedCancel) {
            if (isReturned && !isOriginallyDelivered) {
              setStatusUpdateModal(prev => ({
                ...prev,
                challanError: 'Returned status is only available when the order is Delivered.'
              }));
              setUpdatingStatus(false);
              return;
            }

            if (!(isOriginallyDelivered && isReturned)) {
              setStatusUpdateModal(prev => ({
                ...prev,
                challanError: 'This order must be paid before it can be moved to any status other than "Orders". Please update the payment status first.'
              }));
              setUpdatingStatus(false);
              return;
            }
          }
        }
        
        if (isDeliveredStatus) {
          if (!isReturned) {
            setStatusUpdateModal(prev => ({
              ...prev,
              challanError: `When an order is Delivered, only Returned status is allowed.`
            }));
            setUpdatingStatus(false);
            return;
          }
        } else {
          if (isReturned) {
            setStatusUpdateModal(prev => ({
              ...prev,
              challanError: `Returned status is only available when the order is Delivered.`
            }));
            setUpdatingStatus(false);
            return;
          }
          
          if (isCancelledStatus) {
            setStatusUpdateModal(prev => ({
              ...prev,
              challanError: `Cancelled is a final status. No further status changes are allowed.`
            }));
            setUpdatingStatus(false);
            return;
          }
          
          if (isDispatch) {
            const dispatchOrder = statusOrder['shipped'] || statusOrder['dispatch'] || 999;
            
            const isActuallyProcessing = (currentStatusName === 'processing' || 
                                        currentStatusCategory === 'processing') &&
                                        currentOrder === processingOrder &&
                                        processingOrder > 0;
            
            if (!isActuallyProcessing) {
              const currentStatusDisplay = statusUpdateModal.originalStatus?.name || 
                                        statusUpdateModal.order?.status?.name || 
                                        statusUpdateModal.order?.status_name || 
                                        currentStatusCategory ||
                                        'Unknown';
              setStatusUpdateModal(prev => ({
                ...prev,
                challanError: `Dispatch status is only available when the order is in Processing status. Current status: ${currentStatusDisplay}.`
              }));
              setUpdatingStatus(false);
              return;
            }
          }
        }
        
        if (!isCancelledOrReturned) {
          if (newOrder > currentOrder + 1) {
            setStatusUpdateModal(prev => ({
              ...prev,
              challanError: `Status must be updated sequentially. You can only move to the next status in the sequence. Current: ${statusUpdateModal.order?.status?.name || currentStatusCategory}, Next allowed: ${statusOptions.find(opt => {
                const optOrder = statusOrder[(opt.category || '').toLowerCase()] || 0;
                return optOrder === currentOrder + 1;
              })?.name || 'N/A'}.`
            }));
            setUpdatingStatus(false);
            return;
          }
          
          if (newOrder < currentOrder) {
            setStatusUpdateModal(prev => ({
              ...prev,
              challanError: `Cannot move to a previous status. Current status: ${statusUpdateModal.order?.status?.name || currentStatusCategory}.`
            }));
            setUpdatingStatus(false);
            return;
          }
        }
        
        const selectedOptionForShipping = statusOptions.find(opt => opt.name === statusName);
        const statusCategoryForShipping = (selectedOptionForShipping?.category || '').toLowerCase();
        if (statusCategoryForShipping === 'shipped') {
          const { driverName, vehicleNo } = statusUpdateModal;
          
          if (isStatusUpdateDispatchFormIncomplete(statusUpdateModal)) {
            const isTruckOrderBy = String(statusUpdateModal.orderBy || '').trim().toLowerCase() === 'truck';
            const isProcessingTab = (statusFilter || '').toLowerCase() === 'processing';
            let challanError = 'Please fill all required delivery details before shipping.';
            if (isTruckOrderBy && isVehicleReferenceNoInvalid(vehicleNo)) {
              challanError = 'Vehicle No / Reference No must be in format MH 12 AB 1234';
            } else if (
              isTruckOrderBy &&
              isProcessingTab &&
              !String(statusUpdateModal.start_km || '').trim()
            ) {
              challanError = 'Start KM is required for Truck orders.';
            } else if (
              isTruckOrderBy &&
              String(statusUpdateModal.start_km || '').trim() !== '' &&
              Number(statusUpdateModal.start_km) < 1
            ) {
              challanError = 'Start KM must be 1 or greater';
            }
            setStatusUpdateModal(prev => ({
              ...prev,
              challanError
            }));
            setUpdatingStatus(false);
            return;
          }

          setStatusUpdateModal(prev => ({
            ...prev,
            isCreatingChallan: true,
            challanError: ''
          }));

          try {
            const challanResponse = await createDeliveryChallan(orderId, driverName, vehicleNo);
            
            if (!challanResponse?.success) {
              throw new Error(challanResponse?.message || 'Failed to create delivery challan');
            }

            console.log('Delivery challan created successfully');
          } catch (challanError) {
            console.error('Error creating delivery challan:', challanError);
            setStatusUpdateModal(prev => ({
              ...prev,
              challanError: challanError.message || 'Failed to create delivery challan',
              isCreatingChallan: false
            }));
            setUpdatingStatus(false);
            return;
          }

          setStatusUpdateModal(prev => ({
            ...prev,
            isCreatingChallan: false
          }));
        }

        if (statusName === 'Delivered') {
          const isDispatchTab = ['dispatch', 'shipped'].includes((statusFilter || '').toLowerCase());
          const isTruckOrder = isDispatchTab
            ? isOrderByTruck(statusUpdateModal.order)
            : String(statusUpdateModal.orderBy || '').trim().toLowerCase() === 'truck';
          if (!isDispatchTab || isTruckOrder) {
            const startKmFromModal = statusUpdateModal.start_km;
            const startKmFromOrder = statusUpdateModal.order?.delivery_challan?.start_km;
            const startKm = parseFloat(startKmFromModal || startKmFromOrder);
            const endKm = parseFloat(statusUpdateModal.end_km);
            const hasStartKm = !isNaN(startKm);
            
            if (hasStartKm && !String(statusUpdateModal.end_km || '').trim()) {
              setStatusUpdateModal(prev => ({
                ...prev,
                challanError: 'End KM is required'
              }));
              setUpdatingStatus(false);
              return;
            }

            if (hasStartKm && !isNaN(endKm) && endKm <= startKm) {
              alert('End KM must be greater than Start KM. Please enter a valid End KM value.');
              setStatusUpdateModal(prev => ({
                ...prev,
                challanError: 'End KM must be greater than Start KM'
              }));
              setUpdatingStatus(false);
              return;
            }
          }
        }

        const userId = localStorage.getItem("user_id");
        const dispatchPersonName = statusUpdateModal.useCustomDispatchPerson 
          ? statusUpdateModal.customDispatchPerson 
          : statusUpdateModal.dispatchPersonName;
        const payload = {
          user_id: userId,
          status: statusName,
          order_id: orderId,
          emails: statusUpdateModal.emails,
          generateInvoice: statusUpdateModal.generateInvoice,
          driverName: statusUpdateModal.driverName,
          vehicleNo: statusUpdateModal.vehicleNo,
          delivery_date: statusName === 'Processing' ? null : statusUpdateModal.shippedDate,
          dispatch_person_name: dispatchPersonName,
          start_km: statusUpdateModal.start_km,
          ...(statusUpdateModal.orderBy && { order_by: statusUpdateModal.orderBy }),
          ...(statusUpdateModal.entityName && { order_by_entity: statusUpdateModal.entityName }),
          ...(statusName === 'Delivered' && { end_km: statusUpdateModal.end_km }),
          ...(statusName === 'Cancelled' && { reason: statusUpdateModal.cancellationReason || '' }),
          ...(statusName === 'Returned' && { reason: statusUpdateModal.returnReason || '' })
        };

        const isSequentialNext = isSequentialNextStatusTransition(
          statusOptions,
          statusUpdateModal.originalStatus?.name,
          statusUpdateModal.originalStatus?.category,
          statusName
        );

        const response = await apiService.put(
          API_ENDPOINTS.UPDATE_product_status,
          payload,
          'application/json'
        );

        if (response?.success) {
          setStatusUpdateModal({ 
            open: false, 
            order: null,
            driverName: '',
            vehicleNo: '',
            delivery_date: new Date().toISOString().slice(0,10),
            generateInvoice: false,
            isCreatingChallan: false,
            challanError: '',
            dispatchPersonName: '',
            customDispatchPerson: '',
            useCustomDispatchPerson: false,
            start_km: '',
            end_km: '',
            cancellationReason: '',
            returnReason: '',
            allowUnpaidCancelFromPlaced: false,
          });
          
          if (onSelectAll) {
            onSelectAll(false);
          }
          setTimeout(() => {
            onRefresh?.();
          }, 100);
        } else {
          console.error('Status update failed:', response?.message);
          const apiErrors = response?.errors;
          const errorMessage = Array.isArray(apiErrors) && apiErrors.length > 0
            ? apiErrors.join(' ')
            : (response?.message || 'Failed to update status');
          setStatusUpdateModal(prev => ({
            ...prev,
            challanError: errorMessage
          }));
        }
      } catch (error) {
        console.error('Error updating status:', error);
        const apiErrors = error?.errors;
        const errorMessage = Array.isArray(apiErrors) && apiErrors.length > 0
          ? apiErrors.join(' ')
          : (error?.message || 'An error occurred while updating status');
        setStatusUpdateModal(prev => ({
          ...prev,
          challanError: errorMessage
        }));
      } finally {
        setUpdatingStatus(false);
      }
    };

    const validatePaidPaymentDetails = (modalState) => {
      const apiValidation = validateNewPaidPaymentRequest(modalState);
      if (apiValidation) return apiValidation;
      const mandatoryError = validatePaymentMethodMandatoryFields(modalState);
      if (mandatoryError) return mandatoryError;
      return validatePaidPaymentFormDetails(
        modalState,
        enforcePaymentMethodRequiredFields(modalState.paymentMethodConfig)
      );
    };

    const canSubmitPaidPayment = (modalState) => {
      if (modalState?.paymentOption === 'credit') return false;
      if (!modalState?.orderPaymentDetails) return false;
      if (!String(modalState.creditNote || '').trim()) return false;
      if (isZeroRemainingPayment(modalState)) return true;
      if (!modalState.paymentMethod || !modalState.paymentMethodConfig) return false;
      if (validatePaymentMethodMandatoryFields(modalState)) return false;
      return !validatePaidPaymentFormDetails(
        modalState,
        enforcePaymentMethodRequiredFields(modalState.paymentMethodConfig)
      );
    };

    const canSubmitCreditPayment = (modalState) => {
      if (modalState?.paymentOption !== 'credit') return false;
      return Boolean(String(modalState.creditNote || '').trim());
    };

    const shouldShowCreditSubmitButton = (modalState) => {
      if (modalState?.paymentOption !== 'credit') return false;
      const remainingAmount = Number(modalState?.orderPaymentDetails?.remainingAmount ?? 0);
      return remainingAmount >= 0;
    };

    const submitPaymentStatusRequest = async (order_id, newStatus, extras = {}) => {
      const normalizedStatus = String(newStatus || '').toLowerCase();

      if (normalizedStatus === 'paid') {
        if (!extras.modalState) {
          throw { success: false, message: 'Payment details are required' };
        }
        return submitNewPaidPaymentStatus(extras.modalState, order_id);
      }

      const userId = localStorage.getItem('user_id');
      const { refundAmount, orderTotalAmount, installmentAmount, creditNote } = extras;
      const isRefunded = normalizedStatus === 'refunded';
      const isCredit = normalizedStatus === 'credit';
      const isFailed = normalizedStatus === 'failed';
      const isPending = normalizedStatus === 'pending';
      const isOutstanding = normalizedStatus === 'outstanding';
      const refundVal =
        refundAmount != null && refundAmount !== '' ? refundAmount : orderTotalAmount ?? 0;

      const payload = {
        order_id,
        payment_status: newStatus,
        user_id: userId,
        ...(isRefunded && { refund_amount: refundVal }),
        ...(isCredit && installmentAmount != null && { installment_amount: installmentAmount }),
        ...((isCredit || isFailed || isPending || isOutstanding || isRefunded) && creditNote && { note: creditNote }),
      };
      return apiService.put(API_ENDPOINTS.UPDATE_payment_status, payload);
    };

    const handlePaymentStatusUpdate = async (
      orderId,
      newStatus,
      order_id,
      refundAmount,
      orderTotalAmount,
      paidPaymentExtras = null
    ) => {
      setUpdatingPayment(true)
      try {
        const response = await submitPaymentStatusRequest(order_id, newStatus, {
          refundAmount,
          orderTotalAmount,
          installmentAmount: paidPaymentExtras?.installmentAmount,
          creditNote: paidPaymentExtras?.creditNote,
          paidPayload: paidPaymentExtras?.paidPayload,
          paymentFiles: paidPaymentExtras?.paymentFiles,
        });
        if (response?.success) {
          toast.showSuccess(response?.message || 'Payment status updated successfully');
          if (shouldAdvanceTabAfterPaymentUpdate(newStatus)) {
            await refreshOrdersAfterPaymentUpdate(paymentUpdateModal.order, response)
          } else {
            await onRefresh()
          }
          setPaymentUpdateModal(closePaymentUpdateModalState())
          setPaymentStatusChoices([])
        } else {
          const message = response?.message || 'Failed to update payment status';
          toast.showError(message);
          setPaymentUpdateModal((prev) => ({ ...prev, paymentError: message }));
        }
      } catch (error) {
        console.error("Error updating payment status:", error)
        const message = extractPaymentApiError(error);
        toast.showError(message);
        setPaymentUpdateModal((prev) => ({
          ...prev,
          paymentError: message,
        }));
      } finally {
        setUpdatingPayment(false)
      }
    }

    const handleConfirmCreditPaymentDetails = async () => {
      const methodValidationError = validatePaymentMethodMandatoryFields(paymentUpdateModal);
      if (methodValidationError) {
        setPaymentUpdateModal((prev) => ({ ...prev, paymentError: methodValidationError }));
        return;
      }

      if (!canSubmitCreditPayment(paymentUpdateModal)) {
        return;
      }

      const remainingAmount = Number(
        paymentUpdateModal.orderPaymentDetails?.remainingAmount ?? 0
      );
      const orderId = paymentUpdateModal.order?.order_id;
      const filledInstallment = paymentUpdateModal.installmentAmount;
      const installmentAmount =
        remainingAmount === 0
          ? 0
          : filledInstallment !== '' && !Number.isNaN(Number(filledInstallment))
            ? Number(filledInstallment)
            : 0;
      const creditNote = paymentUpdateModal.creditNote || '';

      setUpdatingPayment(true);
      setPaymentUpdateModal((prev) => ({ ...prev, paymentError: '' }));

      try {
        const formData = buildNewCreditPaymentFormData(
          paymentUpdateModal,
          orderId,
          installmentAmount,
          creditNote
        );

        const response = await apiService.putMultipart(
          API_ENDPOINTS.UPDATE_new_payment_status,
          formData
        );

        if (response?.success) {
          toast.showSuccess(response?.message || 'Payment status updated successfully');
          await refreshOrdersAfterPaymentUpdate(paymentUpdateModal.order, response);
          setPaymentUpdateModal(closePaymentUpdateModalState());
          setPaymentStatusChoices([]);
        } else {
          const message = response?.message || 'Failed to update payment status';
          toast.showError(message);
          setPaymentUpdateModal((prev) => ({ ...prev, paymentError: message }));
        }
      } catch (error) {
        console.error('Error submitting credit payment:', error);
        const message = extractPaymentApiError(error);
        toast.showError(message);
        setPaymentUpdateModal((prev) => ({ ...prev, paymentError: message }));
      } finally {
        setUpdatingPayment(false);
      }
    };

    const handleConfirmPaidPaymentDetails = async () => {
      const validationError = validatePaidPaymentDetails(paymentUpdateModal);
      if (validationError) {
        setPaymentUpdateModal((prev) => ({ ...prev, paymentError: validationError }));
        return;
      }

      const orderId = paymentUpdateModal.order?.order_id;
      setUpdatingPayment(true);
      setPaymentUpdateModal((prev) => ({ ...prev, paymentError: '' }));

      try {
        const formData = buildNewPaidPaymentFormData(paymentUpdateModal, orderId);
        formData.append('note', paymentUpdateModal.creditNote.trim());
        const response = await apiService.putMultipart(
          API_ENDPOINTS.UPDATE_new_payment_status,
          formData
        );
        if (response?.success) {
          toast.showSuccess(
            response?.message || 'Payment status updated successfully'
          );
          await refreshOrdersAfterPaymentUpdate(paymentUpdateModal.order, response);
          setPaymentUpdateModal(closePaymentUpdateModalState());
          setPaymentStatusChoices([]);
        } else {
          const message = response?.message || 'Failed to update payment status';
          toast.showError(message);
          setPaymentUpdateModal((prev) => ({ ...prev, paymentError: message }));
        }
      } catch (error) {
        console.error('Error submitting paid payment:', error);
        const message = extractPaymentApiError(error);
        toast.showError(message);
        setPaymentUpdateModal((prev) => ({ ...prev, paymentError: message }));
      } finally {
        setUpdatingPayment(false);
      }
    };

    const getSelectedOrdersForBulkPayment = () =>
      filteredData.filter((order) => selectedProducts.includes(order.id));

    const getUnpaidOrdersFromList = (orders = []) =>
      orders.filter((order) => {
        const paymentStatus = getOrderPaymentStatusValue(order);
        return !isPaymentSatisfiedForOrderAdvance(paymentStatus);
      });

    const openPaidPaymentFlowForOrder = (order) => {
      openPaymentModalForOrder(order);
    };

    const openBulkPaidPaymentFlow = (orders = null) => {
      const targetOrders = orders?.length
        ? orders
        : getUnpaidOrdersFromList(getSelectedOrdersForBulkPayment());
      if (targetOrders.length === 0) return;
      const summaryOrder = targetOrders[0];
      setBulkPaymentStatusModal({
        ...DEFAULT_BULK_PAYMENT_STATUS_MODAL,
        open: true,
        selectedPaymentStatus: 'paid',
        step: 'payment_details',
        paymentOption: 'remaining',
        summaryOrder,
      });
      fetchPaymentStatusChoicesForOrder(summaryOrder?.order_id);
      closeActionsMenu();
    };

    const handleConfirmBulkCreditPaymentDetails = async () => {
      const bulkCreditState = {
        ...bulkPaymentStatusModal,
        order: bulkPaymentStatusModal.summaryOrder,
      };

      const bulkMethodValidationError = validatePaymentMethodMandatoryFields(bulkCreditState);
      if (bulkMethodValidationError) {
        setBulkPaymentStatusModal((prev) => ({
          ...prev,
          paymentError: bulkMethodValidationError,
        }));
        return;
      }

      if (!canSubmitCreditPayment(bulkCreditState)) {
        return;
      }

      const unpaidOrders = getUnpaidOrdersFromList(getSelectedOrdersForBulkPayment());
      if (unpaidOrders.length === 0) {
        setBulkPaymentStatusModal((prev) => ({
          ...prev,
          paymentError: 'No unpaid orders in the current selection.',
        }));
        return;
      }

      const remainingAmount = Number(
        bulkPaymentStatusModal.orderPaymentDetails?.remainingAmount ?? 0
      );
      const filledInstallment = bulkPaymentStatusModal.installmentAmount;
      const installmentAmount =
        remainingAmount === 0
          ? 0
          : filledInstallment !== '' && !Number.isNaN(Number(filledInstallment))
            ? Number(filledInstallment)
            : 0;
      const creditNote = bulkPaymentStatusModal.creditNote || '';

      setBulkPaymentStatusModal((prev) => ({ ...prev, isUpdating: true, error: '', paymentError: '' }));

      try {
        const responses = await Promise.all(
          unpaidOrders.map((order) => {
            const orderDetails = getOrderPaymentDetailsFromOrder(order);
            const orderRemaining = Number(orderDetails.remainingAmount ?? 0);
            const orderInstallment = orderRemaining === 0 ? 0 : installmentAmount;
            const orderModalState = {
              ...bulkCreditState,
              order,
              orderPaymentDetails: orderDetails,
            };
            const formData = buildNewCreditPaymentFormData(
              orderModalState,
              order.order_id,
              orderInstallment,
              creditNote
            );
            return apiService.putMultipart(API_ENDPOINTS.UPDATE_new_payment_status, formData);
          })
        );
        const allSuccess = responses.every((response) => response?.success);
        if (allSuccess) {
          toast.showSuccess(responses[0]?.message || 'Payment status updated successfully');
          setBulkPaymentStatusModal({ ...DEFAULT_BULK_PAYMENT_STATUS_MODAL });
          await refreshOrdersAfterPaymentUpdate();
        } else {
          const failed = responses.find((response) => !response?.success);
          const message = failed?.message || 'Some payment status updates failed. Please try again.';
          toast.showError(message);
          setBulkPaymentStatusModal((prev) => ({
            ...prev,
            isUpdating: false,
            error: message,
          }));
        }
      } catch (error) {
        console.error('Error submitting bulk credit payment:', error);
        const message = extractPaymentApiError(error);
        toast.showError(message);
        setBulkPaymentStatusModal((prev) => ({
          ...prev,
          isUpdating: false,
          error: message,
        }));
      }
    };

    const handleConfirmBulkPaidPaymentDetails = async () => {
      const bulkPaidState = {
        ...bulkPaymentStatusModal,
        order: bulkPaymentStatusModal.summaryOrder,
      };
      const unpaidOrders = getUnpaidOrdersFromList(getSelectedOrdersForBulkPayment());
      if (unpaidOrders.length === 0) {
        setBulkPaymentStatusModal((prev) => ({
          ...prev,
          paymentError: 'No unpaid orders in the current selection.',
        }));
        return;
      }

      for (const order of unpaidOrders) {
        const orderDetails = getOrderPaymentDetailsFromOrder(order);
        const orderModalState = {
          ...bulkPaidState,
          order,
          orderPaymentDetails: orderDetails,
          advanceAmount: String(orderDetails.remainingAmount),
          paymentMethodConfig:
            paymentMethodsById[bulkPaidState.paymentMethod] || bulkPaidState.paymentMethodConfig,
        };
        const validationError = validatePaidPaymentDetails(orderModalState);
        if (validationError) {
          setBulkPaymentStatusModal((prev) => ({
            ...prev,
            paymentError: `${order.order_id}: ${validationError}`,
          }));
          return;
        }
      }

      setBulkPaymentStatusModal((prev) => ({ ...prev, isUpdating: true, error: '', paymentError: '' }));

      try {
        const responses = await Promise.all(
          unpaidOrders.map((order) => {
            const orderDetails = getOrderPaymentDetailsFromOrder(order);
            const orderModalState = {
              ...bulkPaidState,
              order,
              orderPaymentDetails: orderDetails,
              advanceAmount: String(orderDetails.remainingAmount),
            };
            const formData = buildNewPaidPaymentFormData(orderModalState, order.order_id);
            formData.append('note', bulkPaidState.creditNote.trim());
            return apiService.putMultipart(
              API_ENDPOINTS.UPDATE_new_payment_status,
              formData
            );
          })
        );
        const allSuccess = responses.every((response) => response?.success);
        if (allSuccess) {
          toast.showSuccess(
            responses[0]?.message || 'Payment status updated successfully'
          );
          setBulkPaymentStatusModal({ ...DEFAULT_BULK_PAYMENT_STATUS_MODAL });
          await refreshOrdersAfterPaymentUpdate(
            bulkPaymentStatusModal.summaryOrder || unpaidOrders[0],
            responses[0]
          );
        } else {
          const failed = responses.find((response) => !response?.success);
          const message =
            failed?.message || 'Some payment status updates failed. Please try again.';
          toast.showError(message);
          setBulkPaymentStatusModal((prev) => ({
            ...prev,
            isUpdating: false,
            error: message,
          }));
        }
      } catch (error) {
        console.error('Error updating bulk paid payment:', error);
        const message = extractPaymentApiError(error);
        toast.showError(message);
        setBulkPaymentStatusModal((prev) => ({
          ...prev,
          isUpdating: false,
          error: message,
        }));
      }
    };

    const handleBulkPaymentStatusUpdate = async () => {
      if (!bulkChallanModal.bulkPaymentStatus) {
        setBulkChallanModal(prev => ({
          ...prev,
          error: 'Please select a payment status'
        }))
        return
      }

      setIsUpdatingBulkPayment(true)
      try {
        const userId = localStorage.getItem("user_id");
        const selectedOrders = filteredData.filter(order => selectedProducts.includes(order.id));
        
        const unpaidOrders = selectedOrders.filter(order => {
          const paymentStatus = (order.payment_status?.name || order.payment_status || '').toLowerCase();
          const isPaid = isPaymentSatisfiedForOrderAdvance(paymentStatus);
          return !isPaid;
        });

        if (unpaidOrders.length === 0) {
          setBulkChallanModal(prev => ({
            ...prev,
            error: 'All selected orders are already paid or outstanding. No payment status update needed.'
          }))
          setIsUpdatingBulkPayment(false)
          return
        }

        const unpaidOrderIds = unpaidOrders.map(order => order.order_id).filter(Boolean);

        const updatePromises = unpaidOrderIds.map(orderId => {
          const payload = {
            order_id: orderId,
            payment_status: bulkChallanModal.bulkPaymentStatus,
            user_id: userId,
          };
          return apiService.put(
            `${API_ENDPOINTS.UPDATE_payment_status}`,
            payload
          );
        });

        const responses = await Promise.all(updatePromises);
        const allSuccess = responses.every(response => response?.success);

        if (allSuccess) {
          setBulkChallanModal(prev => ({
            ...prev,
            bulkPaymentStatus: '',
            error: ''
          }))
          await onRefresh()
        } else {
          setBulkChallanModal(prev => ({
            ...prev,
            error: 'Some payment status updates failed. Please try again.'
          }))
        }
      } catch (error) {
        console.error("Error updating bulk payment status:", error)
        setBulkChallanModal(prev => ({
          ...prev,
          error: 'Error updating payment status. Please try again.'
        }))
      } finally {
        setIsUpdatingBulkPayment(false)
      }
    }

    const handleBulkPaymentStatusUpdateFromModal = async () => {
      if (!bulkPaymentStatusModal.selectedPaymentStatus) {
        setBulkPaymentStatusModal(prev => ({
          ...prev,
          error: 'Please select a payment status'
        }))
        return
      }

      if (bulkPaymentStatusModal.selectedPaymentStatus === 'paid') {
        handleConfirmBulkPaidPaymentDetails();
        return;
      }

      if (
        ['failed', 'pending', 'outstanding', 'refunded'].includes(
          bulkPaymentStatusModal.selectedPaymentStatus
        ) &&
        !String(bulkPaymentStatusModal.creditNote || '').trim()
      ) {
        setBulkPaymentStatusModal((prev) => ({
          ...prev,
          error: 'Please enter a note',
        }));
        return;
      }

      setBulkPaymentStatusModal(prev => ({ ...prev, isUpdating: true, error: '' }))
      try {
        const userId = localStorage.getItem("user_id");
        const selectedOrders = filteredData.filter(order => selectedProducts.includes(order.id));
        const selectedOrderIds = selectedOrders.map(order => order.order_id).filter(Boolean);

        if (selectedOrderIds.length === 0) {
          setBulkPaymentStatusModal(prev => ({
            ...prev,
            error: 'No orders selected',
            isUpdating: false
          }))
          return
        }

        const updatePromises = selectedOrderIds.map(orderId => {
          const payload = {
            order_id: orderId,
            payment_status: bulkPaymentStatusModal.selectedPaymentStatus,
            user_id: userId,
            ...(bulkPaymentStatusModal.creditNote?.trim()
              ? { note: bulkPaymentStatusModal.creditNote.trim() }
              : {}),
          };
          return apiService.put(
            `${API_ENDPOINTS.UPDATE_payment_status}`,
            payload
          );
        });

        const responses = await Promise.all(updatePromises);
        const allSuccess = responses.every(response => response?.success);

        if (allSuccess) {
          setBulkPaymentStatusModal({ ...DEFAULT_BULK_PAYMENT_STATUS_MODAL })
          await onRefresh()
        } else {
          setBulkPaymentStatusModal(prev => ({
            ...prev,
            error: 'Some payment status updates failed. Please try again.',
            isUpdating: false
          }))
        }
      } catch (error) {
        console.error("Error updating bulk payment status:", error)
        setBulkPaymentStatusModal(prev => ({
          ...prev,
          error: 'Error updating payment status. Please try again.',
          isUpdating: false
        }))
      }
    }

    const handleOpenBulkPaymentStatusModal = () => {
      if (isBulkPaymentUpdateDisabled()) return;
      const selectedOrders = getSelectedOrdersForBulkPayment();
      const summaryOrder = selectedOrders[0] || null;
      setBulkPaymentStatusModal({
        ...DEFAULT_BULK_PAYMENT_STATUS_MODAL,
        open: true,
        summaryOrder,
      });
      fetchPaymentStatusChoicesForOrder(summaryOrder?.order_id);
      loadAdvancePaymentMethods();
      closeActionsMenu();
    };

    const handleCloseBulkPaymentStatusModal = () => {
      if (bulkPaymentStatusModal.isUpdating) return;
      setBulkPaymentStatusModal({ ...DEFAULT_BULK_PAYMENT_STATUS_MODAL });
      setPaymentStatusChoices([]);
    };

    const handleOpenStatusModal = (order) => {
      const originalStatus = {
        name: order?.status?.name || order?.status_name || '',
        category: order?.status?.category || ''
      };
      const isCancelledStatus = (originalStatus.name || '').toLowerCase() === 'cancelled' || 
                                (originalStatus.category || '').toLowerCase() === 'cancelled';
      const isPlacedOrdersTab = (statusFilter || '').toLowerCase() === 'placed';

      let nextStatus = null;
      let allowUnpaidCancelFromPlaced = false;
      let defaultCancellationReason = '';

      if (isPlacedOrdersTab && !isCancelledStatus) {
        nextStatus = getNextSequentialStatus(originalStatus.name, originalStatus.category);
        allowUnpaidCancelFromPlaced = true;
        defaultCancellationReason = '';
      } else if (isCancelledStatus) {
        nextStatus = statusOptions.find(opt => 
          (opt.name || '').toLowerCase() === 'returned' || 
          (opt.category || '').toLowerCase() === 'returned'
        );
      } else {
        nextStatus = getNextSequentialStatus(originalStatus.name, originalStatus.category);
      }
      
      const updatedOrder = nextStatus ? {
        ...order,
        status: {
          name: nextStatus.name,
          category: nextStatus.category
        },
        status_name: nextStatus.name
      } : order;

      const dispatchChallan = getOrderChallan(order);
      const isTruckOrder = isOrderByTruck(order);
      
      setStatusUpdateModal({
        open: true,
        order: updatedOrder,
        originalStatus: originalStatus,
        entityName: '',
        orderBy: '',
        driverName: isTruckOrder ? (dispatchChallan?.driver_name || '') : '',
        vehicleNo: isTruckOrder
          ? (
              dispatchChallan?.vehicle_number ||
              dispatchChallan?.vehicle_no ||
              dispatchChallan?.vehicle_name ||
              ''
            )
          : '',
        shippedDate: new Date().toISOString().slice(0, 10),
        start_km:
          isTruckOrder &&
          dispatchChallan?.start_km !== null &&
          dispatchChallan?.start_km !== undefined
            ? String(dispatchChallan.start_km)
            : '',
        end_km: '',
        generateInvoice: false,
        isCreatingChallan: false,
        challanError: '',
        dispatchPersonName: '',
        customDispatchPerson: '',
        useCustomDispatchPerson: false,
        cancellationReason: defaultCancellationReason,
        returnReason: '',
        allowUnpaidCancelFromPlaced,
      });
      setEntityOptions([]);
    };

    const handleDownload = async (type, downloadFunction) => {
      setIsDownloading(true);
      closeActionsMenu();
      
      try {
        await downloadFunction(type);
      } catch (error) {
        console.error('Download failed:', error);
      } finally {
        setIsDownloading(false);
      }
    };

    const handleCloseStatusModal = () => {
      setStatusUpdateModal({
        open: false,
        order: null,
        entityName: '',
        orderBy: '',
        driverName: '',
        vehicleNo: '',
        delivery_date: new Date().toISOString().slice(0,10),
        shippedDate: new Date().toISOString().slice(0, 10),
        generateInvoice: false,
        isCreatingChallan: false,
        challanError: '',
        dispatchPersonName: '',
        customDispatchPerson: '',
        useCustomDispatchPerson: false,
        start_km: '',
        end_km: '',
        cancellationReason: '',
        returnReason: '',
        allowUnpaidCancelFromPlaced: false,
      });
    };

    const handleStatusModalChange = (field, value) => {
      setStatusUpdateModal(prev => ({
        ...prev,
        [field]: value,
        challanError: ''
      }));
    };

    // `search` is sent to the API from `product.jsx` (list-user-orders). Do not filter again here:
    // a second pass on email / order_id / customer name only hid valid results when the match was
    // in expanded row data (e.g. product name, SKU on order line items).
    const filteredData = products;

    const formatCurrency = (amount) => {
      return `₹${parseFloat(amount).toLocaleString('en-IN')}`;
    };

    const getStatusIcon = (status) => {
      const category = typeof status === 'object' && status !== null && 'category' in status
        ? status.category
        : status
      return getIconForCategory(category)
    };


    if (isLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress size={40} />
        </Box>
      )
    }

    return (
      <Box sx={{ width: "100%", p: 2 }}>
        {/* Export Controls */}
        {selectedProducts.length > 0 && (
          <Box sx={{ mb: 2, p: 2, backgroundColor: alpha(theme.palette.primary.main, 0.1), borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="body1" fontWeight={600}>
                {selectedProducts.length} product{selectedProducts.length > 1 ? 's' : ''} selected
              </Typography>
              {getEligibleProductsCount() > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ ml: 1, display: 'block' }}>
                  {getEligibleProductsCount()} item Selected
                </Typography>
              )}
            </Box>
            <Box>
              <Button variant="contained" size="small" startIcon={isDownloading ? <CircularProgress size={20} sx={{ color: "#ffffff" }} /> : <FileDownload />} onClick={openActionsMenu} sx={{ textTransform: 'none' }} disabled={isDownloading}>
                {isDownloading ? (<span style={{ color: '#fff7f7', fontWeight: 'bold' }}>Downloading..</span>) : ('Actions')}
              </Button>
              <Menu anchorEl={actionsAnchorEl} open={isActionsMenuOpen} onClose={closeActionsMenu} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
                <ListSubheader disableSticky>Export</ListSubheader>
                <MenuItem onClick={() => handleDownload('excel', onExport)} disabled={isDownloading}>
                  <ListItemIcon><FileDownload fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Export to Excel" />
                </MenuItem>
                <MenuItem onClick={() => handleDownload('csv', onExport)} disabled={isDownloading}>
                  <ListItemIcon><FileDownload fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Export to CSV" />
                </MenuItem>
                <Divider />
                <ListSubheader disableSticky>Documents ZIP</ListSubheader>
                <MenuItem onClick={() => handleDownload('invoice', onZipDownload)} disabled={shouldDisableDocumentsAndEmail() || isDownloading}>
                  <ListItemIcon><ReceiptIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Invoices ZIP" />
                </MenuItem>
                <MenuItem onClick={() => handleDownload('challan', onZipDownload)} disabled={shouldDisableDocumentsAndEmail() || isDownloading}>
                  <ListItemIcon><ChallanIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Challans ZIP" />
                </MenuItem>
                <MenuItem onClick={() => handleDownload('both', onZipDownload)} disabled={shouldDisableDocumentsAndEmail() || isDownloading}>
                  <ListItemIcon><DownloadIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Both ZIP" />
                </MenuItem>
                <Divider />
                <ListSubheader disableSticky>Email</ListSubheader>
                <MenuItem onClick={handleOpenEmailDialog} disabled={shouldDisableDocumentsAndEmail() || isDownloading}>
                  <ListItemIcon><EmailIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Send via Email" />
                </MenuItem>
                <Divider />
                <ListSubheader disableSticky>Operations</ListSubheader>
                <MenuItem
                  onClick={handleOpenBulkPaymentStatusModal}
                  disabled={selectedProducts.length === 0 || isDownloading || isBulkPaymentUpdateDisabled()}
                >
                  <ListItemIcon><Payment fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Bulk Payment Status Update" />
                </MenuItem>
                <MenuItem onClick={() => { closeActionsMenu(); handleBulkDeliveryChallan(); }} disabled={selectedProducts.length === 0 || ['Returned', 'Cancelled'].includes(statusFilter) || isDownloading}>
                  <ListItemIcon><DeliveryIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primary={statusFilter === 'Dispatch' ? 'Status Update' : 'Bulk Delivery Status'} />
                </MenuItem>
              </Menu>
            </Box>
          </Box>
        )}

       <TableContainer
          component={Paper}
          sx={(theme) => ({
            maxHeight: 600,
            width: "100%",
            maxWidth: "100%",
            overflowX: "auto",
            WebkitOverflowScrolling: "touch",
            borderRadius: 2,
            scrollbarWidth: "thin",
            scrollbarColor: theme.palette.mode === "dark" ? "#555 #1e1e1e" : "#c1c1c1 #f1f1f1",
            "&::-webkit-scrollbar": { height: 8, width: 8 },
            "&::-webkit-scrollbar-track": {
              background: theme.palette.mode === "dark" ? "#1e1e1e" : "#f1f1f1",
              borderRadius: 8,
            },
            "&::-webkit-scrollbar-thumb": {
              background: theme.palette.mode === "dark" ? "#555" : "#c1c1c1",
              borderRadius: 8,
            },
            "&::-webkit-scrollbar-thumb:hover": {
              background: theme.palette.mode === "dark" ? "#777" : "#a8a8a8",
            },
          })}
        >
          <Table
            stickyHeader
            sx={{
              width: "100%",
              minWidth: { xs: 1100, lg: "100%" },
              tableLayout: { xs: "auto", lg: "fixed" },
              "& .MuiTableCell-root": {
                verticalAlign: "middle",
                [theme.breakpoints.down("md")]: {
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                  padding: "10px 8px",
                  fontSize: "0.8125rem",
                },
              },
            }}
          >
            <TableHead
              sx={(theme) => ({
                '& .MuiTableCell-root': {
                  backgroundColor: theme.palette.grey[200],
                },
                '& .MuiTableCell-stickyHeader': {
                  backgroundColor: theme.palette.grey[200],
                  zIndex: 10,
                },
              })}
            >
              <TableRow sx={{ backgroundColor: (theme) => theme.palette.grey[200] }}>
                <StyledTableCell sx={{ width: 40, minWidth: 40, backgroundColor: (theme) => theme.palette.grey[200] }}>
                  <Checkbox checked={isAllSelected} indeterminate={selectedProducts.length > 0 && !isAllSelected} onChange={handleSelectAllToggle} color="primary" />
                </StyledTableCell>
                <StyledTableCell align="center" sx={{ width: 64, minWidth: 64, backgroundColor: (theme) => theme.palette.grey[200] }}>Sr. No.</StyledTableCell>
                <StyledTableCell
                  align="center"
                  sx={{
                    width: 155,
                    minWidth: { xs: 150, lg: 155 },
                    backgroundColor: (theme) => theme.palette.grey[200],
                  }}
                >
                  Order ID
                </StyledTableCell>
                <StyledTableCell
                  align="center"
                  sx={{
                    width: 150,
                    minWidth: { xs: 130, lg: 150 },
                    maxWidth: 220,
                    backgroundColor: (theme) => theme.palette.grey[200],
                  }}
                >
                  Customer Name
                </StyledTableCell>
                <StyledTableCell align="center" sx={{ width: 200, minWidth: 140, whiteSpace: "normal", backgroundColor: (theme) => theme.palette.grey[200] }}>Company Name</StyledTableCell>
                <StyledTableCell align="center" sx={{ width: 110, minWidth: { xs: 88, md: 100 }, backgroundColor: (theme) => theme.palette.grey[200] }}>Status</StyledTableCell>
                <StyledTableCell align="center" sx={{ width: 70, minWidth: { xs: 72, md: 88 }, backgroundColor: (theme) => theme.palette.grey[200] }}>Total Amount</StyledTableCell>
                <StyledTableCell align="center" sx={{ width: 180, minWidth: 180, position: "sticky", top: 0, right: 0, zIndex: 12, backgroundColor: (theme) => theme.palette.grey[200] }}>Actions</StyledTableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {filteredData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} sx={{ border: 'none' }}>
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
                      <Typography color="text.secondary">No Orders found</Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                filteredData.map((order, index) => {
                  const isSelected = isProductSelected(order.id)
                  const isCancelRequested = order?.is_cancel_requested === true
                  const isReturnRequested = order?.is_return_requested === true
                  
                  return (
                    <React.Fragment key={order.id}>
                      <StyledTableRow
                        hover
                        selected={isSelected}
                        onClick={() => openOrderDetails(order)}
                        
                        sx={
                          
                          isCancelRequested
                            ? {
                                borderLeft: "4px solid #ef4444",
                              }
                            : isReturnRequested
                              ? {
                                  borderLeft: "4px solid #3b82f6",
                                }
                              : undefined

                  
                        }
                      >
                        <StyledTableCell
                          onClick={(e) => e.stopPropagation()}
                          sx={{ width: 40, minWidth: 40 }}
                        >
                          <Checkbox checked={isSelected} onChange={(e) => handleProductSelection(order.id, e.target.checked)} color="primary" size="small" />
                        </StyledTableCell>
                        <StyledTableCell align="center" sx={{ width: 64, minWidth: 64 }}>
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>{(currentPage - 1) * pageSize + index + 1}</Typography>
                        </StyledTableCell>
                        <StyledTableCell
                          align="center"
                          variant="body2"
                          sx={{
                            width: 155,
                            minWidth: { xs: 150, lg: 155 },
                          }}
                        >
                          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5, minWidth: 0 }}>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                openOrderDetails(order);
                              }}
                              sx={{
                                p: 0.25,
                                flexShrink: 0,
                              }}
                            >
                              <KeyboardArrowRightIcon fontSize="small" />
                            </IconButton>
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <Typography
                                  fontWeight={500}
                                  sx={{
                                    fontSize: { xs: "0.75rem", md: "0.875rem" },
                                    wordBreak: "break-word",
                                    lineHeight: 1.35,
                                  }}
                                >
                                  {order.order_id}
                                </Typography>
                              </Box>
                              {isCancelRequested ? (
                                <Chip
                                  label="Cancel Requested"
                                  size="small"
                                  sx={{
                                    mt: 0.5,
                                    height: 18,
                                    fontSize: "10px",
                                    bgcolor: "#fee2e2",
                                    color: "#b91c1c",
                                    fontWeight: 600,
                                  }}
                                />
                              ) : isReturnRequested ? (
                                <Chip
                                  label="Return Requested"
                                  size="small"
                                  sx={{
                                    mt: 0.5,
                                    height: 18,
                                    fontSize: "10px",
                                    bgcolor: "#dbeafe",
                                    color: "#1d4ed8",
                                    fontWeight: 600,
                                  }}
                                />
                              ) : null}
                            </Box>
                          </Box>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: "block", mt: 0.25, wordBreak: "break-word", lineHeight: 1.35 }}
                          >
                            {order.order_date_formatted || "Date N/A"}
                          </Typography>
                        </StyledTableCell>
                        <StyledTableCell
                          align="center"
                          style={{ fontSize: "14px" }}
                          variant="body2"
                          sx={{
                            width: 150,
                            minWidth: { xs: 130, lg: 150 },
                            maxWidth: 220,
                          }}
                        >
                          <Typography variant="body2" sx={{ wordBreak: "break-word", lineHeight: 1.35 }}>
                            {order.user.full_name}
                          </Typography>
                          <Typography variant="caption" color="textSecondary" sx={{ wordBreak: "break-word", display: "block" }}>
                            {order.user.mobile_number}
                          </Typography>
                          {(order.farmer_name || getAdvancePaymentEntries(order)[0]?.order?.farmer_name) && (
                            <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: theme.palette.info.main, fontWeight: 600, fontStyle: 'italic' }}>
                              Behalf of: {order.farmer_name || getAdvancePaymentEntries(order)[0]?.order?.farmer_name}
                            </Typography>
                          )}
                        </StyledTableCell>
                        <StyledTableCell align="center" style={{ fontSize: "14px" }} variant="body2" sx={{ width: 200, minWidth: 140, whiteSpace: "normal", wordBreak: "break-word" }}>
                          <Typography variant="body2">
                            {order?.company?.name || order?.company_name || order?.company_details?.name || order?.items?.[0]?.company?.name || order?.items?.[0]?.company_name || 'N/A'}
                          </Typography>
                        </StyledTableCell>
                        <StyledTableCell align="center" sx={{ width: 110, minWidth: { xs: 88, md: 100 } }}>
                          <Box sx={{ display: "flex", justifyContent: "center" }}>
                            <StatusChip label={order.status.name} status={order.status.category?.toLowerCase()} size="small" />
                          </Box>
                        </StyledTableCell>
                        <StyledTableCell align="center" sx={{ width: 70, minWidth: { xs: 72, md: 88 } }}>
                          <Typography sx={{ fontWeight: 500, fontSize: "14px" }}>{formatCurrency(order.total_amount)}</Typography>
                        </StyledTableCell>
                        <StyledTableCell
                          align="center"
                          onClick={(e) => e.stopPropagation()}
                          sx={(theme) => ({
                            width: 180,
                            minWidth: 180,
                            position: "sticky",
                            right: 0,
                            zIndex: 4,
                            backgroundColor: theme.palette.background.paper,
                          })}
                        >
                         <Box
  display="flex"
  gap={0.5}
  sx={{
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
    flexWrap: "nowrap",
  }}
>
  {showCancelRequestActions && onApproveCancelRequest && onRejectCancelRequest ? (
    <>
      <Button size="small" color="success" variant="outlined" disabled={updatingCancelRequestOrderId === (order?.order_id ?? order?.id)} onClick={(e) => { e.stopPropagation(); onApproveCancelRequest(order); }} sx={{ textTransform: 'none', fontWeight: 600 }}>
        {updatingCancelRequestOrderId === (order?.order_id ?? order?.id) ? <CircularProgress size={18} /> : 'Accepted'}
      </Button>
      <Button size="small" color="error" variant="outlined" disabled={updatingCancelRequestOrderId === (order?.order_id ?? order?.id)} onClick={(e) => { e.stopPropagation(); onRejectCancelRequest(order); }} sx={{ textTransform: 'none', fontWeight: 600 }}>
        Rejected
      </Button>
    </>
  ) : showReturnRequestActions && onApproveReturnRequest && onRejectReturnRequest ? (
    <>
      <Button size="small" color="success" variant="outlined" disabled={updatingReturnRequestOrderId === (order?.order_id ?? order?.id)} onClick={(e) => { e.stopPropagation(); onApproveReturnRequest(order); }} sx={{ textTransform: 'none', fontWeight: 600 }}>
        {updatingReturnRequestOrderId === (order?.order_id ?? order?.id) ? <CircularProgress size={18} /> : 'Accepted'}
      </Button>
      <Button size="small" color="error" variant="outlined" disabled={updatingReturnRequestOrderId === (order?.order_id ?? order?.id)} onClick={(e) => { e.stopPropagation(); onRejectReturnRequest(order); }} sx={{ textTransform: 'none', fontWeight: 600 }}>
        Rejected
      </Button>
    </>
  ) : (
    <>
      {/* Update Status — always visible */}
      <Tooltip
        title={
          ['cancelled', 'returned'].includes((statusFilter || '').toLowerCase())
            ? 'Status update is not available on Cancelled or Returned tabs'
            : !shouldEnableButton?.('update_status')
              ? "You don't have permission to update status"
              : "Update Status"
        }
      >
        <span>
          <IconButton
            size="small"
            onClick={(e) => { e.stopPropagation(); handleOpenStatusModal(order); }}
            disabled={
              !shouldEnableButton?.('update_status') ||
              ['cancelled', 'returned'].includes((statusFilter || '').toLowerCase())
            }
            sx={{
              opacity:
                !shouldEnableButton?.('update_status') ||
                ['cancelled', 'returned'].includes((statusFilter || '').toLowerCase())
                  ? 0.5 : 1
            }}
          >
            <UpdateIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>

      {/* Update Payment — always visible */}
      <Tooltip title={getPaymentUpdateTooltip(order)}>
        <span>
          <IconButton
            size="small"
            onClick={(e) => { e.stopPropagation(); openPaymentModalForOrder(order); }}
            disabled={!shouldEnableButton?.('update_payment') || isPaymentUpdateDisabled(order)}
            sx={{
              opacity: !shouldEnableButton?.('update_payment') || isPaymentUpdateDisabled(order) ? 0.5 : 1,
            }}
          >
            <Payment fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>

      {/* Send Email — always takes up space, hidden on placed/confirmed/processing */}
      <span style={{
        visibility: ['placed', 'confirmed', 'processing'].includes((statusFilter || '').toLowerCase())
          ? 'hidden' : 'visible'
      }}>
        <Tooltip title={!shouldEnableButton?.('send_email') ? "You don't have permission to send email" : "Send Email"}>
          <span>
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); openSingleOrderEmail(order); }}
              disabled={!shouldEnableButton?.('send_email')}
              sx={{ opacity: !shouldEnableButton?.('send_email') ? 0.5 : 1 }}
            >
              <EmailIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </span>

      {/* View Invoice — always takes up space, invisible when no invoice */}
      <span style={{
        visibility: extractInvoice(order)?.path ? 'visible' : 'hidden'
      }}>
        <Tooltip title={!shouldEnableButton?.('view_invoice') ? "You don't have permission to view invoice" : "View Invoice"}>
          <span>
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); handleViewInvoice(order); }}
              disabled={!shouldEnableButton?.('view_invoice')}
              color="primary"
              sx={{ opacity: !shouldEnableButton?.('view_invoice') ? 0.5 : 1 }}
            >
              <ReceiptIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </span>

      {/* View Delivery Challan — always takes up space, invisible when no challan */}
      <span style={{
        visibility: extractDeliveryChallan(order)?.path ? 'visible' : 'hidden'
      }}>
        <Tooltip title={!shouldEnableButton?.('view_delivery_challan') ? "You don't have permission to view delivery challan" : "View Delivery Challan"}>
          <span>
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); handleViewDeliveryChallan(order); }}
              disabled={!shouldEnableButton?.('view_delivery_challan')}
              color="secondary"
              sx={{ opacity: !shouldEnableButton?.('view_delivery_challan') ? 0.5 : 1 }}
            >
              <ChallanIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </span>
    </>
  )}
</Box>
                        </StyledTableCell>
                      </StyledTableRow>
                    </React.Fragment>
                  )
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Rest of the modals remain the same - Status Update Modal, Payment Status Update Modal, etc. */}
        {/* Status Update Modal */}
        <Dialog 
          open={statusUpdateModal.open} 
          onClose={handleCloseStatusModal}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <UpdateIcon color="primary" />
              <Typography variant="h6">Update Order Status</Typography>
            </Box>
            {statusUpdateModal.order && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Order ID: {statusUpdateModal.order.order_id}
              </Typography>
            )}
          </DialogTitle>
          
          <DialogContent
          sx={{ /* Firefox */
            scrollbarWidth: "thin",
            scrollbarColor:
              theme.palette.mode === "dark"
                ? "#555 #1e1e1e"
                : "#c1c1c1 #f1f1f1",
            /* Chrome, Edge, Safari */
            "&::-webkit-scrollbar": {
              width: 8,
            },
            "&::-webkit-scrollbar-track": {
              background:
                theme.palette.mode === "dark" ? "#1e1e1e" : "#f1f1f1",
              borderRadius: 8,
            },
            "&::-webkit-scrollbar-thumb": {
              background:
                theme.palette.mode === "dark" ? "#555" : "#c1c1c1",
              borderRadius: 8,
            },
            "&::-webkit-scrollbar-thumb:hover": {
              background:
                theme.palette.mode === "dark" ? "#777" : "#a8a8a8",
            },
            }}>
            {statusUpdateModal.challanError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {statusUpdateModal.challanError}
              </Alert>
            )}

            {statusUpdateModal.isCreatingChallan && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} />
                  Creating delivery challan...
                </Box>
              </Alert>
            )}

          {/* Payment Status Warning */}
          {(() => {
            const paymentStatus = (statusUpdateModal.order?.payment_status?.name || statusUpdateModal.order?.payment_status || '').toLowerCase();
            const isPaid = isPaymentSatisfiedForOrderAdvance(paymentStatus);

            const selectedStatus = (statusUpdateModal.order?.status?.name || statusUpdateModal.order?.status_name || '').toLowerCase();
            const originalStatusName = (statusUpdateModal.originalStatus?.name || '').toLowerCase();
            const originalStatusCategory = (statusUpdateModal.originalStatus?.category || '').toLowerCase();
            const isOriginallyDelivered = originalStatusName === 'delivered' || originalStatusCategory === 'delivered';
            const isReturnedSelected = selectedStatus === 'returned';

            // Show payment warning for unpaid orders, but NOT when user has selected
            // Returned (in that case we rely on the Returned-specific validation message).
            if (!isPaid && selectedStatus !== 'returned' && !(isOriginallyDelivered && isReturnedSelected)) {
              return (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <Typography variant="body2" fontWeight={600}>
                    Payment Required
                  </Typography>
                  <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                    This order must be paid before it can be moved to any status other than "Orders". 
                    Please update the payment status first.
                  </Typography>
                  {/* <Button
                    size="small"
                    variant="outlined"
                    sx={{ mt: 1.5, textTransform: 'none' }}
                    startIcon={<Payment fontSize="small" />}
                    onClick={() => openPaidPaymentFlowForOrder(statusUpdateModal.order)}
                    disabled={!shouldEnableButton?.('update_payment')}
                  >
                    Update Payment
                  </Button> */}
                </Alert>
              );
            }
            return null;
          })()}

          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Order Status</InputLabel>
            <Select
              value={
                statusUpdateModal.order?.status?.name || statusUpdateModal.order?.status_name || ''
              }
              label="Order Status"
              onChange={(e) => {
                const selectedOption = statusOptions.find(opt => opt.name === e.target.value);
                handleStatusModalChange('order', { 
                  ...statusUpdateModal.order, 
                  status: {
                    name: e.target.value,
                    category: selectedOption?.category || ''
                  },
                  status_name: e.target.value
                });
              }}
              disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
            >
              {statusOptions
                .map((option) => {
                // Use original status for validation, not the auto-selected next status
                // This ensures we only show the next sequential status, not skip ahead
                const originalStatusCategory = (statusUpdateModal.originalStatus?.category || '').toLowerCase();
                const originalStatusName = (statusUpdateModal.originalStatus?.name || '').toLowerCase();
                
                // Get current status category and name - try multiple sources (for display)
                let currentStatusCategory = statusUpdateModal.order?.status?.category?.toLowerCase();
                const currentStatusName = statusUpdateModal.order?.status?.name || statusUpdateModal.order?.status_name;
                
                // If category not available, find it from statusOptions by name
                if (!currentStatusCategory && currentStatusName) {
                  const currentStatusOption = statusOptions.find(opt => opt.name === currentStatusName);
                  currentStatusCategory = currentStatusOption?.category?.toLowerCase() || '';
                }
                
                // Get statuses to disable based on ORIGINAL status (not the auto-selected one)
                const statusesToDisable = getStatusesToDisable(originalStatusCategory, originalStatusName);
                
                // Check payment status
                const paymentStatus = (statusUpdateModal.order?.payment_status?.name || statusUpdateModal.order?.payment_status || '').toLowerCase();
                const isPaid = isPaymentSatisfiedForOrderAdvance(paymentStatus);
                
                // If not paid, only allow "Orders" status (name: "Orders"), except Cancelled
                // from the Placed-tab flow (allowUnpaidCancelFromPlaced).
                const isOrdersStatus = option.name === 'Orders';
                const isCancelledOption = (option.name || '').toLowerCase() === 'cancelled';
                const shouldDisableByPayment =
                  !isPaid &&
                  !isOrdersStatus &&
                  !(statusUpdateModal.allowUnpaidCancelFromPlaced && isCancelledOption);
                
                // Check if this option should be disabled
                const optionCategory = (option.category || '').toLowerCase();
                // Only disable if category is valid and in the disabled list
                const shouldDisableBySequence = optionCategory && statusesToDisable.includes(optionCategory);
                const allowPlacedTabCancelledPick =
                  statusUpdateModal.allowUnpaidCancelFromPlaced && isCancelledOption;
                const shouldDisable =
                  (shouldDisableByPayment || shouldDisableBySequence) && !allowPlacedTabCancelledPick;
                
                return (
                  <MenuItem 
                    key={option.id || option.name} 
                    value={option.name}
                    disabled={shouldDisable}
                    sx={shouldDisable ? { 
                      opacity: 0.5,
                      backgroundColor: theme.palette.grey[100],
                      // '&:hover': {
                      //   backgroundColor: theme.palette.grey[100],
                      //   cursor: 'not-allowed'
                      // }
                    } : {}}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                      {getIconForCategory(option.category)}
                      <StatusChip 
                        status={option.category?.toLowerCase()}
                        label={option.name}
                        size="small"
                      />
                      {!shouldDisable && statusUpdateModal.order?.status?.name === option.name && (
                        <CheckCircle 
                          color="primary" 
                          sx={{ 
                            ml: 'auto', 
                            fontSize: '16px' 
                          }} 
                        />
                      )}
                      {shouldDisableByPayment && (
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', fontSize: '0.7rem' }}>
                          Payment required
                        </Typography>
                      )}
                      {shouldDisableBySequence && !shouldDisableByPayment && (
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', fontSize: '0.7rem' }}>
                          Sequential only
                        </Typography>
                      )}
                    </Box>
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>

          

          {/* Conditional reason field for Cancelled status */}
          {(statusUpdateModal.order?.status?.name === 'Cancelled' || statusUpdateModal.order?.status_name === 'Cancelled') && (
            <TextField
              fullWidth
              label="Reason"
              value={statusUpdateModal.cancellationReason}
              onChange={(e) => handleStatusModalChange('cancellationReason', e.target.value)}
              disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
              required
              placeholder="Enter reason for cancellation"
              sx={{ mt: 2 }}
              multiline
              rows={3}
            />
          )}

          {/* Conditional reason field for Returned status */}
          {(statusUpdateModal.order?.status?.name === 'Returned' || statusUpdateModal.order?.status_name === 'Returned') && (
            <TextField
              fullWidth
              label="Reason"
              value={statusUpdateModal.returnReason}
              onChange={(e) => handleStatusModalChange('returnReason', e.target.value)}
              disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
              required
              placeholder="Enter reason for returned"
              sx={{ mt: 2 }}
              multiline
              rows={3}

              
            />
          )}

            {/* Conditional fields for shipping */}
            {(statusUpdateModal.order?.status?.category || statusUpdateModal.order?.status)?.toLowerCase() === 'shipped' && (
              <Box sx={{ mt: 3 }}>
                <Box sx={{ 
                  p: 2, 
                  backgroundColor: alpha(theme.palette.info.main, 0.1),
                  borderRadius: 2,
                  mb: 2
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <DeliveryIcon color="info" />
                    <Typography variant="subtitle1" fontWeight={600}>
                      Delivery Information Required
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Please provide delivery details to create the delivery challan
                  </Typography>
                </Box>

                <Grid container spacing={2}>

                    <Grid item xs={12}>
                    <FormControl fullWidth required>
                      <InputLabel>Order By</InputLabel>
                      <Select
                        value={statusUpdateModal.orderBy}
                        onChange={(e) => handleStatusModalChange('orderBy', e.target.value)}
                        disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
                        label="Order By"
                      >
                        <MenuItem value="">
                          <em>Select order by</em>
                        </MenuItem>
                        {orderByChoices.map((choice) => (
                          <MenuItem key={choice.key} value={choice.key}>
                            {choice.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>


                {/* Entity Name */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Delivery Partner Name"
                    value={statusUpdateModal.entityName}
                    onChange={(e) => {
                      const lettersOnly = e.target.value.replace(/[^A-Za-z ]/g, '');
                      handleStatusModalChange('entityName', lettersOnly);
                    }}
                    disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
                    required
                    placeholder="Enter Delivery Partner"
                  />
                </Grid>

              
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Driver/Authorized Name"
                    value={statusUpdateModal.driverName}
                    onChange={(e) => {
                      const lettersOnly = e.target.value.replace(/[^A-Za-z ]/g, '');
                      handleStatusModalChange('driverName', lettersOnly);
                    }}
                    disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
                    required
                    placeholder="Driver/Authorized Name"
                    // error={statusUpdateModal.challanError.includes('Driver name')}
                  />
                </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Vehicle No / Reference No"
                      value={statusUpdateModal.vehicleNo}
                      onChange={(e) => {
                        const isTruckOrderBy = String(statusUpdateModal.orderBy || '').trim().toLowerCase() === 'truck';
                        handleStatusModalChange(
                          'vehicleNo',
                          isTruckOrderBy ? formatVehicleReferenceNo(e.target.value) : e.target.value
                        );
                      }}
                      disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
                      required={String(statusUpdateModal.orderBy || '').trim().toLowerCase() === 'truck'}
                      placeholder="Vehicle No / Reference No"
                      inputProps={
                        String(statusUpdateModal.orderBy || '').trim().toLowerCase() === 'truck'
                          ? { maxLength: 13 }
                          : undefined
                      }
                      error={
                        (String(statusUpdateModal.orderBy || '').trim().toLowerCase() === 'truck' &&
                          isVehicleReferenceNoInvalid(statusUpdateModal.vehicleNo)) ||
                        (statusUpdateModal.challanError && statusUpdateModal.challanError.includes('vehicle number'))
                      }
                      helperText={
                        String(statusUpdateModal.orderBy || '').trim().toLowerCase() === 'truck' &&
                        isVehicleReferenceNoInvalid(statusUpdateModal.vehicleNo)
                          ? VEHICLE_REFERENCE_NO_ERROR
                          : ''
                      }
                    />
                  </Grid>

                  {/* Start KM - only show when Order By is "Truck" */}
                  {String(statusUpdateModal.orderBy || '').trim().toLowerCase() === 'truck' && (
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Start KM"
                        type="number"
                        // required={(statusFilter || '').toLowerCase() === 'processing'}
                        value={statusUpdateModal.start_km}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === '' || /^[1-9]\d*$/.test(value)) {
                            handleStatusModalChange('start_km', value);
                          }
                        }}
                        onKeyDown={(e) => {
                          if (['e', 'E', '+', '-', '.'].includes(e.key)) {
                            e.preventDefault();
                          }
                        }}
                        onWheel={(e) => e.currentTarget.blur()}
                        disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
                        required={(statusFilter || '').toLowerCase() === 'processing'}
                        placeholder="Enter starting kilometer reading"
                        inputProps={{ min: 1, step: 1 }}
                      />
                    </Grid>
                  )}

                  <Grid item xs={12}>
                    <Box>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={statusUpdateModal.useCustomDispatchPerson}
                            onChange={(e) => handleStatusModalChange('useCustomDispatchPerson', e.target.checked)}
                            disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
                          />
                        }
                        label="Add custom dispatch person"
                      />
                      {statusUpdateModal.useCustomDispatchPerson ? (
                        <TextField
                          fullWidth
                          label="Custom Dispatch Person Name"
                          value={statusUpdateModal.customDispatchPerson}
                          onChange={(e) => handleStatusModalChange('customDispatchPerson', e.target.value)}
                          disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
                          required
                          placeholder="Enter custom dispatch person name"
                          sx={{ mt: 1 }}
                        />
                      ) : (
                        <FormControl fullWidth required sx={{ mt: 1 }}>
                          <InputLabel>Dispatch Person Name</InputLabel>
                          <Select
                            value={statusUpdateModal.dispatchPersonName}
                            label="Dispatch Person Name"
                            onChange={(e) => handleStatusModalChange('dispatchPersonName', e.target.value)}
                            disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
                          >
                            <MenuItem value="">
                              <em>Select dispatch person</em>
                            </MenuItem>
                            {dispatchPersons.map((person) => (
                              <MenuItem key={person.id} value={person.name}>
                                {person.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                    </Box>
                  </Grid>
                    <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Delivery Date"
                    type="date"
                    value={statusUpdateModal.shippedDate}
                    onChange={(e) => handleStatusModalChange('shippedDate', e.target.value)}
                    disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
                    required
                    InputLabelProps={{ shrink: true }}
                    inputProps={{
                      min: new Date().toISOString().split("T")[0], // 🔑 disables all past dates
                    }}
                  />
                </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={statusUpdateModal.generateInvoice}
                          onChange={(e) => handleStatusModalChange('generateInvoice', e.target.checked)}
                          disabled={
                            updatingStatus ||
                            statusUpdateModal.isCreatingChallan ||
                            (['processing'].includes((statusFilter || '').toLowerCase()) &&
                              !shouldEnableButton?.('generate_invoice_along_with_delivery_challan'))
                          }
                        />
                      }
                      label="Generate Invoice along with Delivery Challan"
                    />
                  </Grid>
                </Grid>

                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  * A delivery challan will be created automatically with these details
                  {statusUpdateModal.generateInvoice && (
                    <><br />* An invoice will also be generated for this order.</>
                  )}
                </Typography>
              </Box>
            )}

            {/* End KM - shown when Start KM is present (optional) */}
            {/* {(String(statusUpdateModal.start_km || statusUpdateModal.order?.delivery_challan?.start_km || '').trim() !== '') && (
              <Box sx={{ mt: 3 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="End KMss"
                      type="number"
                      value={statusUpdateModal.end_km}
                      onChange={(e) => {
                        const endKmValue = e.target.value;
                        handleStatusModalChange('end_km', endKmValue);
                        // Validate End KM > Start KM
                        const startKm = parseFloat(statusUpdateModal.start_km);
                        const endKm = parseFloat(endKmValue);
                        if (!isNaN(startKm) && !isNaN(endKm) && endKm <= startKm) {
                          // Error will be shown via button disabled state and alert on submit
                        }
                      }}
                      disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
                      placeholder="Enter ending kilometer reading"
                      error={(() => {
                        const startKmFromModal = statusUpdateModal.start_km;
                        const startKmFromOrder = statusUpdateModal.order?.delivery_challan?.start_km;
                        const startKm = parseFloat(startKmFromModal || startKmFromOrder);
                        const endKm = parseFloat(statusUpdateModal.end_km);
                        return !isNaN(startKm) && !isNaN(endKm) && endKm <= startKm;
                      })()}
                      helperText={(() => {
                        const startKmFromModal = statusUpdateModal.start_km;
                        const startKmFromOrder = statusUpdateModal.order?.delivery_challan?.start_km;
                        const startKm = parseFloat(startKmFromModal || startKmFromOrder);
                        const endKm = parseFloat(statusUpdateModal.end_km);
                        if (!isNaN(startKm) && !isNaN(endKm) && endKm <= startKm) {
                          return 'End KM must be greater than Start KM';
                        }
                        return '';
                      })()}
                    />
                  </Grid>
                </Grid>
              </Box>
            )} */}

            {(() => {
              const order = statusUpdateModal.order;
              const orderChallan = getOrderChallan(order);
              const isDeliveredSelected =
                (
                  order?.status?.name ||
                  order?.status_name ||
                  ''
                ).toLowerCase() === 'delivered';
              const isDispatchTab = ['dispatch', 'shipped'].includes((statusFilter || '').toLowerCase());

              if (!isDeliveredSelected) return null;

              if (isDispatchTab) {
                if (!isOrderByTruck(order)) return null;

                const startKmValue =
                  statusUpdateModal.start_km ||
                  (
                    orderChallan?.start_km !== null && orderChallan?.start_km !== undefined
                      ? String(orderChallan.start_km)
                      : ''
                  );

                if (!String(startKmValue).trim()) return null;

                const driverName =
                  statusUpdateModal.driverName ||
                  orderChallan?.driver_name ||
                  '';
                const vehicleNo =
                  statusUpdateModal.vehicleNo ||
                  orderChallan?.vehicle_number ||
                  orderChallan?.vehicle_no ||
                  orderChallan?.vehicle_name ||
                  '';

                return (
                  <Box sx={{ mt: 3 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Driver Name"
                          value={driverName}
                          disabled
                          InputProps={{ readOnly: true }}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Vehicle No / Reference No"
                          value={vehicleNo}
                          disabled
                          InputProps={{ readOnly: true }}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Start KM"
                          value={startKmValue}
                          disabled
                          InputProps={{ readOnly: true }}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="End KM"
                          type="number"
                          value={statusUpdateModal.end_km}
                          onChange={(e) => {
                            handleStatusModalChange('end_km', e.target.value);
                          }}
                          disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
                          placeholder="Enter ending kilometer reading"
                          error={(() => {
                            const startKm = parseFloat(startKmValue);
                            const endKm = parseFloat(statusUpdateModal.end_km);
                            const hasEndKm = String(statusUpdateModal.end_km || '').trim() !== '';
                            return !hasEndKm || (!isNaN(startKm) && !isNaN(endKm) && endKm <= startKm);
                          })()}
                          helperText={(() => {
                            const startKm = parseFloat(startKmValue);
                            const endKm = parseFloat(statusUpdateModal.end_km);
                            const hasEndKm = String(statusUpdateModal.end_km || '').trim() !== '';
                            if (!hasEndKm) {
                              return 'End KM is required';
                            }
                            if (!isNaN(startKm) && !isNaN(endKm) && endKm <= startKm) {
                              return 'End KM must be greater than Start KM';
                            }
                            return '';
                          })()}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                );
              }

              const hasStartKm =
                String(statusUpdateModal.start_km || orderChallan?.start_km || '').trim() !== '';
              const isTruckOrder = isOrderByTruck(order);

              if (!hasStartKm || !isDeliveredSelected) return null;

              return (
                <Box sx={{ mt: 3 }}>
                  <Grid container spacing={2}>
                    {isTruckOrder && (
                      <>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Driver Name"
                            value={
                              statusUpdateModal.driverName ||
                              orderChallan?.driver_name ||
                              ''
                            }
                            disabled
                            InputProps={{ readOnly: true }}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Vehicle No / Reference No"
                            value={
                              statusUpdateModal.vehicleNo ||
                              orderChallan?.vehicle_number ||
                              orderChallan?.vehicle_no ||
                              orderChallan?.vehicle_name ||
                              ''
                            }
                            disabled
                            InputProps={{ readOnly: true }}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Start KM"
                            value={
                              statusUpdateModal.start_km ||
                              orderChallan?.start_km ||
                              ''
                            }
                            disabled
                            InputProps={{ readOnly: true }}
                          />
                        </Grid>
                      </>
                    )}
                    <Grid item xs={12} md={isTruckOrder ? 6 : 12}>
                      <TextField
                        fullWidth
                        label="End KM"
                        type="number"
                        value={statusUpdateModal.end_km}
                        onChange={(e) => {
                          handleStatusModalChange('end_km', e.target.value);
                        }}
                        disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
                        placeholder="Enter ending kilometer reading"
                        error={(() => {
                          const startKm = parseFloat(
                            statusUpdateModal.start_km || orderChallan?.start_km
                          );
                          const endKm = parseFloat(statusUpdateModal.end_km);
                          const hasEndKm = String(statusUpdateModal.end_km || '').trim() !== '';
                          return !hasEndKm || (!isNaN(startKm) && !isNaN(endKm) && endKm <= startKm);
                        })()}
                        helperText={(() => {
                          const startKm = parseFloat(
                            statusUpdateModal.start_km || orderChallan?.start_km
                          );
                          const endKm = parseFloat(statusUpdateModal.end_km);
                          const hasEndKm = String(statusUpdateModal.end_km || '').trim() !== '';
                          if (!hasEndKm) {
                            return 'End KM is required';
                          }
                          if (!isNaN(startKm) && !isNaN(endKm) && endKm <= startKm) {
                            return 'End KM must be greater than Start KM';
                          }
                          return '';
                        })()}
                      />
                    </Grid>
                  </Grid>
                </Box>
              );
            })()}

          </DialogContent>
          
          <DialogActions>
            <Button 
              onClick={handleCloseStatusModal}
              disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
            >
              Cancel
            </Button>
            <Button 
              onClick={() => handleStatusUpdate(
                statusUpdateModal.order?.id,
                statusUpdateModal.order?.status?.name || statusUpdateModal.order?.status_name,
                statusUpdateModal.order?.order_id
              )}
              variant="contained"
              disabled={isSingleStatusSubmitDisabled()}
              startIcon={
                updatingStatus || statusUpdateModal.isCreatingChallan ? 
                <CircularProgress size={16} /> : 
                (statusUpdateModal.order?.status?.category || '').toLowerCase() === 'shipped' ? <DeliveryIcon /> : <UpdateIcon />
              }
            >
              {updatingStatus ? 'Updating Status...' : 
              statusUpdateModal.isCreatingChallan ? 'Creating Challan...' :
              (statusUpdateModal.order?.status?.category || '').toLowerCase() === 'shipped' ? 'Ship Order' : 'Update Status'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Payment Status Update Modal */}
        <Dialog 
          open={paymentUpdateModal.open} 
          onClose={() => {
            if (updatingPayment) return;
            setPaymentUpdateModal(closePaymentUpdateModalState());
            setPaymentStatusChoices([]);
          }}
          maxWidth={paymentUpdateModal.step === 'payment_details' ? false : 'sm'}
          fullWidth={paymentUpdateModal.step !== 'payment_details'}
          scroll={paymentUpdateModal.step === 'payment_details' ? 'paper' : 'body'}
          PaperProps={
            paymentUpdateModal.step === 'payment_details'
              ? PAYMENT_DETAILS_DIALOG_PAPER_PROPS
              : undefined
          }
        >
          <DialogTitle>
            {paymentUpdateModal.step === 'payment_option'
              ? 'Payment Option'
              : paymentUpdateModal.step === 'payment_details'
                ? 'Payment Details'
                : 'Update Payment Status'}
            {paymentUpdateModal.order && (
              <Typography variant="body2" color="text.secondary">
                Order ID: {paymentUpdateModal.order.order_id}
              </Typography>
            )}
          </DialogTitle>
          <DialogContent
            sx={
              paymentUpdateModal.step === 'payment_details'
                ? PAYMENT_DETAILS_DIALOG_CONTENT_SX
                : undefined
            }
          >
            {paymentUpdateModal.paymentError && (
              <Alert severity="error" sx={{ mt: 1, mb: 1 }}>
                {paymentUpdateModal.paymentError}
              </Alert>
            )}

            {paymentUpdateModal.step === 'payment_option' && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Choose your payment method:
                </Typography>
                <RadioGroup
                  value={paymentUpdateModal.paymentOption}
                  onChange={(e) =>
                    setPaymentUpdateModal((prev) => ({
                      ...prev,
                      paymentOption: e.target.value,
                      paymentError: '',
                      ...(e.target.value === 'full'
                        ? { advanceAmount: String(prev.order?.total_amount ?? prev.order?.total ?? '') }
                        : { advanceAmount: '' }),
                    }))
                  }
                >
                  <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5, borderRadius: 2 }}>
                    <FormControlLabel
                      value="full"
                      control={<Radio />}
                      label="Order with full Payment"
                    />
                  </Paper>
                  {/* <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                    <FormControlLabel
                      value="advance"
                      control={<Radio />}
                      label="Order with Advanced Payment"
                    />
                  </Paper> */}
                </RadioGroup>
              </Box>
            )}

            {paymentUpdateModal.step === 'status' && (
            <>
            {isPaymentUpdateDisabled(paymentUpdateModal.order) && (
              <Alert severity="info" sx={{ mt: 2 }}>
                {getPaymentUpdateDisabledReason(paymentUpdateModal.order, statusFilter)}
              </Alert>
            )}
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>Payment Status</InputLabel>
              <Select
                value={getOrderPaymentStatusValue(paymentUpdateModal.order)}
                label="Payment Status"
                disabled={
                  loadingPaymentStatusChoices ||
                  updatingPayment ||
                  isPaymentUpdateDisabled({
                    ...paymentUpdateModal.order,
                    payment_status: {
                      name: paymentUpdateModal.initialPaymentStatus || '',
                    },
                  })
                }
                MenuProps={{
                  disableScrollLock: true,
                  PaperProps: {
                    sx: { maxHeight: 300 },
                  },
                  sx: {
                    zIndex: (theme) => theme.zIndex.modal + 1,
                  },
                }}
                onChange={(e) => {
                  const newStatus = e.target.value;
                  if (newStatus === 'paid') {
                    loadAdvancePaymentMethods();
                  }
                  setPaymentUpdateModal(prev => ({
                    ...prev,
                    order: { 
                      ...prev.order, 
                      payment_status: { 
                        ...(typeof prev.order?.payment_status === 'object' &&
                        prev.order?.payment_status !== null
                          ? prev.order.payment_status
                          : {}),
                        name: newStatus,
                      }
                    },
                    refundAmount: newStatus === 'refunded' ? (prev.order?.total_amount ?? prev.refundAmount ?? '') : prev.refundAmount,
                    paymentError: '',
                    ...(newStatus === 'paid'
                      ? getInlinePaidPaymentStepState(prev.orderPaymentDetails)
                      : newStatus === 'credit'
                        ? getCreditPaymentStepState(prev.orderPaymentDetails)
                        : {
                            step: 'status',
                            paymentOption: '',
                            advanceAmount: '',
                            installmentAmount: '',
                            creditNote: '',
                            paymentMethod: '',
                            paymentMethodConfig: null,
                            paymentFields: {},
                            paymentFiles: {},
                          }),
                  }));
                }}
              >
                {renderPaymentStatusMenuItems()}
              </Select>
            </FormControl>
            {(paymentUpdateModal.paymentOption === 'credit' ||
              paymentUpdateModal.paymentOption === 'remaining') && (
              <Box sx={{ mt: 2 }}>
                <PaymentDetailsStepContent
                  idPrefix="single-payment-inline"
                  order={paymentUpdateModal.order}
                  orderPaymentDetails={paymentUpdateModal.orderPaymentDetails}
                  paymentOption={paymentUpdateModal.paymentOption}
                  advanceAmount={paymentUpdateModal.advanceAmount}
                  onAdvanceAmountChange={(e) =>
                    setPaymentUpdateModal((prev) => ({
                      ...prev,
                      advanceAmount: e.target.value,
                      paymentError: '',
                    }))
                  }
                  installmentAmount={paymentUpdateModal.installmentAmount || ''}
                  onInstallmentAmountChange={(e) => {
                    const value = e.target.value;
                    const remaining = Number(
                      paymentUpdateModal.orderPaymentDetails?.remainingAmount ?? 0
                    );
                    if (value !== '' && Number(value) > remaining) {
                      setPaymentUpdateModal((prev) => ({
                        ...prev,
                        paymentError: 'Installment amount cannot exceed the remaining amount',
                      }));
                      return;
                    }
                    setPaymentUpdateModal((prev) => ({
                      ...prev,
                      installmentAmount: value,
                      paymentError: '',
                      ...(prev.paymentOption === 'credit'
                        ? {
                            paymentFields: getCreditChequePaymentFields(
                              prev.paymentFields,
                              value,
                              prev.paymentMethodConfig
                            ),
                          }
                        : {}),
                    }));
                  }}
                  creditNote={paymentUpdateModal.creditNote || ''}
                  onCreditNoteChange={(e) =>
                    setPaymentUpdateModal((prev) => ({
                      ...prev,
                      creditNote: e.target.value,
                      paymentError: '',
                    }))
                  }
                  paymentMethod={paymentUpdateModal.paymentMethod}
                  paymentMethodConfig={paymentUpdateModal.paymentMethodConfig}
                  advancePaymentMethods={advancePaymentMethods}
                  loadingAdvancePaymentMethods={loadingAdvancePaymentMethods}
                  onPaymentMethodChange={(e) => {
                    const methodId = e.target.value;
                    setPaymentUpdateModal((prev) => {
                      const payload = buildPaymentMethodChangePayload(
                        methodId,
                        prev.orderPaymentDetails
                      );
                      if (prev.paymentOption === 'credit' && payload.paymentMethodConfig?.type === 'cheque') {
                        payload.paymentFields = getCreditChequePaymentFields(
                          payload.paymentFields,
                          prev.installmentAmount ?? '',
                          payload.paymentMethodConfig
                        );
                      }
                      return { ...prev, ...payload };
                    });
                  }}
                  paymentFields={paymentUpdateModal.paymentFields}
                  paymentFiles={paymentUpdateModal.paymentFiles}
                  onFieldChange={(key, value) => {
                    if (key === 'cheque_amount' && paymentUpdateModal.orderPaymentDetails) {
                      return;
                    }
                    setPaymentUpdateModal((prev) => ({
                      ...prev,
                      paymentFields: { ...prev.paymentFields, [key]: value },
                      paymentError: '',
                    }));
                  }}
                  onFileChange={(key, file) =>
                    setPaymentUpdateModal((prev) => ({
                      ...prev,
                      paymentFiles: { ...prev.paymentFiles, [key]: file },
                      paymentError: '',
                    }))
                  }
                />
              </Box>
            )}
            {['failed', 'pending', 'outstanding'].includes(
              getOrderPaymentStatusValue(paymentUpdateModal.order)
            ) && (
              <Box sx={{ mt: 2 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Note"
                  value={paymentUpdateModal.creditNote || ''}
                  onChange={(e) =>
                    setPaymentUpdateModal((prev) => ({
                      ...prev,
                      creditNote: e.target.value,
                      paymentError: '',
                    }))
                  }
                  placeholder="Enter note"
                  multiline
                  minRows={3}
                  required
                />
              </Box>
            )}
            {(paymentUpdateModal.order?.payment_status?.name || paymentUpdateModal.order?.payment_status || '')
              .toLowerCase() === 'refunded' && (() => {
                const apiDetails = paymentUpdateModal.orderPaymentDetails;
                const totalAmount = Number(
                  apiDetails?.totalAmount ??
                    paymentUpdateModal.order?.total_amount ??
                    paymentUpdateModal.order?.total ??
                    0
                );
                const advancePaidTotal = getAdvancePaymentEntries(
                  paymentUpdateModal.order || paymentUpdateModal
                ).reduce((sum, entry) => sum + Number(entry.advance_amount || 0), 0);
                const paidAmountFallback =
                  advancePaidTotal > 0
                    ? advancePaidTotal
                    : (paymentUpdateModal.order?.amount_paid ??
                        paymentUpdateModal.order?.paid ??
                        0);
                const paidAmount = Number(
                  apiDetails?.paidAmount ?? paidAmountFallback
                );
                const pendingAmount = Number(
                  apiDetails?.remainingAmount ?? Math.max(0, totalAmount - paidAmount)
                );
                const maxAmount = paidAmount;
                const refundAmount = Number(paymentUpdateModal.refundAmount || 0);
                const isExceeding =
                  paymentUpdateModal.refundAmount !== '' &&
                  (!Number.isFinite(refundAmount) || refundAmount <= 0 || refundAmount > maxAmount);
                const formatAmount = (n) =>
                  Number.isNaN(n) ? '0.00' : n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

                return (
                  <Box sx={{ mt: 2 }}>
                    {
                      (() => {
                        const advanceAmount = paidAmount;
                        const remainingAdvance = pendingAmount;

                    //     return (
                    //       <Box sx={{ mb: 1, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    //          <TextField
                    //   fullWidth
                    //   label="Refund Amount"
                    //   type="number"
                    //   value={paymentUpdateModal.refundAmount ?? ''}
                    //   placeholder="Enter refund amount"
                    //   inputProps={{
                    //     min: 0,
                    //     max: maxAmount,
                    //     step: 0.01,
                    //   }}
                    //   error={isExceeding}
                    //   helperText={
                    //     isExceeding
                    //       ? "You can’t enter more than the actual amount"
                    //       : ''
                    //   }
                    //   onChange={(e) =>
                    //     setPaymentUpdateModal((prev) => ({
                    //       ...prev,
                    //       refundAmount: e.target.value,
                    //     }))
                    //   }
                    // />
                    //         <Typography variant="body2" color="text.secondary">
                    //           Total: ₹{formatAmount(totalAmount)}
                    //         </Typography>
                    //         {/* <Typography variant="body2" color="text.secondary">
                    //           Paid: ₹{formatAmount(paidAmount)}
                    //         </Typography>
                    //         <Typography variant="body2" color="text.secondary">
                    //           Pending: ₹{formatAmount(pendingAmount)}
                    //         </Typography> */}
                    //         <Typography variant="body2" color="text.secondary">
                    //           Paid: ₹{formatAmount(advanceAmount)}
                    //         </Typography>
                    //         <Typography variant="body2" color="text.secondary">
                    //           Pending: ₹{formatAmount(remainingAdvance)}
                    //         </Typography>
                    //       </Box>
                    //     );
                    return (
                      <Box sx={{ mb: 1, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <TextField
                  fullWidth
                  label="Refund Amount"
                  type="number"
                  value={paymentUpdateModal.refundAmount ?? ''}
                  placeholder="Enter refund amount"
                  inputProps={{
                    min: 0,
                    max: maxAmount,
                    step: 0.01,
                  }}
                  error={isExceeding}
                  helperText={
                    isExceeding
                      ? 'Refund amount cannot exceed the available paid amount'
                      : ''
                  }
                  onChange={(e) =>
                    setPaymentUpdateModal((prev) => ({
                      ...prev,
                      refundAmount: e.target.value,
                    }))
                  }
                />
                        <Box display="flex" gap={2} mt={2}>
                      <TextField
                        label="Total"
                      value={formatAmount(totalAmount)}
                        size="small"
                        fullWidth
                          disabled
                        />

                        <TextField
                        label="Paid"
                      value={formatAmount(advanceAmount)}
                      size="small"
                      fullWidth
                      disabled
                      />

                    <TextField
                      label="Pending"
                      value={formatAmount(remainingAdvance)}
                        size="small"
                      fullWidth
                      disabled
                        />
                      </Box>
                        <TextField
                          fullWidth
                          size="small"
                          label="Note"
                          value={paymentUpdateModal.creditNote || ''}
                          onChange={(e) =>
                            setPaymentUpdateModal((prev) => ({
                              ...prev,
                              creditNote: e.target.value,
                              paymentError: '',
                            }))
                          }
                          placeholder="Enter note"
                          multiline
                          minRows={3}
                          required
                          sx={{ mt: 2 }}
                        />

                  </Box>
                    );
                  })()
                    }

                  
                  </Box>
                );
              })()
            }
            </>
            )}


          </DialogContent>
          <DialogActions
            sx={
              paymentUpdateModal.step === 'payment_details'
                ? PAYMENT_DETAILS_DIALOG_ACTIONS_SX
                : undefined
            }
          >
            <Button 
              onClick={() => {
                if (paymentUpdateModal.step === 'payment_details') {
                  const previousStatus = (
                    paymentUpdateModal.orderPaymentDetails?.current_payment_status ||
                    paymentUpdateModal.order?.payment_status?.name ||
                    paymentUpdateModal.order?.payment_status ||
                    'pending'
                  )
                    .toString()
                    .toLowerCase();
                  setPaymentUpdateModal((prev) => ({
                    ...prev,
                    step: 'status',
                    paymentOption: '',
                    advanceAmount: '',
                    installmentAmount: '',
                    creditNote: '',
                    paymentMethod: '',
                    paymentFields: {},
                    paymentFiles: {},
                    paymentError: '',
                    order: {
                      ...prev.order,
                      payment_status: {
                        ...prev.order?.payment_status,
                        name: previousStatus,
                      },
                    },
                  }));
                  return;
                }
                setPaymentUpdateModal(closePaymentUpdateModalState());
                setPaymentStatusChoices([]);
              }}
              disabled={updatingPayment}
            >
              {paymentUpdateModal.step === 'status' ? 'Cancel' : 'Back'}
            </Button>

            {paymentUpdateModal.paymentOption === 'credit' &&
              shouldShowCreditSubmitButton(paymentUpdateModal) && (
              <Button
                variant="contained"
                disabled={updatingPayment || !canSubmitCreditPayment(paymentUpdateModal)}
                onClick={handleConfirmCreditPaymentDetails}
                startIcon={updatingPayment ? <CircularProgress size={16} /> : <UpdateIcon />}
              >
                {updatingPayment ? 'Updating...' : 'Submit Payment'}
              </Button>
            )}

            {paymentUpdateModal.paymentOption === 'remaining' && (
              <Button
                variant="contained"
                disabled={updatingPayment || !canSubmitPaidPayment(paymentUpdateModal)}
                onClick={handleConfirmPaidPaymentDetails}
                startIcon={updatingPayment ? <CircularProgress size={16} /> : <UpdateIcon />}
              >
                {updatingPayment ? 'Updating...' : 'Submit Payment'}
              </Button>
            )}

            {paymentUpdateModal.step === 'status' &&
              getOrderPaymentStatusValue(paymentUpdateModal.order) !== 'paid' &&
              getOrderPaymentStatusValue(paymentUpdateModal.order) !== 'credit' && (
              <Button 
                onClick={() => handlePaymentStatusUpdate(
                  paymentUpdateModal.order?.id,
                  getOrderPaymentStatusValue(paymentUpdateModal.order),
                  paymentUpdateModal.order?.order_id,
                  paymentUpdateModal.refundAmount,
                  paymentUpdateModal.order?.total_amount,
                  { creditNote: paymentUpdateModal.creditNote }
                )}
                variant="contained"
                disabled={
                  updatingPayment ||
                  isPaymentUpdateDisabled(paymentUpdateModal.order) ||
                  (() => {
                    const ps = getOrderPaymentStatusValue(paymentUpdateModal.order);
                    if (
                      ['failed', 'pending', 'outstanding'].includes(ps) &&
                      !String(paymentUpdateModal.creditNote || '').trim()
                    ) {
                      return true;
                    }
                    if (ps !== 'refunded') return false;
                    if (!String(paymentUpdateModal.creditNote || '').trim()) return true;
                    const refundNum = Number(paymentUpdateModal.refundAmount);
                    const apiDetails = paymentUpdateModal.orderPaymentDetails;
                    const advancePaidTotal = getAdvancePaymentEntries(
                      paymentUpdateModal.order || paymentUpdateModal
                    ).reduce((sum, entry) => sum + Number(entry.advance_amount || 0), 0);
                    const paidFallback =
                      advancePaidTotal > 0
                        ? advancePaidTotal
                        : (paymentUpdateModal.order?.amount_paid ??
                            paymentUpdateModal.order?.paid ??
                            0);
                    const maxRefund = Number(apiDetails?.paidAmount ?? paidFallback);
                    return (
                      !Number.isFinite(refundNum) ||
                      refundNum <= 0 ||
                      refundNum > maxRefund
                    );
                  })()
                }
                startIcon={updatingPayment ? <CircularProgress size={16} /> : <UpdateIcon />}
              >
                {updatingPayment ? 'Updating...' : 'Update Status'}
              </Button>
            )}
          </DialogActions>
        </Dialog>

        {/* Invoice View Modal */}
        <InvoiceModal open={invoiceModal.open} onClose={handleCloseInvoiceModal} maxWidth={false} fullWidth>
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PdfIcon color="error" />
                <Typography variant="h6" fontWeight={600}>Invoice - {invoiceModal.product?.product_name}</Typography>
              </Box>
              <IconButton onClick={handleCloseInvoiceModal}><CloseIcon /></IconButton>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ p: 0, height: '100%' }}>
            {invoiceModal.invoice && invoiceModal.invoice.path && <InvoiceFrame src={invoiceModal.invoice.path} title="Invoice PDF" onError={(e) => { console.error('Error loading PDF:', e); alert('Error loading invoice PDF. Please try downloading instead.'); }} />}
          </DialogContent>
        </InvoiceModal>

        {/* Delivery Challan View Modal */}
        <InvoiceModal open={deliveryChallanModal.open} onClose={handleCloseDeliveryChallanModal} maxWidth={false} fullWidth>
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ChallanIcon color="secondary" />
                <Typography variant="h6" fontWeight={600}>Delivery Challan - {deliveryChallanModal.product?.product_name}</Typography>
              </Box>
              <IconButton onClick={handleCloseDeliveryChallanModal}><CloseIcon /></IconButton>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ p: 0, height: '100%' }}>
            {deliveryChallanModal.challan && deliveryChallanModal.challan.path && <InvoiceFrame src={deliveryChallanModal.challan.path} title="Delivery Challan PDF" onError={(e) => { console.error('Error loading PDF:', e); alert('Error loading delivery challan PDF. Please try downloading instead.'); }} />}
          </DialogContent>
        </InvoiceModal>

        {/* Bulk Delivery Challan Modal */}
        <Dialog
          open={bulkChallanModal.open}
          onClose={handleCloseBulkChallanModal}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <DeliveryIcon color="secondary" />
              <Typography variant="h6" fontWeight={600}>
                Bulk Status Update & Delivery Management
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Update status for {getSelectedProductsCount()} selected orders. For "Dispatch" status, delivery challans will be created.
            </Typography>
          </DialogTitle>

          <DialogContent>
            {bulkChallanModal.error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {bulkChallanModal.error}
              </Alert>
            )}

            {bulkChallanModal.isCreating && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} />
                  Creating bulk delivery challan...
                </Box>
              </Alert>
            )}

  {(() => {
              // Check payment status for all selected orders
              const selectedOrders = filteredData.filter(order => selectedProducts.includes(order.id));
              const unpaidOrders = selectedOrders.filter(order => {
                const paymentStatus = (order.payment_status?.name || order.payment_status_name || order.payment_status || '').toLowerCase();
                const isPaid = isPaymentSatisfiedForOrderAdvance(paymentStatus);
                return !isPaid;
              });
              
              if (unpaidOrders.length > 0) {
                return (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    <Typography variant="body2" fontWeight={600}>
                      Payment Required
                    </Typography>
                    <Typography variant="caption" display="block" sx={{ mt: 0.5, mb: 1.5 }}>
                      {unpaidOrders.length} of {selectedOrders.length} selected order{selectedOrders.length > 1 ? 's' : ''} must be paid before status can be updated. 
                      Please update the payment status first. Only unpaid orders will be updated.
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      sx={{ textTransform: 'none' }}
                      startIcon={<Payment fontSize="small" />}
                      onClick={() => openBulkPaidPaymentFlow(unpaidOrders)}
                      disabled={
                        !shouldEnableButton?.('update_payment') ||
                        bulkChallanModal.isCreating ||
                        isBulkPaymentUpdateDisabled()
                      }
                    >
                      Update Payment
                    </Button>
                    {/* <Box sx={{ mt: 2, display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                      <FormControl sx={{ minWidth: 200 }} size="small">
                        <InputLabel>Update Payment Status To</InputLabel>
                        <Select
                          value={bulkChallanModal.bulkPaymentStatus || ''}
                          label="Update Payment Status To"
                          onChange={(e) => handleBulkChallanInputChange('bulkPaymentStatus', e.target.value)}
                          disabled={isUpdatingBulkPayment || bulkChallanModal.isCreating}
                        >
                          {paymentStatusChoices
                            .filter(choice => {
                              // Only show "paid" and "outstanding" options for this modal
                              const value = (choice.value || choice.key || choice.name || '').toLowerCase();
                              return value === 'paid' || value === 'outstanding';
                            })
                            .map((choice) => {
                              const value = choice.value || choice.key || choice.name || '';
                              const label = choice.label || choice.name || value;
                              return (
                                <MenuItem key={value} value={value}>
                                  <PaymentStatusChip 
                                    label={label} 
                                    status={value.toLowerCase()}
                                    size="small"
                                    sx={{ mr: 1 }}
                                  />
                                  {label}
                                </MenuItem>
                              );
                            })}
                        </Select>
                      </FormControl>
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        onClick={handleBulkPaymentStatusUpdate}
                        disabled={!bulkChallanModal.bulkPaymentStatus || isUpdatingBulkPayment || bulkChallanModal.isCreating}
                        startIcon={isUpdatingBulkPayment ? <CircularProgress size={16} /> : <Payment />}
                        sx={{ mt: 0.5 }}
                      >
                        {isUpdatingBulkPayment ? 'Updating...' : 'Update Payment Status'}
                      </Button>
                    </Box> */}
                  </Alert>
                );
              }
              return null;
            })()}

            <Box sx={{ mt: 2 }}>
              {/* <Box sx={{ 
                p: 2, 
                backgroundColor: alpha(theme.palette.secondary.main, 0.1),
                borderRadius: 2,
                mb: 3
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <ChallanIcon color="secondary" />
                  <Typography variant="subtitle1" fontWeight={600}>
                    Delivery Information
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  This information will be used for all selected orders
                </Typography>
              </Box> */}

              <Grid container spacing={2}>
                <Grid item xs={12}>
              

                {/* <FormControl fullWidth>
                  <InputLabel>Update Status Tosdfgh</InputLabel>
                  <Select
                    value={
                      // If Dispatch tab is active and Dispatch is selected, clear it
                      (statusFilter === 'Dispatch' && bulkChallanModal.selectedStatus === 'Dispatch') 
                        ? '' 
                        : (bulkChallanModal.selectedStatus || '')
                    }
                    label="Update Status To"
                    onChange={(e) => {
                      const selectedValue = e.target.value;
                      const selectedOption = statusOptions.find(opt => opt.name === selectedValue);
                      
                      // Prevent selecting Dispatch when Dispatch tab is active
                      if (statusFilter === 'Dispatch' && selectedValue === 'Dispatch') {
                        return; // Don't allow selection
                      }
                      
                      handleBulkChallanInputChange('selectedStatus', selectedValue);
                      handleBulkChallanInputChange('selectedStatusCategory', selectedOption?.category || '');
                    }}
                    disabled={false}
                  >
                    {statusOptions.map((option) => {
                      // Find minimum status among selected orders for sequential validation
                      const selectedOrders = filteredData.filter(order => selectedProducts.includes(order.id));
                      const statusOrder = getStatusOrder();
                      
                      // Check payment status for all selected orders
                      const hasUnpaidOrders = selectedOrders.some(order => {
                        const paymentStatus = (order.payment_status?.name || order.payment_status || '').toLowerCase();
                        const isPaid = isPaymentSatisfiedForOrderAdvance(paymentStatus);
                        return !isPaid;
                      });
                      
                      // If not paid, only allow "Orders" status (name: "Orders")
                      const isOrdersStatus = option.name === 'Orders';
                      const shouldDisableByPayment = hasUnpaidOrders && !isOrdersStatus;
                      
                      // Get the minimum status order (lowest sequence) among selected orders
                      let minStatusOrder = 999;
                      selectedOrders.forEach(order => {
                        const orderStatusCategory = (order.status?.category || '').toLowerCase();
                        const orderStatusName = order.status?.name || '';
                        
                        // Try to find category from statusOptions if not directly available
                        let orderCategory = orderStatusCategory;
                        if (!orderCategory && orderStatusName) {
                          const orderStatusOption = statusOptions.find(opt => opt.name === orderStatusName);
                          orderCategory = orderStatusOption?.category?.toLowerCase() || '';
                        }
                        
                        const orderSequence = statusOrder[orderCategory] || 0;
                        if (orderSequence > 0 && orderSequence < minStatusOrder) {
                          minStatusOrder = orderSequence;
                        }
                      });
                      
                      // If no valid status found, allow all (safety fallback)
                      if (minStatusOrder === 999) {
                        minStatusOrder = 0;
                      }
                      
                      // Always allow Cancelled and Returned (check by category)
                      const alwaysAvailableCategories = ['cancelled', 'returned'];
                      const optionCategory = (option.category || '').toLowerCase();
                      const isAlwaysAvailable = alwaysAvailableCategories.includes(optionCategory);
                      
                      // Get the sequence for this option
                      const optionSequence = statusOrder[optionCategory] || 0;
                      
                      // Disable if:
                      // 1. Payment required (except for "Orders" status)
                      // 2. It's before the minimum status (can't go backwards)
                      // 3. Never disable Cancelled or Returned
                      // 4. If current statusFilter is "Dispatch", disable "Dispatch" option (can only go to Delivered, Returned, or Cancelled)
                      // Note: For bulk operations, we allow jumping forward to any status (not just next sequential)
                      const isDispatchTab = statusFilter === 'Dispatch';
                      const isDispatchOption = option.name === 'Dispatch';
                      const shouldDisableByDispatchTab = isDispatchTab && isDispatchOption;
                      
                      const shouldDisableBySequence = !isAlwaysAvailable && (
                        optionSequence < minStatusOrder || 
                        shouldDisableByDispatchTab
                      );
                      
                      const shouldDisable = shouldDisableByPayment || shouldDisableBySequence;
                      
                      return (
                      <MenuItem 
                          key={option.id || option.name} 
                          value={option.name}
                        disabled={shouldDisable}
                        >

                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                            {getIconForCategory(option.category)}
                            <StatusChip 
                              status={option.category?.toLowerCase()}
                              label={option.name}
                              size="small"
                            />
                            {shouldDisableByPayment && (
                              <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', fontSize: '0.7rem' }}>
                                Payment required
                              </Typography>
                            )}
                            {shouldDisableBySequence && !shouldDisableByPayment && (
                              <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', fontSize: '0.7rem' }}>
                                {shouldDisableByDispatchTab ? 'Not available' : 'Sequential only'}
                              </Typography>
                            )}
                          </Box>
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl> */}
  {(() => {
    const selectedOrders = filteredData.filter(order => selectedProducts.includes(order.id));
    const hasUnpaidOrders = selectedOrders.some(order => {
      const paymentStatus = (order.payment_status?.name || order.payment_status || '').toLowerCase();
      return !isPaymentSatisfiedForOrderAdvance(paymentStatus);
    });

    return (
      <>
        {/* {hasUnpaidOrders && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight={600}>
              Payment Required
            </Typography>
            <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
              Some selected orders must be paid before they can be moved to any status other than "Orders". Please update the payment status first.
            </Typography>
          </Alert>
        )} */}

        <FormControl fullWidth>
          <InputLabel>Update Status To</InputLabel>
          <Select
            value={
              (statusFilter === 'Dispatch' && bulkChallanModal.selectedStatus === 'Dispatch')
                ? ''
                : (bulkChallanModal.selectedStatus || '')
            }
            label="Update Status To"
            onChange={(e) => {
              const selectedValue = e.target.value;
              const selectedOption = statusOptions.find(opt => opt.name === selectedValue);

              // Prevent selecting Dispatch when Dispatch tab is active
              if (statusFilter === 'Dispatch' && selectedValue === 'Dispatch') return;

              handleBulkChallanInputChange('selectedStatus', selectedValue);
              handleBulkChallanInputChange('selectedStatusCategory', selectedOption?.category || '');
              if (selectedValue === 'Delivered') {
                const selectedOrders = filteredData.filter(order => selectedProducts.includes(order.id));
                const selectedOrderWithStartKm = selectedOrders.find((order) => {
                  const startKm = order.delivery_challan?.start_km;
                  return startKm !== null && startKm !== undefined && String(startKm).trim() !== '';
                });
                const deliveryChallan = selectedOrderWithStartKm?.delivery_challan;
                if (deliveryChallan) {
                  setBulkChallanModal(prev => ({
                    ...prev,
                    driverName: deliveryChallan.driver_name || '',
                    vehicleNo:
                      deliveryChallan.vehicle_number ||
                      deliveryChallan.vehicle_no ||
                      deliveryChallan.vehicle_name ||
                      '',
                    start_km:
                      deliveryChallan.start_km !== null && deliveryChallan.start_km !== undefined
                        ? String(deliveryChallan.start_km)
                        : '',
                  }));
                }
              }
            }}
          >
            {statusOptions.map((option) => {
              // Use the same sequential validation logic as individual modal
              const statusOrder = getStatusOrder();
              const isDeliveredTab = (statusFilter || '').toLowerCase() === 'delivered';

              // Payment check (Placed-tab bulk flow: allow Cancelled while unpaid)
              const isOrdersStatus = option.name === 'Orders';
              const isCancelledOption = (option.name || '').toLowerCase() === 'cancelled';
              const shouldDisableByPayment =
                hasUnpaidOrders &&
                !isOrdersStatus &&
                !(bulkChallanModal.allowUnpaidBulkCancelFromPlaced && isCancelledOption);

              // Sequential validation - check each selected order individually
              // For bulk operations, we need to check if ALL selected orders can move to this status
              // A status is only available if it's the next sequential status for ALL selected orders
              let isAvailableForAllOrders = true;
              const optionCategory = (option.category || '').toLowerCase();
              const optionName = (option.name || '').toLowerCase();
              
              // Check each selected order
              for (const order of selectedOrders) {
                const orderStatusCategory = (order.status?.category || '').toLowerCase();
                const orderStatusName = (order.status?.name || '').toLowerCase();
                
                // Get statuses to disable for this order (using same logic as individual modal)
                const statusesToDisable = getStatusesToDisable(orderStatusCategory, orderStatusName);
                
                // Check if this option should be disabled for this order
                const shouldDisableForThisOrder = statusesToDisable.includes(optionCategory);
                
                if (shouldDisableForThisOrder) {
                  isAvailableForAllOrders = false;
                  break; // If any order can't use this status, disable it
                }
              }

              // Additional checks for Dispatch tab
              const isDispatchTab = statusFilter === 'Dispatch';
              const isDispatchOption = option.name === 'Dispatch';
              const shouldDisableByDispatchTab = isDispatchTab && isDispatchOption;

              //* Combine all disable conditions
              const isReturnedOption = optionName === 'returned' || optionCategory === 'returned';
              const shouldDisableByDeliveredTab = isDeliveredTab && !isReturnedOption;
              const shouldDisableBySequence = !isAvailableForAllOrders || shouldDisableByDispatchTab || shouldDisableByDeliveredTab;
              const allowPlacedTabBulkCancelledPick =
                bulkChallanModal.allowUnpaidBulkCancelFromPlaced && isCancelledOption;
              const shouldDisable =
                (shouldDisableByPayment || shouldDisableBySequence) && !allowPlacedTabBulkCancelledPick;

              return (
                <MenuItem
                  key={option.id || option.name}
                  value={option.name}
                  disabled={shouldDisable}
                  sx={shouldDisable ? { opacity: 0.5, backgroundColor: theme.palette.grey[100] } : {}}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                    {getIconForCategory(option.category)}
                    <StatusChip status={option.category?.toLowerCase()} label={option.name} size="small" />
                    {shouldDisableByPayment && (
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', fontSize: '0.7rem' }}>
                        Payment required
                      </Typography>
                    )}
                    {shouldDisableBySequence && !shouldDisableByPayment && (
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', fontSize: '0.7rem' }}>
                        {shouldDisableByDispatchTab || shouldDisableByDeliveredTab ? 'Not available' : 'Sequential only'}
                      </Typography>
                    )}
                  </Box>
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
      </>
    );
  })() }

                </Grid>
                
                {/* Show delivery fields only for Dispatch status */}
                {bulkChallanModal.selectedStatus === 'Dispatch' && (
                  <>
                    <Grid item xs={12}>
                      <Box sx={{ 
                        p: 2, 
                        backgroundColor: alpha(theme.palette.warning.main, 0.1),
                        borderRadius: 2,
                        mb: 2
                      }}>
                        <Typography variant="subtitle2" fontWeight={600} color="warning.main">
                          Delivery Information Required for Dispatch Status
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Please provide delivery details to create delivery challans
                        </Typography>
                      </Box>
                    </Grid>
                    
                    {/* Order By - 1st in sequence (bulk modal) */}
                    <Grid item xs={12}>
                      <FormControl fullWidth required>
                        <InputLabel>Order By</InputLabel>
                        <Select
                          value={bulkChallanModal.orderBy}
                          onChange={(e) => handleBulkChallanInputChange('orderBy', e.target.value)}
                          disabled={bulkChallanModal.isCreating}
                          label="Order By"
                        >
                          <MenuItem value="">
                            <em>Select order by</em>
                          </MenuItem>
                          {orderByChoices.map((choice) => (
                            <MenuItem key={choice.key} value={choice.key}>
                              {choice.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    {/* Entity Name - Dynamic based on Order By selection - 2nd in sequence */}
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Delivery Partner Name"
                        value={bulkChallanModal.entityName}
                        onChange={(e) => {
                          const lettersOnly = e.target.value.replace(/[^A-Za-z]/g, '');
                          handleBulkChallanInputChange('entityName', lettersOnly);
                        }}
                        disabled={bulkChallanModal.isCreating}
                        required
                        placeholder="Enter Delivery Partner"
                      />
                    </Grid>
                    {/* Driver/Authorized Name - 3rd in sequence */}
                    <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Driver/Authorized Name"
                      value={bulkChallanModal.driverName || ''}
                      onChange={(e) => {
                        const lettersOnly = e.target.value.replace(/[^A-Za-z]/g, '');
                        handleBulkChallanInputChange('driverName', lettersOnly);
                      }}
                      disabled={bulkChallanModal.isCreating}
                      required
                      placeholder="Enter driver's full name"
                      error={bulkChallanModal.error && bulkChallanModal.error.includes('Driver name')}
                    />
                  </Grid>
                    
                    {/* Vehicle No / Reference No - 4th in sequence */}
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Vehicle No / Reference No"
                        value={bulkChallanModal.vehicleNo || ''}
                        onChange={(e) => {
                          const isTruckOrderBy = String(bulkChallanModal.orderBy || '').trim().toLowerCase() === 'truck';
                          handleBulkChallanInputChange(
                            'vehicleNo',
                            isTruckOrderBy ? formatVehicleReferenceNo(e.target.value) : e.target.value
                          );
                        }}
                        disabled={bulkChallanModal.isCreating}
                        required={String(bulkChallanModal.orderBy || '').trim().toLowerCase() === 'truck'}
                        placeholder="Enter vehicle registration number"
                        inputProps={
                          String(bulkChallanModal.orderBy || '').trim().toLowerCase() === 'truck'
                            ? { maxLength: 13 }
                            : undefined
                        }
                        error={
                          (String(bulkChallanModal.orderBy || '').trim().toLowerCase() === 'truck' &&
                            isVehicleReferenceNoInvalid(bulkChallanModal.vehicleNo)) ||
                          (bulkChallanModal.error && bulkChallanModal.error.includes('vehicle number'))
                        }
                        helperText={
                          String(bulkChallanModal.orderBy || '').trim().toLowerCase() === 'truck' &&
                          isVehicleReferenceNoInvalid(bulkChallanModal.vehicleNo)
                            ? VEHICLE_REFERENCE_NO_ERROR
                            : ''
                        }
                      />
                    </Grid>

                    {/* Start KM - only show when Order By is "Truck" */}
                    {String(bulkChallanModal.orderBy || '').trim().toLowerCase() === 'truck' && (
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Start KM"
                          type="number"
                          value={bulkChallanModal.start_km || ''}
                          onChange={(e) => {
                            const value = e.target.value;

                            if (value === '' || /^[1-9]\d*$/.test(value)) {
                              handleBulkChallanInputChange('start_km', value);
                            }
                          }}
                          onKeyDown={(e) => {
                            // Block unwanted keys
                            if (['e', 'E', '+', '-', '.'].includes(e.key)) {
                              e.preventDefault();
                            }
                          }}
                          onWheel={(e) => e.currentTarget.blur()}
                          disabled={bulkChallanModal.isCreating}
                          placeholder="Enter starting kilometer reading"
                          inputProps={{ min: 1, step: 1 }}
                        />
                      </Grid>
                    )}
                    
                    <Grid item xs={12}>
                      <Box>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={bulkChallanModal.useCustomDispatchPerson}
                              onChange={(e) => handleBulkChallanInputChange('useCustomDispatchPerson', e.target.checked)}
                              disabled={bulkChallanModal.isCreating}
                            />
                          }
                          label="Add custom dispatch person"
                        />
                        {bulkChallanModal.useCustomDispatchPerson ? (
                          <TextField
                            fullWidth
                            label="Custom Dispatch Person Name"
                            value={bulkChallanModal.customDispatchPerson}
                            onChange={(e) => handleBulkChallanInputChange('customDispatchPerson', e.target.value)}
                            disabled={bulkChallanModal.isCreating}
                            placeholder="Enter custom dispatch person name"
                            sx={{ mt: 1 }}
                          />
                        ) : (
                          <FormControl fullWidth sx={{ mt: 1 }}>
                            <InputLabel>Dispatch Person Name</InputLabel>
                            <Select
                              value={bulkChallanModal.dispatchPersonName}
                              label="Dispatch Person Name"
                              onChange={(e) => handleBulkChallanInputChange('dispatchPersonName', e.target.value)}
                              disabled={bulkChallanModal.isCreating}
                            >
                              <MenuItem value="">
                                <em>Select dispatch person</em>
                              </MenuItem>
                              {dispatchPersons.map((person) => (
                                <MenuItem key={person.id} value={person.name}>
                                  {person.name}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        )}
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Delivery Date"
                        type="date"
                        value={bulkChallanModal.deliveryDate || new Date().toISOString().slice(0,10)}
                        onChange={(e) => handleBulkChallanInputChange('deliveryDate', e.target.value)}
                        disabled={bulkChallanModal.isCreating}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Box>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                          Email Addresses (Optional)
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                          <TextField
                            fullWidth
                            size="small"
                            value={bulkChallanModal.emailInput || ''}
                            onChange={(e) => handleBulkChallanInputChange('emailInput', e.target.value)}
                            disabled={bulkChallanModal.isCreating}
                            placeholder="Enter email address"
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                addEmailToBulkModal();
                              }
                            }}
                          />
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={addEmailToBulkModal}
                            disabled={!bulkChallanModal.emailInput?.trim() || bulkChallanModal.isCreating}
                          >
                            Add
                          </Button>
                        </Box>
                        {bulkChallanModal.emails && bulkChallanModal.emails.length > 0 && (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {bulkChallanModal.emails.map((email, index) => (
                              <Chip
                                key={index}
                                label={email}
                                onDelete={() => removeEmailFromBulkModal(email)}
                                size="small"
                                disabled={bulkChallanModal.isCreating}
                              />
                            ))}
                          </Box>
                        )}
                        <Typography variant="caption" color="text.secondary">
                          Delivery challans will be sent to these email addresses
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={bulkChallanModal.generateInvoice || false}
                            onChange={(e) => handleBulkChallanInputChange('generateInvoice', e.target.checked)}
                            disabled={
                              bulkChallanModal.isCreating ||
                              (['processing'].includes((statusFilter || '').toLowerCase()) &&
                                !shouldEnableButton?.('generate_invoice_along_with_delivery_challan'))
                            }
                          />
                        }
                        label="Generate Invoice along with Delivery Challan"
                      />
                    </Grid>
                  </>
                )}

                  {/* End KM field for Delivered status - only when any selected order has Start KM */}
                  {bulkChallanModal.selectedStatus === 'Delivered' && (() => {
                    const selectedOrders = filteredData.filter(order => selectedProducts.includes(order.id));
                    const isDispatchTabForBulk = ['dispatch', 'shipped'].includes((statusFilter || '').toLowerCase());
                    const selectedOrderWithStartKm = selectedOrders.find(order => {
                      const startKm = order.delivery_challan?.start_km;
                      const hasStartKm = startKm !== null && startKm !== undefined && String(startKm).trim() !== '';
                      if (isDispatchTabForBulk && !isOrderByTruck(order)) return false;
                      return hasStartKm;
                    });
                    const deliveryChallan = selectedOrderWithStartKm?.delivery_challan;
                    if (!deliveryChallan) return null;

                    const driverName = bulkChallanModal.driverName || deliveryChallan.driver_name || '';
                    const vehicleNo =
                      bulkChallanModal.vehicleNo ||
                      deliveryChallan.vehicle_number ||
                      deliveryChallan.vehicle_no ||
                      deliveryChallan.vehicle_name ||
                      '';
                    const startKm =
                      bulkChallanModal.start_km ||
                      (
                        deliveryChallan.start_km !== null && deliveryChallan.start_km !== undefined
                          ? String(deliveryChallan.start_km)
                          : ''
                      );

                    return (
                      <>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Driver Name"
                            value={driverName}
                            disabled
                            InputProps={{ readOnly: true }}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Vehicle No / Reference No"
                            value={vehicleNo}
                            disabled
                            InputProps={{ readOnly: true }}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Start KM"
                            value={startKm}
                            disabled
                            InputProps={{ readOnly: true }}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="End KM"
                            required
                            type="number"
                            value={bulkChallanModal.end_km || ''}
                            onChange={(e) => {
                              const endKmValue = e.target.value;
                              handleBulkChallanInputChange('end_km', endKmValue);
                            }}
                            disabled={bulkChallanModal.isCreating}
                            placeholder="Enter ending kilometer reading"
                            error={(() => {
                              const parsedStartKm = parseFloat(startKm);
                              const endKm = parseFloat(bulkChallanModal.end_km);
                              const hasEndKm = String(bulkChallanModal.end_km || '').trim() !== '';
                              return !hasEndKm || (!isNaN(parsedStartKm) && !isNaN(endKm) && endKm <= parsedStartKm);
                            })()}
                            helperText={(() => {
                              const parsedStartKm = parseFloat(startKm);
                              const endKm = parseFloat(bulkChallanModal.end_km);
                              const hasEndKm = String(bulkChallanModal.end_km || '').trim() !== '';
                              if (!hasEndKm) {
                                return 'End KM is required';
                              }
                              if (!isNaN(parsedStartKm) && !isNaN(endKm) && endKm <= parsedStartKm) {
                                return 'End KM must be greater than Start KM';
                              }
                              return '';
                            })()}
                          />
                        </Grid>
                      </>
                    );
                  })()}

                {bulkChallanModal.selectedStatus === 'Cancelled' && (
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Reason"
                      value={bulkChallanModal.cancellationReason || ''}
                      onChange={(e) => handleBulkChallanInputChange('cancellationReason', e.target.value)}
                      disabled={bulkChallanModal.isCreating}
                      required
                      placeholder="Enter reason for cancellation"
                      sx={{ mt: 2 }}
                      multiline
                      rows={3}
                    />
                  </Grid>
                )}

                {bulkChallanModal.selectedStatus === 'Returned' && (
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Reason"
                      value={bulkChallanModal.returnReason || ''}
                      onChange={(e) => handleBulkChallanInputChange('returnReason', e.target.value)}
                      disabled={bulkChallanModal.isCreating}
                      required
                      placeholder="Enter reason for returned"
                      sx={{ mt: 2 }}
                      multiline
                      rows={3}
                    />
                  </Grid>
                )}
              </Grid>

            
            </Box>
          </DialogContent>

          <DialogActions>
            <Button 
              onClick={handleCloseBulkChallanModal}
              disabled={bulkChallanModal.isCreating}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleBulkChallanSubmit}
              variant="contained"
              color="secondary"
              disabled={isBulkSubmitDisabled()}
              startIcon={
                bulkChallanModal.isCreating ? 
                <CircularProgress size={16} /> : 
                <DeliveryIcon />
              }
              sx={{
                '&:hover': {
                  backgroundColor: theme.palette.success.main,
                },
                transition: 'all 0.2s ease-in-out'
              }}
            >
              {bulkChallanModal.isCreating ? 'Processing...' : 
                bulkChallanModal.selectedStatus === 'Dispatch' ? 
                  `Create ${getEligibleProductsCount()} Challans` : 
                  `Update ${getSelectedProductsCount()} Orders`
              }
            </Button>
          </DialogActions>
        </Dialog>

        {/* Email Dialogs */}
        <Dialog
          open={emailDialog.open}
          onClose={handleCloseEmailDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <EmailIcon color="primary" />
              <Typography variant="h6" fontWeight={600}>Send Documents via Email</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Selected orders: {filteredData.filter(p => selectedProducts.includes(p.id)).length}
            </Typography>
          </DialogTitle>
          <DialogContent>
            {emailDialog.error && (
              <Alert severity="error" sx={{ mb: 2 }}>{emailDialog.error}</Alert>
            )}
            {emailDialog.success && (
              <Alert severity="success" sx={{ mb: 2 }}>{emailDialog.success}</Alert>
            )}
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Emails"
                  placeholder="Enter email addresses separated by commas"
                  value={emailDialog.emails}
                  onChange={(e) => handleEmailDialogChange('emails', e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                  <FormControlLabel
                    control={<Checkbox checked={emailDialog.sendInvoice} onChange={(e) => handleEmailDialogChange('sendInvoice', e.target.checked)} />}
                    label={`Include Invoices (${getAvailableCounts().invoices} available)`}
                  />
                  <FormControlLabel
                    control={<Checkbox checked={emailDialog.sendDelivery} onChange={(e) => handleEmailDialogChange('sendDelivery', e.target.checked)} />}
                    label={`Include Delivery Challans (${getAvailableCounts().challans} available)`}
                  />
                </Box>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseEmailDialog} disabled={emailDialog.isSending}>Cancel</Button>
            <Button
              variant="contained"
              startIcon={emailDialog.isSending ? <CircularProgress size={16} /> : <SendIcon />}
              disabled={emailDialog.isSending}
              onClick={handleEmailSendSubmit}
              sx={{ textTransform: 'none' }}
            >
              {emailDialog.isSending ? 'Sending...' : 'Send Email'}
            </Button>
          </DialogActions>
        </Dialog>

        <AppOrderPopup open={singleEmailDialog.open} onClose={closeSingleOrderEmail} maxWidth="sm" fullWidth>
          <DialogTitle>Send Documents via Email</DialogTitle>
          <DialogContent>
            <Stack spacing={2.25} sx={{ mt: 1 }}>
              {singleEmailDialog.order && (
                <Typography variant="body2" color="text.secondary">
                  Order ID: {singleEmailDialog.order.order_id}
                </Typography>
              )}

              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Add Email"
                  value={singleEmailDialog.emailInput}
                  onChange={(e) => setSingleEmailDialog(prev => ({ ...prev, emailInput: e.target.value, error: '', success: '' }))}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') addEmailToSingle()
                  }}
                />
                <Button
                  variant="contained"
                  onClick={addEmailToSingle}
                  sx={{ minWidth: 92, textTransform: 'none' }}
                  startIcon={<AddIcon />}
                >
                  Add
                </Button>
              </Box>

              {singleEmailDialog.emails.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {singleEmailDialog.emails.map((email) => (
                    <Chip
                      key={email}
                      label={email}
                      onDelete={() => removeEmailFromSingle(email)}
                      size="small"
                      variant="outlined"
                      color="primary"
                    />
                  ))}
                </Box>
              )}

              <Box sx={{ display: 'flex', gap: 3 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={singleEmailDialog.sendInvoice}
                      onChange={(e) => setSingleEmailDialog(prev => ({ ...prev, sendInvoice: e.target.checked, error: '', success: '' }))}
                      disabled={!extractInvoice(singleEmailDialog.order)?.path}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2">include Invoice</Typography>
                      {!extractInvoice(singleEmailDialog.order)?.path && (
                        <Typography variant="caption" color="text.secondary">
                          (not available)
                        </Typography>
                      )}
                    </Box>
                  }
                />

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={singleEmailDialog.sendDelivery}
                      onChange={(e) => setSingleEmailDialog(prev => ({ ...prev, sendDelivery: e.target.checked, error: '', success: '' }))}
                      disabled={!extractDeliveryChallan(singleEmailDialog.order)?.path}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2">Include Delivery Challan</Typography>
                      {!extractDeliveryChallan(singleEmailDialog.order)?.path && (
                        <Typography variant="caption" color="text.secondary">
                          (not available)
                        </Typography>
                      )}
                    </Box>
                  }
                />
              </Box>

              {singleEmailDialog.error && (
                <Alert severity="error" onClose={() => setSingleEmailDialog(prev => ({ ...prev, error: '' }))}>
                  {singleEmailDialog.error}
                </Alert>
              )}

              {singleEmailDialog.success && (
                <Alert severity="success" onClose={() => setSingleEmailDialog(prev => ({ ...prev, success: '' }))}>
                  {singleEmailDialog.success}
                </Alert>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeSingleOrderEmail} disabled={singleEmailDialog.isSending}>Cancel</Button>
            <Button variant="contained" startIcon={singleEmailDialog.isSending ? <CircularProgress size={16} /> : <SendIcon />} disabled={singleEmailDialog.isSending || (!extractInvoice(singleEmailDialog.order)?.path && !extractDeliveryChallan(singleEmailDialog.order)?.path)} onClick={submitSingleOrderEmail}>
              {singleEmailDialog.isSending ? 'Sending...' : 'Send Email'}
            </Button>
          </DialogActions>
        </AppOrderPopup>

        {/* Bulk Payment Status Update Modal */}
        <Dialog
          open={bulkPaymentStatusModal.open}
          onClose={handleCloseBulkPaymentStatusModal}
          maxWidth={bulkPaymentStatusModal.step === 'payment_details' ? false : 'sm'}
          fullWidth={bulkPaymentStatusModal.step !== 'payment_details'}
          scroll={bulkPaymentStatusModal.step === 'payment_details' ? 'paper' : 'body'}
          PaperProps={
            bulkPaymentStatusModal.step === 'payment_details'
              ? PAYMENT_DETAILS_DIALOG_PAPER_PROPS
              : undefined
          }
        >
          <DialogTitle>
            {bulkPaymentStatusModal.step === 'payment_option'
              ? 'Payment Option'
              : bulkPaymentStatusModal.step === 'payment_details'
                ? 'Payment Details'
                : 'Update Payment Status'}
            <Typography variant="body2" color="text.secondary">
              {getUnpaidOrdersFromList(getSelectedOrdersForBulkPayment()).length} unpaid of{' '}
              {getSelectedProductsCount()} selected order{getSelectedProductsCount() > 1 ? 's' : ''}
            </Typography>
          </DialogTitle>
          <DialogContent
            sx={
              bulkPaymentStatusModal.step === 'payment_details'
                ? PAYMENT_DETAILS_DIALOG_CONTENT_SX
                : undefined
            }
          >
            {bulkPaymentStatusModal.error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {bulkPaymentStatusModal.error}
              </Alert>
            )}

            {bulkPaymentStatusModal.paymentError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {bulkPaymentStatusModal.paymentError}
              </Alert>
            )}

            {bulkPaymentStatusModal.isUpdating && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} />
                  Updating payment status
                </Box>
              </Alert>
            )}

            {bulkPaymentStatusModal.step === 'payment_option' && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Choose your payment method:
                </Typography>
                <RadioGroup
                  value={bulkPaymentStatusModal.paymentOption}
                  onChange={(e) =>
                    setBulkPaymentStatusModal((prev) => ({
                      ...prev,
                      paymentOption: e.target.value,
                      paymentError: '',
                      ...(e.target.value === 'full'
                        ? {
                            advanceAmount: String(
                              prev.summaryOrder?.total_amount ?? prev.summaryOrder?.total ?? ''
                            ),
                          }
                        : { advanceAmount: '' }),
                    }))
                  }
                >
                  <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5, borderRadius: 2 }}>
                    <FormControlLabel
                      value="full"
                      control={<Radio />}
                      label="Order with full Payment"
                    />
                  </Paper>
                  <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                    <FormControlLabel
                      value="advance"
                      control={<Radio />}
                      label="Order with Advanced Payment"
                    />
                  </Paper>
                </RadioGroup>
              </Box>
            )}

            {bulkPaymentStatusModal.step === 'payment_details' && (() => {
              const unpaidCount = getUnpaidOrdersFromList(getSelectedOrdersForBulkPayment()).length;

              return (
                <PaymentDetailsStepContent
                  idPrefix="bulk-payment"
                  order={bulkPaymentStatusModal.summaryOrder}
                  orderPaymentDetails={bulkPaymentStatusModal.orderPaymentDetails}
                  paymentOption={bulkPaymentStatusModal.paymentOption}
                  advanceAmount={bulkPaymentStatusModal.advanceAmount}
                  onAdvanceAmountChange={(e) =>
                    setBulkPaymentStatusModal((prev) => ({
                      ...prev,
                      advanceAmount: e.target.value,
                      paymentError: '',
                    }))
                  }
                  creditNote={bulkPaymentStatusModal.creditNote || ''}
                  onCreditNoteChange={(e) =>
                    setBulkPaymentStatusModal((prev) => ({
                      ...prev,
                      creditNote: e.target.value,
                      paymentError: '',
                    }))
                  }
                  paymentMethod={bulkPaymentStatusModal.paymentMethod}
                  paymentMethodConfig={bulkPaymentStatusModal.paymentMethodConfig}
                  advancePaymentMethods={advancePaymentMethods}
                  loadingAdvancePaymentMethods={loadingAdvancePaymentMethods}
                  onPaymentMethodChange={(e) => {
                    const methodId = e.target.value;
                    setBulkPaymentStatusModal((prev) => ({
                      ...prev,
                      ...buildPaymentMethodChangePayload(
                        methodId,
                        prev.orderPaymentDetails
                      ),
                    }));
                  }}
                  paymentFields={bulkPaymentStatusModal.paymentFields}
                  paymentFiles={bulkPaymentStatusModal.paymentFiles}
                  onFieldChange={(key, value) => {
                    if (key === 'cheque_amount' && bulkPaymentStatusModal.orderPaymentDetails) {
                      return;
                    }
                    setBulkPaymentStatusModal((prev) => ({
                      ...prev,
                      paymentFields: { ...prev.paymentFields, [key]: value },
                      paymentError: '',
                    }));
                  }}
                  onFileChange={(key, file) =>
                    setBulkPaymentStatusModal((prev) => ({
                      ...prev,
                      paymentFiles: { ...prev.paymentFiles, [key]: file },
                      paymentError: '',
                    }))
                  }
                  itemsLimit={3}
                  totalLabel="Reference total"
                  summaryBanner={
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Applying payment to <strong>{unpaidCount}</strong> unpaid order
                      {unpaidCount > 1 ? 's' : ''}.
                    </Typography>
                  }
                />
              );
            })()}

            {bulkPaymentStatusModal.step === 'status' && (
              <FormControl fullWidth sx={{ mt: 2 }}>
                <InputLabel>Payment Status</InputLabel>
                <Select
                  value={bulkPaymentStatusModal.selectedPaymentStatus || ''}
                  label="Payment Status"
                  onChange={(e) => {
                    const newStatus = e.target.value;
                    if (newStatus === 'paid') {
                      loadAdvancePaymentMethods();
                    }
                    setBulkPaymentStatusModal((prev) => ({
                      ...prev,
                      selectedPaymentStatus: newStatus,
                      error: '',
                      paymentError: '',
                      ...(newStatus === 'paid'
                        ? getInlinePaidPaymentStepState(prev.orderPaymentDetails)
                        : newStatus === 'credit'
                          ? getCreditPaymentStepState(prev.orderPaymentDetails)
                          : {
                              step: 'status',
                              paymentOption: '',
                              advanceAmount: '',
                              installmentAmount: '',
                              creditNote: '',
                              paymentMethod: '',
                              paymentMethodConfig: null,
                              paymentFields: {},
                              paymentFiles: {},
                            }),
                    }));
                  }}
                  disabled={
                    bulkPaymentStatusModal.isUpdating ||
                    loadingPaymentStatusChoices ||
                    isBulkPaymentUpdateDisabled()
                  }
                >
                  {renderPaymentStatusMenuItems({
                    hidePaid: ['placed', 'confirmed'].includes(
                      (statusFilter || '').toLowerCase()
                    ),
                  })}
                </Select>
              </FormControl>
            )}
            {bulkPaymentStatusModal.step === 'status' &&
              (bulkPaymentStatusModal.paymentOption === 'credit' ||
                bulkPaymentStatusModal.paymentOption === 'remaining') && (() => {
                const unpaidCount = getUnpaidOrdersFromList(getSelectedOrdersForBulkPayment()).length;
                return (
                  <Box sx={{ mt: 2 }}>
                    <PaymentDetailsStepContent
                      idPrefix="bulk-payment-credit"
                      order={bulkPaymentStatusModal.summaryOrder}
                      orderPaymentDetails={bulkPaymentStatusModal.orderPaymentDetails}
                      paymentOption={bulkPaymentStatusModal.paymentOption}
                      advanceAmount={bulkPaymentStatusModal.advanceAmount}
                      onAdvanceAmountChange={(e) =>
                        setBulkPaymentStatusModal((prev) => ({
                          ...prev,
                          advanceAmount: e.target.value,
                          paymentError: '',
                        }))
                      }
                      installmentAmount={bulkPaymentStatusModal.installmentAmount || ''}
                      onInstallmentAmountChange={(e) => {
                        const value = e.target.value;
                        const remaining = Number(
                          bulkPaymentStatusModal.orderPaymentDetails?.remainingAmount ?? 0
                        );
                        if (value !== '' && Number(value) > remaining) {
                          setBulkPaymentStatusModal((prev) => ({
                            ...prev,
                            paymentError: 'Installment amount cannot exceed the remaining amount',
                          }));
                          return;
                        }
                        setBulkPaymentStatusModal((prev) => ({
                          ...prev,
                          installmentAmount: value,
                          paymentError: '',
                          paymentFields: getCreditChequePaymentFields(
                            prev.paymentFields,
                            value,
                            prev.paymentMethodConfig
                          ),
                        }));
                      }}
                      creditNote={bulkPaymentStatusModal.creditNote || ''}
                      onCreditNoteChange={(e) =>
                        setBulkPaymentStatusModal((prev) => ({
                          ...prev,
                          creditNote: e.target.value,
                          paymentError: '',
                        }))
                      }
                      paymentMethod={bulkPaymentStatusModal.paymentMethod}
                      paymentMethodConfig={bulkPaymentStatusModal.paymentMethodConfig}
                      advancePaymentMethods={advancePaymentMethods}
                      loadingAdvancePaymentMethods={loadingAdvancePaymentMethods}
                      onPaymentMethodChange={(e) => {
                        const methodId = e.target.value;
                        setBulkPaymentStatusModal((prev) => {
                          const payload = buildPaymentMethodChangePayload(
                            methodId,
                            prev.orderPaymentDetails
                          );
                          if (payload.paymentMethodConfig?.type === 'cheque') {
                            payload.paymentFields = getCreditChequePaymentFields(
                              payload.paymentFields,
                              prev.installmentAmount ?? '',
                              payload.paymentMethodConfig
                            );
                          }
                          return { ...prev, ...payload };
                        });
                      }}
                      paymentFields={bulkPaymentStatusModal.paymentFields}
                      paymentFiles={bulkPaymentStatusModal.paymentFiles}
                      onFieldChange={(key, value) => {
                        if (key === 'cheque_amount' && bulkPaymentStatusModal.orderPaymentDetails) {
                          return;
                        }
                        setBulkPaymentStatusModal((prev) => ({
                          ...prev,
                          paymentFields: { ...prev.paymentFields, [key]: value },
                          paymentError: '',
                        }));
                      }}
                      onFileChange={(key, file) =>
                        setBulkPaymentStatusModal((prev) => ({
                          ...prev,
                          paymentFiles: { ...prev.paymentFiles, [key]: file },
                          paymentError: '',
                        }))
                      }
                      itemsLimit={3}
                      totalLabel="Reference total"
                      summaryBanner={
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          Applying{' '}
                          {bulkPaymentStatusModal.paymentOption === 'credit'
                            ? 'credit payment'
                            : 'payment'}{' '}
                          to <strong>{unpaidCount}</strong> unpaid order
                          {unpaidCount > 1 ? 's' : ''}.
                        </Typography>
                      }
                    />
                  </Box>
                );
              })()}
            {bulkPaymentStatusModal.step === 'status' &&
              ['failed', 'pending', 'outstanding', 'refunded'].includes(
                bulkPaymentStatusModal.selectedPaymentStatus
              ) && (
                <Box sx={{ mt: 3 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Note"
                    value={bulkPaymentStatusModal.creditNote || ''}
                    onChange={(e) =>
                      setBulkPaymentStatusModal((prev) => ({
                        ...prev,
                        creditNote: e.target.value,
                        paymentError: '',
                      }))
                    }
                    placeholder="Enter note"
                    multiline
                    minRows={3}
                    required
                  />
                </Box>
              )}
          </DialogContent>
          <DialogActions
            sx={
              bulkPaymentStatusModal.step === 'payment_details'
                ? PAYMENT_DETAILS_DIALOG_ACTIONS_SX
                : undefined
            }
          >
            <Button
              onClick={() => {
                if (bulkPaymentStatusModal.step === 'payment_details') {
                  setBulkPaymentStatusModal((prev) => ({
                    ...prev,
                    step: 'status',
                    selectedPaymentStatus: '',
                    paymentOption: '',
                    advanceAmount: '',
                    paymentMethod: '',
                    paymentFields: {},
                    paymentFiles: {},
                    paymentError: '',
                  }));
                  return;
                }
                handleCloseBulkPaymentStatusModal();
              }}
              disabled={bulkPaymentStatusModal.isUpdating}
            >
              {bulkPaymentStatusModal.step === 'status' ? 'Cancel' : 'Back'}
            </Button>

            {bulkPaymentStatusModal.step === 'payment_option' && (
              <Button
                variant="contained"
                disabled={!bulkPaymentStatusModal.paymentOption || bulkPaymentStatusModal.isUpdating}
                onClick={() =>
                  setBulkPaymentStatusModal((prev) => ({
                    ...prev,
                    step: 'payment_details',
                    paymentError: '',
                    ...(prev.paymentOption === 'full'
                      ? {
                          advanceAmount: String(
                            prev.summaryOrder?.total_amount ?? prev.summaryOrder?.total ?? ''
                          ),
                        }
                      : {}),
                  }))
                }
              >
                Confirm
              </Button>
            )}

            {bulkPaymentStatusModal.step === 'payment_details' && (
              <Button
                variant="contained"
                disabled={
                  bulkPaymentStatusModal.isUpdating ||
                  !canSubmitPaidPayment(bulkPaymentStatusModal)
                }
                onClick={handleConfirmBulkPaidPaymentDetails}
                startIcon={
                  bulkPaymentStatusModal.isUpdating ? <CircularProgress size={16} /> : <UpdateIcon />
                }
              >
                {bulkPaymentStatusModal.isUpdating ? 'Updating...' : 'Submit Payment'}
              </Button>
            )}

            {bulkPaymentStatusModal.paymentOption === 'remaining' && (
              <Button
                variant="contained"
                disabled={
                  bulkPaymentStatusModal.isUpdating ||
                  !canSubmitPaidPayment(bulkPaymentStatusModal)
                }
                onClick={handleConfirmBulkPaidPaymentDetails}
                startIcon={
                  bulkPaymentStatusModal.isUpdating ? <CircularProgress size={16} /> : <UpdateIcon />
                }
              >
                {bulkPaymentStatusModal.isUpdating ? 'Updating...' : 'Submit Payment'}
              </Button>
            )}

            {bulkPaymentStatusModal.paymentOption === 'credit' &&
              shouldShowCreditSubmitButton({
                ...bulkPaymentStatusModal,
                order: bulkPaymentStatusModal.summaryOrder,
              }) && (
              <Button
                variant="contained"
                disabled={
                  bulkPaymentStatusModal.isUpdating ||
                  !canSubmitCreditPayment({
                    ...bulkPaymentStatusModal,
                    order: bulkPaymentStatusModal.summaryOrder,
                  })
                }
                onClick={handleConfirmBulkCreditPaymentDetails}
                startIcon={
                  bulkPaymentStatusModal.isUpdating ? <CircularProgress size={16} /> : <UpdateIcon />
                }
              >
                {bulkPaymentStatusModal.isUpdating ? 'Updating...' : 'Submit Payment'}
              </Button>
            )}

            {bulkPaymentStatusModal.step === 'status' &&
              bulkPaymentStatusModal.selectedPaymentStatus !== 'paid' &&
              bulkPaymentStatusModal.selectedPaymentStatus !== 'credit' && (
                <Button
                  onClick={handleBulkPaymentStatusUpdateFromModal}
                  variant="contained"
                  disabled={
                    !bulkPaymentStatusModal.selectedPaymentStatus ||
                    bulkPaymentStatusModal.isUpdating ||
                    isBulkPaymentUpdateDisabled() ||
                    (['failed', 'pending', 'outstanding', 'refunded'].includes(
                      bulkPaymentStatusModal.selectedPaymentStatus
                    ) &&
                      !String(bulkPaymentStatusModal.creditNote || '').trim())
                  }
                  startIcon={
                    bulkPaymentStatusModal.isUpdating ? <CircularProgress size={16} /> : <UpdateIcon />
                  }
                >
                  {bulkPaymentStatusModal.isUpdating ? 'Updating...' : 'Update Payment'}
                </Button>
              )}
          </DialogActions>
        </Dialog>
      </Box>
    )
  } 

  export {
    AppOrderDetailsView,
    PaymentDetailsStepContent,
    PAYMENT_DETAILS_DIALOG_CONTENT_SX,
    PAYMENT_DETAILS_DIALOG_ACTIONS_SX,
    PAYMENT_DETAILS_DIALOG_PAPER_PROPS,
    consumeSavedAppOrderStatusTab,
  };

  export default ProductList;